import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, MapPin, Bed, Bath, Maximize, Heart, Phone, MessageCircle, Calendar, ChevronRight, ChevronLeft, Menu, X, Globe, Moon, Sun, Filter, Grid3x3, Map as MapIcon, Star, ArrowRight, Play, Check, TrendingUp, Award, Users, Building2, Home as HomeIcon, Briefcase, Calculator, Share2, Eye, Sparkles, Send, Mail, ChevronDown, SlidersHorizontal, GitCompare, Clock, Pause, Volume2, VolumeX, LayoutDashboard, Plus, Edit3, Trash2, Settings, BarChart3, UserCheck, Database, LogOut, FileText, Lock } from 'lucide-react';
// ============================================================
// DATA LAYER — Mock now, real-backend-ready scaffold below
// ============================================================
// To switch to a real backend (e.g. Supabase) later:
// 1. In your dev environment, install the client SDK
//    (e.g. for Supabase: install the official JS client from npm)
// 2. Uncomment the SDK-init block referenced in setupBackend() below
//    and provide your project URL + anon key
// 3. Set USE_SUPABASE = true
//
// See setupBackend() at the bottom of this file for the exact
// snippet to paste. The api object's method signatures are
// already aligned with the SDK's call shape.
// ============================================================
//
// Required tables:
//   properties (id, title_en, title_ar, area, project, developer, type,
//               price, bedrooms, bathrooms, sqm, finishing, delivery,
//               installments, featured, exclusive, new, image, gallery,
//               description_en, description_ar, score, lat, lng,
//               video_url, created_at)
//   developers (id, name, founded, projects, logo_url)
//   areas      (id, slug, name_en, name_ar, image, tagline_en, tagline_ar,
//               projects, roi, lat, lng)
//   leads      (id, name, email, phone, message, property_id, status, created_at)
//
// ============================================================

const USE_SUPABASE = false; // flip to true after configuring

// Mock leads store (would live in Supabase 'leads' table)
const MOCK_LEADS = [
  { id: 1001, name: 'Mohamed Hassan', email: 'm.hassan@email.com', phone: '+201234567890', message: 'Interested in Hacienda Bay penthouse', property_id: 1, status: 'new', created_at: '2025-11-14T10:30:00Z' },
  { id: 1002, name: 'Yasmin Adel', email: 'yasmin@email.com', phone: '+201112223344', message: 'Looking for villas in New Cairo', property_id: 2, status: 'contacted', created_at: '2025-11-13T14:15:00Z' },
  { id: 1003, name: 'Karim Tarek', email: 'karim.t@email.com', phone: '+201555666777', message: 'Mortgage info needed', property_id: 4, status: 'qualified', created_at: '2025-11-12T09:00:00Z' },
  { id: 1004, name: 'Salma Naguib', email: 'salma@email.com', phone: '+201998877665', message: 'Schedule viewing this weekend', property_id: 7, status: 'new', created_at: '2025-11-11T16:45:00Z' },
  { id: 1005, name: 'Omar Refaat', email: 'omar.r@email.com', phone: '+201123456789', message: 'Investment portfolio inquiry', property_id: null, status: 'qualified', created_at: '2025-11-10T11:20:00Z' },
];

// Unified data API — same shape whether backed by mock or Supabase
const api = {
  async listProperties() {
    if (USE_SUPABASE) {
      // const { data, error } = await client.from('properties').select('*');
      // if (error) throw error; return data;
    }
    return PROPERTIES;
  },
  async getProperty(id) {
    if (USE_SUPABASE) {
      // const { data } = await client.from('properties').select('*').eq('id', id).single();
      // return data;
    }
    return PROPERTIES.find(p => p.id === id);
  },
  async createLead(lead) {
    if (USE_SUPABASE) {
      // const { data } = await client.from('leads').insert([lead]).select();
      // return data[0];
    }
    const newLead = { ...lead, id: Date.now(), status: 'new', created_at: new Date().toISOString() };
    MOCK_LEADS.unshift(newLead);
    return newLead;
  },
  async listLeads() {
    if (USE_SUPABASE) {
      // const { data } = await client.from('leads').select('*').order('created_at', { ascending: false });
      // return data;
    }
    return MOCK_LEADS;
  },
  async updateLeadStatus(id, status) {
    if (USE_SUPABASE) {
      // await client.from('leads').update({ status }).eq('id', id);
    }
    const lead = MOCK_LEADS.find(l => l.id === id);
    if (lead) lead.status = status;
    return lead;
  },
  async addProperty(prop) {
    if (USE_SUPABASE) {
      // const { data } = await client.from('properties').insert([prop]).select();
      // return data[0];
    }
    const newProp = { ...prop, id: Math.max(...PROPERTIES.map(p => p.id)) + 1 };
    PROPERTIES.unshift(newProp);
    return newProp;
  },
  async deleteProperty(id) {
    if (USE_SUPABASE) {
      // await client.from('properties').delete().eq('id', id);
    }
    const idx = PROPERTIES.findIndex(p => p.id === id);
    if (idx > -1) PROPERTIES.splice(idx, 1);
  },
  async updateProperty(id, updates) {
    if (USE_SUPABASE) {
      // const { data } = await client.from('properties').update(updates).eq('id', id).select();
      // return data[0];
    }
    const idx = PROPERTIES.findIndex(p => p.id === id);
    if (idx > -1) PROPERTIES[idx] = { ...PROPERTIES[idx], ...updates };
    return PROPERTIES[idx];
  },
};

// ============================================================
// LOGO COMPONENT — Three nested triangles forming stylized "A"
// ============================================================
function AtlantisLogo({ size = 42, color, showWordmark = false, isRTL = false, vertical = false }) {
  // Triangle apex angles match the uploaded logo's geometry
  // viewBox 100x100, baseline at y=85, apex at y=15 for outer
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: vertical ? '12px' : '14px', flexDirection: vertical ? 'column' : 'row' }}>
      <svg viewBox="0 0 100 100" width={size} height={size} style={{ flexShrink: 0 }}>
        {/* Outer triangle */}
        <path
          d="M 50 12 L 90 88 L 10 88 Z"
          fill="none"
          stroke={color || '#F7F4ED'}
          strokeWidth="3.5"
          strokeLinejoin="miter"
        />
        {/* Middle triangle */}
        <path
          d="M 50 22 L 80 88 L 20 88 Z"
          fill="none"
          stroke={color || '#F7F4ED'}
          strokeWidth="3.5"
          strokeLinejoin="miter"
        />
        {/* Inner triangle */}
        <path
          d="M 50 32 L 70 88 L 30 88 Z"
          fill="none"
          stroke={color || '#F7F4ED'}
          strokeWidth="3.5"
          strokeLinejoin="miter"
        />
      </svg>
      {showWordmark && (
        <div style={{ textAlign: isRTL ? 'right' : 'left', lineHeight: 1 }}>
          <div style={{
            fontSize: vertical ? `${size * 0.42}px` : `${size * 0.48}px`,
            fontWeight: 400,
            letterSpacing: '0.18em',
            color: color || '#F7F4ED',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            lineHeight: 1,
          }}>ATLANTIS</div>
          <div style={{
            fontSize: vertical ? `${size * 0.18}px` : `${size * 0.20}px`,
            letterSpacing: '0.32em',
            color: '#C9A961',
            marginTop: vertical ? '8px' : '5px',
            fontWeight: 500,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}>REAL ESTATE</div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// SKYLINE SILHOUETTE — Animated city skyline backdrop
// ============================================================
function SkylineSilhouette({ color = '#0A1628', opacity = 1 }) {
  return (
    <svg viewBox="0 0 1920 200" preserveAspectRatio="xMidYEnd slice" style={{ width: '100%', height: '100%', display: 'block', opacity }}>
      <defs>
        <linearGradient id="skylineGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0" />
          <stop offset="100%" stopColor={color} stopOpacity="1" />
        </linearGradient>
      </defs>
      {/* Back layer of buildings — faded */}
      <g fill="url(#skylineGrad)">
        <path d="M0,200 L0,140 L60,140 L60,100 L120,100 L120,130 L180,130 L180,80 L240,80 L240,110 L300,110 L300,70 L360,70 L360,120 L420,120 L420,90 L480,90 L480,60 L540,60 L540,100 L600,100 L600,75 L660,75 L660,110 L720,110 L720,85 L780,85 L780,120 L840,120 L840,95 L900,95 L900,70 L960,70 L960,105 L1020,105 L1020,80 L1080,80 L1080,115 L1140,115 L1140,85 L1200,85 L1200,55 L1260,55 L1260,95 L1320,95 L1320,75 L1380,75 L1380,110 L1440,110 L1440,90 L1500,90 L1500,65 L1560,65 L1560,100 L1620,100 L1620,80 L1680,80 L1680,115 L1740,115 L1740,90 L1800,90 L1800,125 L1860,125 L1860,100 L1920,100 L1920,200 Z" />
      </g>
      {/* Front layer — sharper, darker */}
      <g fill={color}>
        <path d="M0,200 L0,170 L40,170 L40,150 L100,150 L100,170 L160,170 L160,130 L220,130 L220,160 L280,160 L280,140 L340,140 L340,165 L400,165 L400,145 L460,145 L460,120 L520,120 L520,155 L580,155 L580,135 L640,135 L640,160 L700,160 L700,140 L760,140 L760,125 L820,125 L820,150 L880,150 L880,170 L940,170 L940,145 L1000,145 L1000,130 L1060,130 L1060,160 L1120,160 L1120,135 L1180,135 L1180,155 L1240,155 L1240,125 L1300,125 L1300,150 L1360,150 L1360,170 L1420,170 L1420,140 L1480,140 L1480,160 L1540,160 L1540,130 L1600,130 L1600,155 L1660,155 L1660,135 L1720,135 L1720,165 L1780,165 L1780,145 L1840,145 L1840,170 L1900,170 L1900,150 L1920,150 L1920,200 Z" />
      </g>
      {/* Window lights — small gold dots */}
      <g fill="#C9A961" opacity="0.5">
        <rect x="65" y="155" width="2" height="3" />
        <rect x="75" y="160" width="2" height="3" />
        <rect x="170" y="140" width="2" height="3" />
        <rect x="185" y="145" width="2" height="3" />
        <rect x="290" y="150" width="2" height="3" />
        <rect x="465" y="130" width="2" height="3" />
        <rect x="475" y="135" width="2" height="3" />
        <rect x="585" y="145" width="2" height="3" />
        <rect x="765" y="135" width="2" height="3" />
        <rect x="945" y="155" width="2" height="3" />
        <rect x="1005" y="140" width="2" height="3" />
        <rect x="1125" y="145" width="2" height="3" />
        <rect x="1245" y="135" width="2" height="3" />
        <rect x="1365" y="160" width="2" height="3" />
        <rect x="1485" y="150" width="2" height="3" />
        <rect x="1605" y="140" width="2" height="3" />
        <rect x="1665" y="145" width="2" height="3" />
        <rect x="1845" y="155" width="2" height="3" />
      </g>
    </svg>
  );
}

// ============================================================
// AMBIENT BACKGROUND — Fixed site-wide atmospheric layer
// Renders behind ALL content: gradient mesh + parallax glow orbs
// + faint grid + vignette. Active across every page.
// ============================================================
function AmbientBackground({ isDark }) {
  const layer1Ref = useRef(null);
  const layer2Ref = useRef(null);
  const layer3Ref = useRef(null);
  const orb1Ref = useRef(null);
  const orb2Ref = useRef(null);

  useEffect(() => {
    let raf;
    const handleScroll = () => {
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        // Each architectural layer drifts at a different rate (depth illusion)
        if (layer1Ref.current) layer1Ref.current.style.transform = `translate3d(0, ${y * 0.05}px, 0) scale(1.05)`;
        if (layer2Ref.current) layer2Ref.current.style.transform = `translate3d(0, ${y * -0.08}px, 0) scale(1.08)`;
        if (layer3Ref.current) layer3Ref.current.style.transform = `translate3d(0, ${y * 0.12}px, 0) scale(1.04)`;
        if (orb1Ref.current) orb1Ref.current.style.transform = `translate3d(0, ${y * 0.15}px, 0)`;
        if (orb2Ref.current) orb2Ref.current.style.transform = `translate3d(0, ${y * -0.1}px, 0)`;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => { window.removeEventListener('scroll', handleScroll); cancelAnimationFrame(raf); };
  }, []);

  const baseBg = isDark
    ? 'radial-gradient(ellipse at top, #0E1D33 0%, #0A1628 50%, #050B17 100%)'
    : 'radial-gradient(ellipse at top, #FFFFFF 0%, #F7F4ED 50%, #EDE7D9 100%)';

  // Architectural reference images (kept faint, blurred, blended)
  // These give the page a constant "luxury real-estate atmosphere"
  const archImage1 = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=2400&q=85'; // New Cairo modern tower
  const archImage2 = 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=2400&q=85'; // New Capital skyline
  const archImage3 = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=2400&q=85'; // Luxury villa exterior

  return (
    <div aria-hidden="true" style={{
      position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
      background: baseBg,
      overflow: 'hidden',
    }}>
      {/* === LAYER 1: Hero architectural image — top of page === */}
      <div ref={layer1Ref} style={{
        position: 'absolute', top: '-5%', left: '-5%', right: '-5%', height: '70vh',
        willChange: 'transform',
      }}>
        <div style={{
          width: '100%', height: '100%',
          backgroundImage: `url("${archImage2}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          filter: isDark ? 'blur(2px) brightness(0.35) saturate(0.9) contrast(1.1)' : 'blur(2px) brightness(0.95) saturate(0.7) contrast(1.05)',
          opacity: isDark ? 0.35 : 0.18,
          mixBlendMode: isDark ? 'screen' : 'multiply',
        }} />
        {/* Mask so it fades into the page */}
        <div style={{
          position: 'absolute', inset: 0,
          background: isDark
            ? 'linear-gradient(180deg, rgba(10, 22, 40, 0.5) 0%, rgba(10, 22, 40, 0.85) 70%, #0A1628 100%)'
            : 'linear-gradient(180deg, rgba(247, 244, 237, 0.4) 0%, rgba(247, 244, 237, 0.85) 70%, #F7F4ED 100%)',
        }} />
      </div>

      {/* === LAYER 2: Mid-page architectural accent (villa) === */}
      <div ref={layer2Ref} style={{
        position: 'absolute', top: '50vh', left: '-10%', width: '70%', height: '90vh',
        willChange: 'transform',
      }}>
        <div style={{
          width: '100%', height: '100%',
          backgroundImage: `url("${archImage3}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: isDark ? 'blur(3px) brightness(0.3) saturate(0.85)' : 'blur(3px) brightness(1) saturate(0.65)',
          opacity: isDark ? 0.22 : 0.12,
          mixBlendMode: isDark ? 'screen' : 'multiply',
          maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 75%)',
        }} />
      </div>

      {/* === LAYER 3: Bottom architectural accent (skyline / compound) === */}
      <div ref={layer3Ref} style={{
        position: 'absolute', top: '120vh', right: '-10%', width: '70%', height: '90vh',
        willChange: 'transform',
      }}>
        <div style={{
          width: '100%', height: '100%',
          backgroundImage: `url("${archImage1}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: isDark ? 'blur(3px) brightness(0.35) saturate(0.9)' : 'blur(3px) brightness(1) saturate(0.65)',
          opacity: isDark ? 0.22 : 0.12,
          mixBlendMode: isDark ? 'screen' : 'multiply',
          maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 75%)',
        }} />
      </div>

      {/* === Gradient color wash on top of imagery === */}
      <div style={{
        position: 'absolute', inset: 0,
        background: isDark ? `
          radial-gradient(ellipse 80% 60% at 15% 20%, rgba(201, 169, 97, 0.10) 0%, transparent 60%),
          radial-gradient(ellipse 70% 50% at 85% 60%, rgba(46, 70, 110, 0.35) 0%, transparent 60%),
          radial-gradient(ellipse 90% 70% at 50% 90%, rgba(201, 169, 97, 0.08) 0%, transparent 60%)
        ` : `
          radial-gradient(ellipse 80% 60% at 15% 20%, rgba(201, 169, 97, 0.14) 0%, transparent 60%),
          radial-gradient(ellipse 70% 50% at 85% 60%, rgba(201, 169, 97, 0.10) 0%, transparent 60%),
          radial-gradient(ellipse 90% 70% at 50% 90%, rgba(10, 22, 40, 0.06) 0%, transparent 60%)
        `,
      }} />

      {/* === Floating gold glow orbs (atmosphere) === */}
      <div ref={orb1Ref} style={{
        position: 'absolute', top: '20%', left: '8%',
        width: '480px', height: '480px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(201, 169, 97, 0.14), transparent 70%)',
        filter: 'blur(80px)',
        animation: 'floatY 12s ease-in-out infinite',
      }} />
      <div ref={orb2Ref} style={{
        position: 'absolute', top: '55%', right: '5%',
        width: '560px', height: '560px', borderRadius: '50%',
        background: isDark
          ? 'radial-gradient(circle, rgba(46, 70, 110, 0.45), transparent 70%)'
          : 'radial-gradient(circle, rgba(201, 169, 97, 0.1), transparent 70%)',
        filter: 'blur(100px)',
        animation: 'floatY 15s ease-in-out infinite 3s',
      }} />

      {/* === Subtle architectural grid (blueprint feel) === */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: isDark
          ? 'linear-gradient(rgba(201, 169, 97, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(201, 169, 97, 0.035) 1px, transparent 1px)'
          : 'linear-gradient(rgba(10, 22, 40, 0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(10, 22, 40, 0.025) 1px, transparent 1px)',
        backgroundSize: '80px 80px',
        maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
      }} />

      {/* === Triangle motif from logo, faint, drifting === */}
      <svg viewBox="0 0 100 100" style={{
        position: 'absolute', top: '15%', right: '12%',
        width: '320px', height: '320px',
        opacity: isDark ? 0.04 : 0.06,
        animation: 'gentleBob 8s ease-in-out infinite',
      }} aria-hidden="true">
        <path d="M 50 12 L 90 88 L 10 88 Z" fill="none" stroke="#C9A961" strokeWidth="0.8" />
        <path d="M 50 22 L 80 88 L 20 88 Z" fill="none" stroke="#C9A961" strokeWidth="0.8" />
        <path d="M 50 32 L 70 88 L 30 88 Z" fill="none" stroke="#C9A961" strokeWidth="0.8" />
      </svg>
      <svg viewBox="0 0 100 100" style={{
        position: 'absolute', bottom: '20%', left: '8%',
        width: '240px', height: '240px',
        opacity: isDark ? 0.03 : 0.05,
        animation: 'gentleBob 10s ease-in-out infinite 2s',
        transform: 'rotate(15deg)',
      }} aria-hidden="true">
        <path d="M 50 12 L 90 88 L 10 88 Z" fill="none" stroke="#C9A961" strokeWidth="0.8" />
        <path d="M 50 22 L 80 88 L 20 88 Z" fill="none" stroke="#C9A961" strokeWidth="0.8" />
        <path d="M 50 32 L 70 88 L 30 88 Z" fill="none" stroke="#C9A961" strokeWidth="0.8" />
      </svg>

      {/* === Vignette === */}
      <div style={{
        position: 'absolute', inset: 0,
        background: isDark
          ? 'radial-gradient(ellipse at center, transparent 0%, rgba(5, 11, 23, 0.6) 100%)'
          : 'radial-gradient(ellipse at center, transparent 40%, rgba(10, 22, 40, 0.08) 100%)',
      }} />

      {/* === Faint city skyline anchor at bottom === */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '180px', opacity: isDark ? 0.45 : 0.18 }}>
        <SkylineSilhouette color={isDark ? '#050B17' : '#0A1628'} />
      </div>
    </div>
  );
}


// ============================================================
// PAGE BACKDROP — Architectural hero strip behind page content
// Each page passes its own image; styled subtly to integrate
// with the global AmbientBackground rather than dominate
// ============================================================
function PageBackdrop({ image, height = '70vh', overlayStrength = 0.85, isDark }) {
  const ref = useRef(null);

  useEffect(() => {
    let raf;
    const handleScroll = () => {
      raf = requestAnimationFrame(() => {
        if (!ref.current) return;
        const offset = window.scrollY * 0.3;
        ref.current.style.transform = `translate3d(0, ${offset}px, 0) scale(1.05)`;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => { window.removeEventListener('scroll', handleScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <div aria-hidden="true" style={{
      position: 'absolute', top: 0, left: 0, right: 0, height,
      overflow: 'hidden', zIndex: 0, pointerEvents: 'none',
    }}>
      <div ref={ref} style={{ position: 'absolute', inset: '-10% 0', willChange: 'transform' }}>
        <img src={image} alt="" style={{
          width: '100%', height: '100%', objectFit: 'cover',
          filter: isDark ? 'brightness(0.45) saturate(1.05) contrast(1.05)' : 'brightness(0.95) saturate(0.85)',
        }} />
      </div>
      {/* Gradient fade so it bleeds into page */}
      <div style={{
        position: 'absolute', inset: 0,
        background: isDark
          ? `linear-gradient(180deg, rgba(10, 22, 40, 0.35) 0%, rgba(10, 22, 40, ${overlayStrength}) 70%, #0A1628 100%)`
          : `linear-gradient(180deg, rgba(247, 244, 237, 0.3) 0%, rgba(247, 244, 237, ${overlayStrength}) 70%, #F7F4ED 100%)`,
      }} />
      {/* Gold accent line at the bottom edge */}
      <div style={{
        position: 'absolute', bottom: 0, left: '20%', right: '20%', height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(201, 169, 97, 0.4), transparent)',
      }} />
    </div>
  );
}


// ============================================================
// LOADING SCREEN — Animated logo entrance
// ============================================================
function LoadingScreen({ isDark }) {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 100);
    const t2 = setTimeout(() => setPhase(2), 1400);
    const t3 = setTimeout(() => setPhase(3), 2200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#050B17',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column',
      opacity: phase >= 3 ? 0 : 1,
      transition: 'opacity 0.5s ease',
      pointerEvents: phase >= 3 ? 'none' : 'auto',
    }}>
      {/* Radial atmospheric glow */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at center, rgba(201, 169, 97, 0.12) 0%, transparent 60%)',
      }} />

      {/* Animated triangles */}
      <svg viewBox="0 0 100 100" width="140" height="140" style={{ position: 'relative', zIndex: 2 }}>
        <defs>
          <linearGradient id="loadGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#F7F4ED" />
            <stop offset="100%" stopColor="#C9A961" />
          </linearGradient>
        </defs>
        {/* Outer triangle - draws first */}
        <path
          d="M 50 12 L 90 88 L 10 88 Z"
          fill="none" stroke="url(#loadGrad)" strokeWidth="2.5" strokeLinejoin="miter"
          style={{
            strokeDasharray: 240,
            strokeDashoffset: phase >= 1 ? 0 : 240,
            transition: 'stroke-dashoffset 0.8s cubic-bezier(0.65, 0, 0.35, 1)',
          }}
        />
        {/* Middle triangle */}
        <path
          d="M 50 22 L 80 88 L 20 88 Z"
          fill="none" stroke="url(#loadGrad)" strokeWidth="2.5" strokeLinejoin="miter"
          style={{
            strokeDasharray: 200,
            strokeDashoffset: phase >= 1 ? 0 : 200,
            transition: 'stroke-dashoffset 0.8s cubic-bezier(0.65, 0, 0.35, 1) 0.2s',
          }}
        />
        {/* Inner triangle */}
        <path
          d="M 50 32 L 70 88 L 30 88 Z"
          fill="none" stroke="url(#loadGrad)" strokeWidth="2.5" strokeLinejoin="miter"
          style={{
            strokeDasharray: 160,
            strokeDashoffset: phase >= 1 ? 0 : 160,
            transition: 'stroke-dashoffset 0.8s cubic-bezier(0.65, 0, 0.35, 1) 0.4s',
          }}
        />
      </svg>

      {/* Wordmark fades in */}
      <div style={{
        marginTop: '32px',
        opacity: phase >= 2 ? 1 : 0,
        transform: phase >= 2 ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.8s cubic-bezier(0.19, 1, 0.22, 1)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '24px', letterSpacing: '0.4em', color: '#F7F4ED', fontWeight: 300, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          ATLANTIS
        </div>
        <div style={{ fontSize: '10px', letterSpacing: '0.5em', color: '#C9A961', marginTop: '12px', fontWeight: 500 }}>
          REAL ESTATE
        </div>
      </div>

      {/* Loading bar */}
      <div style={{
        marginTop: '48px',
        width: '200px', height: '1px',
        background: 'rgba(247, 244, 237, 0.1)',
        overflow: 'hidden',
        opacity: phase >= 1 && phase < 3 ? 1 : 0,
        transition: 'opacity 0.4s ease',
      }}>
        <div style={{
          width: '40%', height: '100%',
          background: 'linear-gradient(90deg, transparent, #C9A961, transparent)',
          animation: 'loadingBar 1.6s ease-in-out infinite',
        }} />
      </div>
    </div>
  );
}

// ============================================================
// CURSOR GLOW — Subtle gold orb follows mouse
// ============================================================
function CursorGlow() {
  const dotRef = useRef(null);
  const glowRef = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Only on devices with fine pointer (desktop)
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(pointer: fine)');
    setEnabled(mq.matches);
    if (!mq.matches) return;

    let mouseX = 0, mouseY = 0;
    let glowX = 0, glowY = 0;

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${mouseX - 4}px, ${mouseY - 4}px)`;
      }
    };

    let raf;
    const animate = () => {
      glowX += (mouseX - glowX) * 0.12;
      glowY += (mouseY - glowY) * 0.12;
      if (glowRef.current) {
        glowRef.current.style.transform = `translate(${glowX - 150}px, ${glowY - 150}px)`;
      }
      raf = requestAnimationFrame(animate);
    };
    animate();

    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  if (!enabled) return null;

  return (
    <>
      <div ref={glowRef} style={{
        position: 'fixed', top: 0, left: 0,
        width: '300px', height: '300px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(201, 169, 97, 0.15) 0%, transparent 60%)',
        pointerEvents: 'none', zIndex: 9998,
        mixBlendMode: 'screen',
        willChange: 'transform',
      }} />
      <div ref={dotRef} style={{
        position: 'fixed', top: 0, left: 0,
        width: '8px', height: '8px', borderRadius: '50%',
        background: '#C9A961',
        boxShadow: '0 0 12px rgba(201, 169, 97, 0.6)',
        pointerEvents: 'none', zIndex: 9999,
        willChange: 'transform',
        mixBlendMode: 'difference',
      }} />
    </>
  );
}

// ============================================================
// PAGE TRANSITION VEIL — Cinematic fade between pages
// ============================================================
function PageTransitionVeil({ active, isDark }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: isDark
        ? 'linear-gradient(180deg, #050B17 0%, #0A1628 100%)'
        : 'linear-gradient(180deg, #FFFFFF 0%, #F7F4ED 100%)',
      opacity: active ? 1 : 0,
      pointerEvents: active ? 'auto' : 'none',
      transition: 'opacity 0.35s cubic-bezier(0.65, 0, 0.35, 1)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        opacity: active ? 1 : 0,
        transition: 'opacity 0.3s ease 0.1s',
      }}>
        <svg viewBox="0 0 100 100" width="60" height="60">
          <g style={{ transformOrigin: '50px 50px', animation: active ? 'spin 1s linear infinite' : 'none' }}>
            <path d="M 50 12 L 90 88 L 10 88 Z" fill="none" stroke="#C9A961" strokeWidth="3" strokeLinejoin="miter" />
            <path d="M 50 22 L 80 88 L 20 88 Z" fill="none" stroke="#C9A961" strokeWidth="3" strokeLinejoin="miter" opacity="0.6" />
            <path d="M 50 32 L 70 88 L 30 88 Z" fill="none" stroke="#C9A961" strokeWidth="3" strokeLinejoin="miter" opacity="0.3" />
          </g>
        </svg>
      </div>
    </div>
  );
}

// ============================================================
// MAGNETIC BUTTON — Button subtly follows cursor on hover
// ============================================================
function MagneticButton({ children, onClick, style = {}, className = '', strength = 0.25 }) {
  const ref = useRef(null);

  const handleMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    ref.current.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
  };

  const handleLeave = () => {
    if (ref.current) ref.current.style.transform = 'translate(0, 0)';
  };

  return (
    <button
      ref={ref}
      onClick={onClick}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={className}
      style={{ ...style, transition: 'transform 0.3s cubic-bezier(0.19, 1, 0.22, 1)' }}
    >
      {children}
    </button>
  );
}

// ============================================================
// MARQUEE — Scrolling luxury text strip between sections
// ============================================================
function Marquee({ items, lang }) {
  const text = items.join('  •  ');
  return (
    <div style={{
      position: 'relative',
      padding: '32px 0',
      overflow: 'hidden',
      borderTop: '1px solid rgba(201, 169, 97, 0.15)',
      borderBottom: '1px solid rgba(201, 169, 97, 0.15)',
      background: 'linear-gradient(90deg, transparent, rgba(201, 169, 97, 0.04), transparent)',
    }}>
      <div style={{
        display: 'flex',
        gap: '0',
        whiteSpace: 'nowrap',
        animation: `marquee ${lang === 'ar' ? '40s' : '50s'} linear infinite`,
      }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{
            display: 'inline-block',
            paddingRight: '60px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 'clamp(36px, 5vw, 64px)',
            fontWeight: 300,
            letterSpacing: '0.05em',
            color: 'rgba(247, 244, 237, 0.08)',
            textTransform: 'uppercase',
          }}>
            {text}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// FLOATING UI BADGES — Achievement marks floating around hero
// ============================================================
function FloatingBadges({ lang }) {
  const badges = lang === 'ar' ? [
    { label: '٤.٩ تقييم', sub: 'العملاء', icon: '★' },
    { label: '٣٠+ مطور', sub: 'شراكات', icon: '◆' },
    { label: '١٢٥٠+ صفقة', sub: 'مكتملة', icon: '✓' },
  ] : [
    { label: '4.9 RATING', sub: 'CLIENTS', icon: '★' },
    { label: '30+ DEVELOPERS', sub: 'PARTNERS', icon: '◆' },
    { label: '1250+ DEALS', sub: 'COMPLETED', icon: '✓' },
  ];

  const positions = [
    { top: '20%', right: '8%', delay: '0.8s' },
    { top: '55%', right: '4%', delay: '1.1s' },
    { top: '35%', right: '12%', delay: '1.4s' },
  ];

  return (
    <>
      {badges.map((b, i) => (
        <div key={i} className="desktop-only" style={{
          position: 'absolute',
          ...positions[i],
          zIndex: 3,
          opacity: 0,
          animation: `fadeUp 1s cubic-bezier(0.19, 1, 0.22, 1) ${positions[i].delay} forwards, gentleBob 6s ease-in-out infinite ${positions[i].delay}`,
        }}>
          <div style={{
            background: 'rgba(10, 22, 40, 0.6)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(201, 169, 97, 0.3)',
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          }}>
            <div style={{
              width: '40px', height: '40px',
              background: 'linear-gradient(135deg, #C9A961, #D4B876)',
              color: '#0A1628',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '18px', fontWeight: 700,
            }}>
              {b.icon}
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#F7F4ED', letterSpacing: '0.05em' }}>{b.label}</div>
              <div style={{ fontSize: '10px', color: '#C9A961', letterSpacing: '0.2em', marginTop: '2px' }}>{b.sub}</div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

// ============================================================
// BEFORE/AFTER SLIDER — Drag to reveal renovation transformation
// ============================================================
function BeforeAfterSlider({ before, after, beforeLabel, afterLabel, lang }) {
  const [position, setPosition] = useState(50);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const updatePosition = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setPosition(pct);
  };

  const handleMove = (e) => {
    if (!draggingRef.current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    updatePosition(x);
  };

  useEffect(() => {
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  return (
    <div ref={containerRef}
      style={{ position: 'relative', aspectRatio: '16/9', overflow: 'hidden', cursor: 'ew-resize', userSelect: 'none', border: '1px solid rgba(201, 169, 97, 0.2)' }}
      onMouseDown={(e) => { draggingRef.current = true; updatePosition(e.clientX); }}
      onTouchStart={(e) => { draggingRef.current = true; updatePosition(e.touches[0].clientX); }}
    >
      {/* After image (full) */}
      <img src={after} alt="After" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
      <div style={{ position: 'absolute', top: '24px', right: '24px', background: 'rgba(201, 169, 97, 0.9)', color: '#0A1628', padding: '8px 14px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em' }}>
        {afterLabel}
      </div>

      {/* Before image (clipped) */}
      <div style={{ position: 'absolute', inset: 0, width: `${position}%`, overflow: 'hidden' }}>
        <img src={before} alt="Before" style={{ position: 'absolute', inset: 0, width: `${100 / (position / 100)}%`, height: '100%', objectFit: 'cover' }} draggable={false} />
        <div style={{ position: 'absolute', top: '24px', left: '24px', background: 'rgba(10, 22, 40, 0.85)', color: '#F7F4ED', padding: '8px 14px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', border: '1px solid rgba(247, 244, 237, 0.2)' }}>
          {beforeLabel}
        </div>
      </div>

      {/* Slider handle */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0,
        left: `${position}%`, width: '2px',
        background: '#C9A961',
        transform: 'translateX(-1px)',
        pointerEvents: 'none',
      }}>
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '48px', height: '48px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #C9A961, #D4B876)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
          color: '#0A1628',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5l-5 7 5 7zM16 5l5 7-5 7z" />
          </svg>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 3D TILT CARD — Card tilts based on cursor position
// ============================================================
function TiltCard({ children, style = {}, className = '', maxTilt = 8 }) {
  const ref = useRef(null);

  const handleMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    ref.current.style.transform = `perspective(1000px) rotateY(${x * maxTilt}deg) rotateX(${-y * maxTilt}deg) translateZ(0)`;
  };

  const handleLeave = () => {
    if (ref.current) ref.current.style.transform = 'perspective(1000px) rotateY(0) rotateX(0) translateZ(0)';
  };

  return (
    <div
      ref={ref}
      className={className}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ ...style, transition: 'transform 0.5s cubic-bezier(0.19, 1, 0.22, 1)', transformStyle: 'preserve-3d' }}
    >
      {children}
    </div>
  );
}

// ============================================================
// SCROLL-DRIVEN PARALLAX SKYLINE STRIP
// Multi-layer skylines moving at different speeds (depth)
// ============================================================
function ParallaxSkylines({ isDark }) {
  const layer1Ref = useRef(null);
  const layer2Ref = useRef(null);
  const layer3Ref = useRef(null);

  useEffect(() => {
    let raf;
    const handleScroll = () => {
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        if (layer1Ref.current) layer1Ref.current.style.transform = `translate3d(${-y * 0.05}px, 0, 0)`;
        if (layer2Ref.current) layer2Ref.current.style.transform = `translate3d(${-y * 0.12}px, 0, 0)`;
        if (layer3Ref.current) layer3Ref.current.style.transform = `translate3d(${-y * 0.2}px, 0, 0)`;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => { window.removeEventListener('scroll', handleScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <div style={{ position: 'relative', height: '320px', overflow: 'hidden', marginTop: '80px' }}>
      {/* Sky gradient */}
      <div style={{
        position: 'absolute', inset: 0,
        background: isDark
          ? 'linear-gradient(180deg, transparent 0%, rgba(201, 169, 97, 0.08) 60%, rgba(10, 22, 40, 0) 100%)'
          : 'linear-gradient(180deg, transparent 0%, rgba(201, 169, 97, 0.12) 60%, rgba(247, 244, 237, 0) 100%)',
      }} />

      {/* Layer 1 — furthest back */}
      <div ref={layer1Ref} style={{ position: 'absolute', bottom: '60px', left: '-10%', right: '-10%', height: '160px', willChange: 'transform' }}>
        <SkylineSilhouette color={isDark ? '#1A2C47' : '#A8B5C8'} opacity={0.5} />
      </div>
      {/* Layer 2 — middle */}
      <div ref={layer2Ref} style={{ position: 'absolute', bottom: '30px', left: '-15%', right: '-15%', height: '180px', willChange: 'transform' }}>
        <SkylineSilhouette color={isDark ? '#0E1D33' : '#6B7A91'} opacity={0.7} />
      </div>
      {/* Layer 3 — closest, sharpest */}
      <div ref={layer3Ref} style={{ position: 'absolute', bottom: 0, left: '-20%', right: '-20%', height: '200px', willChange: 'transform' }}>
        <SkylineSilhouette color={isDark ? '#050B17' : '#0A1628'} opacity={1} />
      </div>
    </div>
  );
}

// ============================================================
// CINEMATIC SECTION — Full-bleed scroll-triggered reveal
// Used for storytelling sections between regular content
// ============================================================
function CinematicSection({ image, eyebrow, title, subtitle, lang, themeStyles, reverse = false }) {
  const ref = useRef(null);
  const imgRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);

    let raf;
    const onScroll = () => {
      raf = requestAnimationFrame(() => {
        if (!imgRef.current || !ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const winH = window.innerHeight;
        const progress = (winH - rect.top) / (winH + rect.height);
        const offset = (progress - 0.5) * 60;
        imgRef.current.style.transform = `translate3d(0, ${offset}px, 0) scale(1.15)`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);

  return (
    <section ref={ref} style={{ position: 'relative', minHeight: '80vh', overflow: 'hidden', display: 'flex', alignItems: 'center', padding: '120px 32px' }}>
      <div ref={imgRef} style={{ position: 'absolute', inset: '-10%', zIndex: 0, willChange: 'transform' }}>
        <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.4) saturate(1.1)' }} />
      </div>
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: 'linear-gradient(180deg, rgba(10, 22, 40, 0.5) 0%, rgba(10, 22, 40, 0.85) 100%)' }} />
      <div style={{ position: 'relative', zIndex: 2, maxWidth: '1440px', margin: '0 auto', width: '100%', display: 'flex', justifyContent: reverse ? 'flex-end' : 'flex-start' }}>
        <div style={{
          maxWidth: '640px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 1s cubic-bezier(0.19, 1, 0.22, 1), transform 1s cubic-bezier(0.19, 1, 0.22, 1)',
          color: '#F7F4ED',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
            <div style={{ width: '40px', height: '1px', background: '#C9A961' }} />
            <span style={{ fontSize: '11px', letterSpacing: '0.4em', color: '#C9A961', fontWeight: 600 }}>{eyebrow}</span>
          </div>
          <h2 className="serif" style={{ fontSize: 'clamp(40px, 6vw, 72px)', fontWeight: 400, lineHeight: 1.05, marginBottom: '24px', letterSpacing: '-0.02em' }}>
            {title}
          </h2>
          <p style={{ fontSize: '17px', lineHeight: 1.7, color: 'rgba(247, 244, 237, 0.8)', fontWeight: 300 }}>
            {subtitle}
          </p>
        </div>
      </div>
    </section>
  );
}



const translations = {
  en: {
    nav: { home: 'Home', listings: 'Properties', developers: 'Developers', areas: 'Areas', about: 'About', contact: 'Contact' },
    hero: {
      tagline: 'EGYPT • EST. 2014',
      title: 'Find Your Next Investment',
      titleAccent: 'with Atlantis',
      subtitle: 'Curated luxury properties across Egypt\'s most coveted destinations.',
      searchPlaceholder: 'Search by location, project, or developer...',
      cta: 'Explore Properties',
      ctaSecondary: 'Watch Showcase',
    },
    search: { location: 'Location', type: 'Property Type', price: 'Price Range', search: 'Search' },
    sections: {
      featured: 'Signature Projects',
      featuredSub: 'Hand-selected developments from Egypt\'s premier developers',
      categories: 'Browse by Category',
      areas: 'Prime Locations',
      areasSub: 'Discover Egypt\'s most prestigious investment destinations',
      whyUs: 'The Atlantis Standard',
      whyUsSub: 'Why discerning investors choose us',
      stats: 'By the Numbers',
      testimonials: 'Trusted by Investors',
      partners: 'Our Developer Partners',
      newsletter: 'Stay Informed',
    },
    categories: { apartments: 'Apartments', villas: 'Villas', chalets: 'Chalets', commercial: 'Commercial' },
    why: {
      one: { t: 'Curated Excellence', d: 'Every property vetted against the highest standards of quality and investment potential.' },
      two: { t: 'Trusted Partnerships', d: 'Direct relationships with Egypt\'s top 30+ developers ensure best pricing and priority access.' },
      three: { t: 'Investment Insight', d: 'Data-driven analysis and market intelligence to guide your most important decisions.' },
      four: { t: 'White-Glove Service', d: 'From discovery to keys, your dedicated advisor handles every detail with discretion.' },
    },
    listings: {
      title: 'Property Collection',
      subtitle: 'Browse our curated inventory',
      filters: 'Filters', clear: 'Clear All', sort: 'Sort by', priceLow: 'Price: Low to High', priceHigh: 'Price: High to Low', newest: 'Newest First', recommended: 'Recommended',
      grid: 'Grid', map: 'Map', save: 'Save', saved: 'Saved',
      bedrooms: 'Bedrooms', bathrooms: 'Bathrooms', area: 'Area', price: 'Price', developer: 'Developer', finishing: 'Finishing', delivery: 'Delivery', installments: 'Installments', any: 'Any',
      results: 'properties found',
      from: 'From', perMonth: 'monthly',
    },
    detail: {
      overview: 'Overview', specifications: 'Specifications', payment: 'Payment Plan', location: 'Location', amenities: 'Amenities', similar: 'Similar Properties',
      callNow: 'Call Now', whatsapp: 'WhatsApp', schedule: 'Schedule Viewing', share: 'Share',
      mortgage: 'Mortgage Calculator', loanAmount: 'Loan Amount', interestRate: 'Interest Rate', loanTerm: 'Loan Term (years)', monthlyPayment: 'Monthly Payment',
      downpayment: 'Down Payment', overYears: 'Over', years: 'years', deliveryIn: 'Delivery',
      agent: 'Your Advisor', specsTitle: 'Property Specifications',
    },
    about: {
      title: 'A Decade of Trust',
      subtitle: 'Defining luxury real estate in Egypt since 2014',
      story: 'Founded with a singular vision — to elevate Egypt\'s real estate experience to international standards — Atlantis has grown from a boutique advisory into one of the country\'s most respected brokerages. We represent visionary developers, sophisticated investors, and discerning end-users across the nation\'s most prestigious destinations.',
      vision: 'Vision', visionText: 'To become the most trusted name in Egyptian luxury real estate, recognized globally for integrity, expertise, and results.',
      mission: 'Mission', missionText: 'To deliver unparalleled real estate advisory through deep market knowledge, ethical practice, and obsessive client care.',
      values: 'Our Values', team: 'Leadership Team',
    },
    contact: { title: 'Begin Your Journey', subtitle: 'Our advisors respond within 30 minutes during business hours.', name: 'Full Name', email: 'Email', phone: 'Phone', message: 'How can we help?', send: 'Send Message', office: 'Head Office', hours: 'Sun – Thu, 10am – 8pm' },
    footer: { tagline: 'Egypt\'s premier luxury real estate brokerage.', quick: 'Navigate', services: 'Services', contact: 'Contact', rights: 'All rights reserved.', subscribe: 'Subscribe', emailPlace: 'Your email address' },
    common: { learnMore: 'Learn More', viewAll: 'View All', readMore: 'Read More', loading: 'Loading...', new: 'NEW', featured: 'FEATURED', exclusive: 'EXCLUSIVE', compare: 'Compare', compareAdded: 'Added to compare', compareMax: 'Max 4 properties', recentlyViewed: 'Recently Viewed', recentlyViewedSub: 'Pick up where you left off', watchVideo: 'Watch Video Tour', videoReel: 'Atlantis Showcase', admin: 'Admin' },
  },
  ar: {
    nav: { home: 'الرئيسية', listings: 'العقارات', developers: 'المطورون', areas: 'المناطق', about: 'من نحن', contact: 'تواصل معنا' },
    hero: {
      tagline: 'مصر • تأسست ٢٠١٤',
      title: 'استثمر مع',
      titleAccent: 'أتلانتس',
      subtitle: 'عقارات فاخرة منتقاة بعناية في أرقى وجهات مصر.',
      searchPlaceholder: 'ابحث حسب الموقع أو المشروع أو المطور...',
      cta: 'استكشف العقارات',
      ctaSecondary: 'شاهد الفيديو',
    },
    search: { location: 'الموقع', type: 'نوع العقار', price: 'النطاق السعري', search: 'بحث' },
    sections: {
      featured: 'مشاريع مميزة',
      featuredSub: 'مشاريع مختارة بعناية من نخبة المطورين',
      categories: 'تصفح حسب الفئة',
      areas: 'أرقى المواقع',
      areasSub: 'اكتشف أرقى الوجهات الاستثمارية في مصر',
      whyUs: 'معيار أتلانتس',
      whyUsSub: 'لماذا يختارنا المستثمرون',
      stats: 'بالأرقام',
      testimonials: 'موضع ثقة المستثمرين',
      partners: 'شركاؤنا من المطورين',
      newsletter: 'ابق على اطلاع',
    },
    categories: { apartments: 'شقق', villas: 'فيلات', chalets: 'شاليهات', commercial: 'تجاري' },
    why: {
      one: { t: 'التميز المنتقى', d: 'كل عقار يخضع لأعلى معايير الجودة والإمكانات الاستثمارية.' },
      two: { t: 'شراكات موثوقة', d: 'علاقات مباشرة مع أفضل ٣٠+ مطور في مصر تضمن لك أفضل الأسعار وأولوية الوصول.' },
      three: { t: 'رؤى استثمارية', d: 'تحليلات قائمة على البيانات لتوجيه قراراتك الأهم.' },
      four: { t: 'خدمة استثنائية', d: 'من البحث حتى التسليم، مستشار مخصص يتولى كل التفاصيل بسرية تامة.' },
    },
    listings: {
      title: 'مجموعة العقارات',
      subtitle: 'تصفح مجموعتنا المنتقاة',
      filters: 'الفلاتر', clear: 'مسح الكل', sort: 'ترتيب حسب', priceLow: 'السعر: من الأقل للأعلى', priceHigh: 'السعر: من الأعلى للأقل', newest: 'الأحدث', recommended: 'موصى به',
      grid: 'شبكة', map: 'خريطة', save: 'حفظ', saved: 'محفوظ',
      bedrooms: 'غرف النوم', bathrooms: 'الحمامات', area: 'المساحة', price: 'السعر', developer: 'المطور', finishing: 'التشطيب', delivery: 'التسليم', installments: 'الأقساط', any: 'الكل',
      results: 'عقار',
      from: 'يبدأ من', perMonth: 'شهرياً',
    },
    detail: {
      overview: 'نظرة عامة', specifications: 'المواصفات', payment: 'خطة السداد', location: 'الموقع', amenities: 'المرافق', similar: 'عقارات مشابهة',
      callNow: 'اتصل الآن', whatsapp: 'واتساب', schedule: 'احجز معاينة', share: 'مشاركة',
      mortgage: 'حاسبة التمويل العقاري', loanAmount: 'مبلغ القرض', interestRate: 'معدل الفائدة', loanTerm: 'مدة القرض (سنوات)', monthlyPayment: 'القسط الشهري',
      downpayment: 'مقدم', overYears: 'على مدار', years: 'سنوات', deliveryIn: 'التسليم',
      agent: 'مستشارك العقاري', specsTitle: 'مواصفات العقار',
    },
    about: {
      title: 'عقد من الثقة',
      subtitle: 'نُعرّف العقارات الفاخرة في مصر منذ ٢٠١٤',
      story: 'تأسست أتلانتس برؤية واحدة — الارتقاء بتجربة العقارات في مصر إلى المستوى العالمي — ونمت من شركة استشارية صغيرة لتصبح من أكثر شركات الوساطة العقارية احتراماً في البلاد. نمثل المطورين أصحاب الرؤى والمستثمرين الراقين والعملاء المميزين عبر أرقى وجهات الوطن.',
      vision: 'الرؤية', visionText: 'أن نصبح الاسم الأكثر ثقة في العقارات الفاخرة بمصر، معترف بنا عالمياً بالنزاهة والخبرة والنتائج.',
      mission: 'الرسالة', missionText: 'تقديم استشارات عقارية لا مثيل لها من خلال المعرفة العميقة بالسوق والممارسة الأخلاقية والاهتمام الفائق بالعميل.',
      values: 'قيمنا', team: 'فريق القيادة',
    },
    contact: { title: 'ابدأ رحلتك', subtitle: 'يستجيب مستشارونا خلال ٣٠ دقيقة في ساعات العمل.', name: 'الاسم الكامل', email: 'البريد الإلكتروني', phone: 'الهاتف', message: 'كيف يمكننا المساعدة؟', send: 'إرسال', office: 'المقر الرئيسي', hours: 'الأحد – الخميس، ١٠ص – ٨م' },
    footer: { tagline: 'أرقى شركة وساطة عقارية فاخرة في مصر.', quick: 'تنقل', services: 'الخدمات', contact: 'تواصل', rights: 'جميع الحقوق محفوظة.', subscribe: 'اشترك', emailPlace: 'بريدك الإلكتروني' },
    common: { learnMore: 'اعرف أكثر', viewAll: 'عرض الكل', readMore: 'اقرأ المزيد', loading: 'جاري التحميل...', new: 'جديد', featured: 'مميز', exclusive: 'حصري', compare: 'قارن', compareAdded: 'تمت إضافة العقار', compareMax: 'الحد الأقصى ٤ عقارات', recentlyViewed: 'شوهد مؤخراً', recentlyViewedSub: 'تابع من حيث توقفت', watchVideo: 'شاهد الجولة', videoReel: 'عرض أتلانتس', admin: 'الإدارة' },
  }
};

// ============== Sample data ==============
const AREAS = [
  { id: 'north-coast', name: { en: 'North Coast', ar: 'الساحل الشمالي' }, image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80', tagline: { en: 'Mediterranean luxury', ar: 'الفخامة المتوسطية' }, projects: 47, roi: '12-18%', lat: 30.9626, lng: 28.9551 },
  { id: 'new-cairo', name: { en: 'New Cairo', ar: 'القاهرة الجديدة' }, image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&q=80', tagline: { en: 'Urban sophistication', ar: 'رقي حضري' }, projects: 89, roi: '8-14%', lat: 30.0286, lng: 31.4954 },
  { id: 'new-capital', name: { en: 'New Administrative Capital', ar: 'العاصمة الإدارية' }, image: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1200&q=80', tagline: { en: 'Egypt\'s future', ar: 'مستقبل مصر' }, projects: 64, roi: '15-22%', lat: 30.0131, lng: 31.7416 },
  { id: 'sheikh-zayed', name: { en: 'Sheikh Zayed', ar: 'الشيخ زايد' }, image: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1200&q=80', tagline: { en: 'Refined living', ar: 'حياة راقية' }, projects: 53, roi: '7-12%', lat: 30.0710, lng: 30.9737 },
  { id: 'sokhna', name: { en: 'Ain Sokhna', ar: 'العين السخنة' }, image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200&q=80', tagline: { en: 'Red Sea retreat', ar: 'ملاذ البحر الأحمر' }, projects: 38, roi: '10-16%', lat: 29.6028, lng: 32.3286 },
];

const DEVELOPERS = [
  { id: 1, name: 'Emaar Misr', founded: 2005, projects: 12 },
  { id: 2, name: 'Palm Hills', founded: 1997, projects: 28 },
  { id: 3, name: 'SODIC', founded: 1996, projects: 22 },
  { id: 4, name: 'Hassan Allam', founded: 1936, projects: 18 },
  { id: 5, name: 'Mountain View', founded: 2005, projects: 24 },
  { id: 6, name: 'Tatweer Misr', founded: 2014, projects: 11 },
  { id: 7, name: 'Ora Developers', founded: 2015, projects: 9 },
  { id: 8, name: 'Misr Italia', founded: 1959, projects: 15 },
];

const PROPERTIES = [
  {
    id: 1, title: { en: 'Hacienda Bay Penthouse', ar: 'بنتهاوس هاسيندا باي' }, area: 'north-coast', areaName: { en: 'North Coast', ar: 'الساحل الشمالي' },
    project: 'Hacienda Bay', developer: 'Palm Hills', type: 'chalets', price: 18500000, currency: 'EGP',
    bedrooms: 4, bathrooms: 3, sqm: 285, finishing: 'Fully Finished', delivery: '2025', installments: 8,
    featured: true, exclusive: true, new: false,
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1600&q=85','https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600&q=85','https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=85','https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1600&q=85'],
    description: { en: 'A masterpiece of coastal architecture overlooking the Mediterranean. This penthouse features panoramic sea views, private terrace, and bespoke Italian finishes throughout.', ar: 'تحفة معمارية ساحلية تطل على البحر المتوسط. يتميز هذا البنتهاوس بإطلالات بانورامية وتراس خاص وتشطيبات إيطالية فاخرة.' },
    score: 94,
    lat: 30.0125, lng: 31.74, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/Sintel.mp4',
    lat: 30.961, lng: 28.954, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4',
    lat: 30.028, lng: 31.494, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
    lat: 29.604, lng: 32.33, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
    lat: 30.072, lng: 30.9745, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
    lat: 30.014, lng: 31.742, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
    lat: 30.029, lng: 31.496, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
    lat: 30.965, lng: 28.958, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
  },
  {
    id: 2, title: { en: 'Mivida Garden Villa', ar: 'فيلا حدائق ميڤيدا' }, area: 'new-cairo', areaName: { en: 'New Cairo', ar: 'القاهرة الجديدة' },
    project: 'Mivida', developer: 'Emaar Misr', type: 'villas', price: 32000000, currency: 'EGP',
    bedrooms: 5, bathrooms: 5, sqm: 520, finishing: 'Semi-Finished', delivery: 'Ready', installments: 6,
    featured: true, new: true,
    image: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1613977257363-707ba9348227?w=1600&q=85','https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1600&q=85','https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=85'],
    description: { en: 'A standalone villa on a 600m² plot featuring private garden, pool, and double-height entrance hall. Located within Mivida\'s most prestigious phase.', ar: 'فيلا مستقلة على قطعة ٦٠٠م² مع حديقة خاصة وحمام سباحة وصالة استقبال بارتفاع مزدوج. في أرقى مراحل ميڤيدا.' },
    score: 91,
  },
  {
    id: 3, title: { en: 'Capital Heights Tower Apartment', ar: 'شقة برج كابيتال هايتس' }, area: 'new-capital', areaName: { en: 'New Capital', ar: 'العاصمة الإدارية' },
    project: 'Capital Heights', developer: 'SODIC', type: 'apartments', price: 8200000, currency: 'EGP',
    bedrooms: 3, bathrooms: 2, sqm: 175, finishing: 'Fully Finished', delivery: '2026', installments: 10,
    featured: true,
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1600&q=85','https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1600&q=85'],
    description: { en: 'High-floor residence in the New Capital\'s tallest mixed-use tower. Floor-to-ceiling glass, smart home integration, and concierge services.', ar: 'وحدة سكنية في الأدوار العليا بأطول برج متعدد الاستخدامات في العاصمة. زجاج من الأرض للسقف وأنظمة منزل ذكي وخدمات كونسيرج.' },
    score: 88,
  },
  {
    id: 4, title: { en: 'Allegria Estate Mansion', ar: 'قصر إليجريا' }, area: 'sheikh-zayed', areaName: { en: 'Sheikh Zayed', ar: 'الشيخ زايد' },
    project: 'Allegria', developer: 'SODIC', type: 'villas', price: 65000000, currency: 'EGP',
    bedrooms: 6, bathrooms: 7, sqm: 850, finishing: 'Fully Finished', delivery: 'Ready', installments: 4,
    exclusive: true, featured: true,
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600&q=85','https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=85','https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1600&q=85'],
    description: { en: 'An exceptional estate within Allegria\'s gated community. Private cinema, wine cellar, indoor pool, and meticulously landscaped 1,200m² gardens.', ar: 'قصر استثنائي داخل مجمع إليجريا المغلق. سينما خاصة وقبو نبيذ وحمام سباحة داخلي وحدائق ١٢٠٠م² مصممة بعناية.' },
    score: 97,
  },
  {
    id: 5, title: { en: 'La Vista Sokhna Chalet', ar: 'شاليه لافيستا السخنة' }, area: 'sokhna', areaName: { en: 'Ain Sokhna', ar: 'العين السخنة' },
    project: 'La Vista 7', developer: 'La Vista', type: 'chalets', price: 6800000, currency: 'EGP',
    bedrooms: 3, bathrooms: 2, sqm: 145, finishing: 'Fully Finished', delivery: '2025', installments: 7,
    new: true,
    image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=1600&q=85','https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1600&q=85'],
    description: { en: 'Beachfront chalet with direct sea access. Open-plan living, private terrace, and shared infinity pool overlooking the Red Sea.', ar: 'شاليه على البحر مباشرة. تصميم مفتوح وتراس خاص وحمام سباحة مشترك بإطلالة على البحر الأحمر.' },
    score: 85,
  },
  {
    id: 6, title: { en: 'Eastown Residences', ar: 'إيست تاون ريزيدنسز' }, area: 'new-cairo', areaName: { en: 'New Cairo', ar: 'القاهرة الجديدة' },
    project: 'Eastown', developer: 'SODIC', type: 'apartments', price: 5400000, currency: 'EGP',
    bedrooms: 2, bathrooms: 2, sqm: 130, finishing: 'Semi-Finished', delivery: '2026', installments: 9,
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1600&q=85'],
    description: { en: 'Modern 2BR apartment in Eastown\'s walkable community. Steps from the boulevard, restaurants, and AUC.', ar: 'شقة عصرية بغرفتي نوم في مجتمع إيست تاون. على بعد خطوات من البوليڤارد والمطاعم والجامعة الأمريكية.' },
    score: 82,
  },
  {
    id: 7, title: { en: 'Marassi Villa Bahari', ar: 'فيلا مراسي بحري' }, area: 'north-coast', areaName: { en: 'North Coast', ar: 'الساحل الشمالي' },
    project: 'Marassi', developer: 'Emaar Misr', type: 'villas', price: 45000000, currency: 'EGP',
    bedrooms: 5, bathrooms: 5, sqm: 420, finishing: 'Fully Finished', delivery: '2025', installments: 6,
    featured: true, exclusive: true,
    image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1600&q=85','https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=85'],
    description: { en: 'Greek-island-inspired beachfront villa within Marassi. White-washed architecture, private pool, and walking distance to Marina.', ar: 'فيلا على البحر مستوحاة من الجزر اليونانية في مراسي. عمارة بيضاء وحمام سباحة خاص وعلى مسافة قريبة من المارينا.' },
    score: 95,
  },
  {
    id: 8, title: { en: 'IL Bosco Studio', ar: 'استوديو إل بوسكو' }, area: 'new-capital', areaName: { en: 'New Capital', ar: 'العاصمة الإدارية' },
    project: 'IL Bosco', developer: 'Misr Italia', type: 'apartments', price: 3200000, currency: 'EGP',
    bedrooms: 1, bathrooms: 1, sqm: 75, finishing: 'Fully Finished', delivery: '2026', installments: 10,
    new: true,
    image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1600&q=85'],
    description: { en: 'Vertical-forest inspired studio in IL Bosco. Smart home, integrated greenery, and access to all amenities.', ar: 'استوديو مستوحى من الغابة العمودية. منزل ذكي ومساحات خضراء متكاملة.' },
    score: 80,
  },
  {
    id: 9, title: { en: 'Beverly Hills Twin House', ar: 'تاون هاوس بيفرلي هيلز' }, area: 'sheikh-zayed', areaName: { en: 'Sheikh Zayed', ar: 'الشيخ زايد' },
    project: 'Beverly Hills', developer: 'SODIC', type: 'villas', price: 22500000, currency: 'EGP',
    bedrooms: 4, bathrooms: 4, sqm: 320, finishing: 'Semi-Finished', delivery: 'Ready', installments: 5,
    image: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1600&q=85','https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1600&q=85'],
    description: { en: 'Family twin house with garden and rooftop terrace in Sheikh Zayed\'s iconic Beverly Hills compound.', ar: 'تاون هاوس عائلي بحديقة وتراس علوي في مجمع بيفرلي هيلز الأيقوني.' },
    score: 87,
    lat: 30.07, lng: 30.972, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/SubaruOutbackOnStreetAndDirt.mp4',
  },
  {
    id: 10, title: { en: 'Telal Sokhna Cabana', ar: 'كابانا تلال السخنة' }, area: 'sokhna', areaName: { en: 'Ain Sokhna', ar: 'العين السخنة' },
    project: 'Telal', developer: 'Roya', type: 'chalets', price: 4900000, currency: 'EGP',
    bedrooms: 2, bathrooms: 2, sqm: 110, finishing: 'Fully Finished', delivery: '2025', installments: 8,
    image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1600&q=85'],
    description: { en: 'Stylish 2BR chalet steps from the beach. Sea views from every room.', ar: 'شاليه أنيق بغرفتي نوم على بعد خطوات من الشاطئ. إطلالة بحرية من كل غرفة.' },
    score: 83,
    lat: 29.6015, lng: 32.3275, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/TearsOfSteel.mp4',
  },
  {
    id: 11, title: { en: 'Cairo Festival City Office', ar: 'مكتب كايرو فيستيڤال سيتي' }, area: 'new-cairo', areaName: { en: 'New Cairo', ar: 'القاهرة الجديدة' },
    project: 'CFC Business Park', developer: 'Al-Futtaim', type: 'commercial', price: 12000000, currency: 'EGP',
    bedrooms: 0, bathrooms: 2, sqm: 220, finishing: 'Core & Shell', delivery: 'Ready', installments: 5,
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=85'],
    description: { en: 'Grade-A office space in CFC Business Park with parking, smart building features, and prime visibility.', ar: 'مكتب من الفئة A في كايرو فيستيڤال سيتي مع موقف وخصائص مبنى ذكي.' },
    score: 86,
    lat: 30.03, lng: 31.497, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/VolkswagenGTIReview.mp4',
  },
  {
    id: 12, title: { en: 'Hyde Park Townhouse', ar: 'تاون هاوس هايد بارك' }, area: 'new-cairo', areaName: { en: 'New Cairo', ar: 'القاهرة الجديدة' },
    project: 'Hyde Park', developer: 'Hyde Park Developments', type: 'villas', price: 28000000, currency: 'EGP',
    bedrooms: 4, bathrooms: 4, sqm: 380, finishing: 'Fully Finished', delivery: '2025', installments: 7,
    featured: true,
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
    gallery: ['https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=85','https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=85'],
    description: { en: 'Corner townhouse in Hyde Park\'s premium phase. Private garden, modern interiors, and walkable to all amenities.', ar: 'تاون هاوس ركني في مرحلة هايد بارك المميزة. حديقة خاصة وتصميمات داخلية عصرية.' },
    score: 89,
    lat: 30.0295, lng: 31.4955, video: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/WeAreGoingOnBullrun.mp4',
  },
];

const TESTIMONIALS = [
  { id: 1, name: 'Ahmed Mahmoud', title: 'CEO, Pyramid Investments', text: { en: 'Atlantis didn\'t just sell me a property — they curated an investment portfolio. The depth of market knowledge is unmatched in Egypt.', ar: 'أتلانتس لم تبع لي عقاراً فحسب — بل صممت لي محفظة استثمارية. عمق المعرفة بالسوق لا مثيل له في مصر.' }, image: 'https://i.pravatar.cc/200?img=12' },
  { id: 2, name: 'Sarah Khalil', title: 'Private Investor', text: { en: 'The level of discretion and professionalism is exactly what I needed. They understood my requirements before I finished explaining them.', ar: 'مستوى السرية والاحترافية هو بالضبط ما كنت أحتاجه. فهموا متطلباتي قبل أن أنتهي من شرحها.' }, image: 'https://i.pravatar.cc/200?img=47' },
  { id: 3, name: 'Omar El-Sayed', title: 'Family Office Director', text: { en: 'Three properties across Cairo and the Coast through Atlantis — every transaction smooth, every promise kept. They are simply the best.', ar: 'ثلاثة عقارات في القاهرة والساحل عبر أتلانتس — كل صفقة سلسة وكل وعد محفوظ. ببساطة الأفضل.' }, image: 'https://i.pravatar.cc/200?img=68' },
];

const formatPrice = (price, currency = 'EGP', lang = 'en') => {
  const rates = { EGP: 1, USD: 0.0205, AED: 0.0753 };
  const symbols = { EGP: lang === 'ar' ? 'ج.م' : 'EGP', USD: '$', AED: 'AED' };
  const converted = Math.round(price * rates[currency]);
  return `${symbols[currency]} ${converted.toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-US')}`;
};

// ============== Main App ==============
export default function AtlantisRealEstate() {
  const [page, setPage] = useState('home');
  const [lang, setLang] = useState('en');
  const [currency, setCurrency] = useState('EGP');
  const [theme, setTheme] = useState('dark');
  const [mobileNav, setMobileNav] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [filterArea, setFilterArea] = useState('all');

  // New state
  const [compareList, setCompareList] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [adminAuth, setAdminAuth] = useState(false);

  // Premium feel state
  const [loading, setLoading] = useState(true);
  const [pageTransition, setPageTransition] = useState(false);

  // Loading screen — show on first mount
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2600);
    return () => clearTimeout(timer);
  }, []);

  // Auto-detect language on first load
  useEffect(() => {
    const browserLang = navigator.language?.toLowerCase() || 'en';
    if (browserLang.startsWith('ar')) setLang('ar');
  }, []);

  const t = translations[lang];
  const isRTL = lang === 'ar';
  const isDark = theme === 'dark';

  const toggleFav = (id) => {
    setFavorites(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleCompare = (id) => {
    setCompareList(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 4) return prev; // max 4
      return [...prev, id];
    });
  };

  const addToRecentlyViewed = useCallback((id) => {
    setRecentlyViewed(prev => {
      const filtered = prev.filter(x => x !== id);
      return [id, ...filtered].slice(0, 8);
    });
  }, []);

  const goTo = (p, propertyId = null, area = null) => {
    setPageTransition(true);
    setTimeout(() => {
      if (propertyId) {
        setSelectedProperty(propertyId);
        addToRecentlyViewed(propertyId);
      }
      if (area) setFilterArea(area);
      setPage(p);
      setMobileNav(false);
      window.scrollTo({ top: 0, behavior: 'instant' });
      setTimeout(() => setPageTransition(false), 50);
    }, 350);
  };

  // Theme tokens — backgrounds are semi-transparent so AmbientBackground shows through
  const themeStyles = isDark ? {
    bg: 'transparent',
    bgSecondary: 'rgba(14, 29, 51, 0.6)',  // semi-transparent navy
    bgTertiary: 'rgba(20, 40, 71, 0.7)',
    bgSolid: '#0A1628',  // for elements that MUST be opaque (modals, drawers)
    text: '#F7F4ED',
    textSecondary: '#A8B5C8',
    textMuted: '#6B7A91',
    border: 'rgba(201, 169, 97, 0.15)',
    borderLight: 'rgba(247, 244, 237, 0.08)',
    glass: 'rgba(20, 40, 71, 0.5)',
  } : {
    bg: 'transparent',
    bgSecondary: 'rgba(255, 255, 255, 0.65)',
    bgTertiary: 'rgba(237, 231, 217, 0.7)',
    bgSolid: '#F7F4ED',
    text: '#0A1628',
    textSecondary: '#3D4A5E',
    textMuted: '#6B7A91',
    border: 'rgba(10, 22, 40, 0.12)',
    borderLight: 'rgba(10, 22, 40, 0.06)',
    glass: 'rgba(255, 255, 255, 0.6)',
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ background: 'transparent', color: themeStyles.text, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif", transition: 'color 0.4s ease', position: 'relative' }}>
      <GlobalStyles isDark={isDark} isRTL={isRTL} />
      <AmbientBackground isDark={isDark} />

      {/* Loading screen with animated logo */}
      {loading && <LoadingScreen isDark={isDark} />}

      {/* Mouse-follow cursor glow (desktop only) */}
      <CursorGlow />

      {/* Page transition overlay */}
      <PageTransitionVeil active={pageTransition} isDark={isDark} />

      <Header
        page={page} goTo={goTo} t={t} lang={lang} setLang={setLang}
        currency={currency} setCurrency={setCurrency}
        theme={theme} setTheme={setTheme}
        mobileNav={mobileNav} setMobileNav={setMobileNav}
        themeStyles={themeStyles} isRTL={isRTL} favorites={favorites}
        compareCount={compareList.length} setCompareOpen={setCompareOpen}
      />

      <main style={{ position: 'relative', zIndex: 1 }}>
        {page === 'home' && <HomePage t={t} lang={lang} goTo={goTo} themeStyles={themeStyles} currency={currency} favorites={favorites} toggleFav={toggleFav} isDark={isDark} compareList={compareList} toggleCompare={toggleCompare} recentlyViewed={recentlyViewed} />}
        {page === 'listings' && <ListingsPage t={t} lang={lang} goTo={goTo} themeStyles={themeStyles} currency={currency} favorites={favorites} toggleFav={toggleFav} isDark={isDark} initialArea={filterArea} setFilterArea={setFilterArea} compareList={compareList} toggleCompare={toggleCompare} />}
        {page === 'detail' && selectedProperty && <DetailPage propertyId={selectedProperty} t={t} lang={lang} goTo={goTo} themeStyles={themeStyles} currency={currency} favorites={favorites} toggleFav={toggleFav} isDark={isDark} compareList={compareList} toggleCompare={toggleCompare} />}
        {page === 'about' && <AboutPage t={t} lang={lang} themeStyles={themeStyles} isDark={isDark} />}
        {page === 'developers' && <DevelopersPage t={t} lang={lang} goTo={goTo} themeStyles={themeStyles} isDark={isDark} />}
        {page === 'areas' && <AreasPage t={t} lang={lang} goTo={goTo} themeStyles={themeStyles} isDark={isDark} />}
        {page === 'contact' && <ContactPage t={t} lang={lang} themeStyles={themeStyles} isDark={isDark} />}
        {page === 'admin' && <AdminPage t={t} lang={lang} themeStyles={themeStyles} isDark={isDark} authed={adminAuth} setAuthed={setAdminAuth} goTo={goTo} />}
      </main>

      <Footer t={t} lang={lang} goTo={goTo} themeStyles={themeStyles} isDark={isDark} />

      <ChatWidget open={chatOpen} setOpen={setChatOpen} lang={lang} isDark={isDark} />

      {/* Compare Drawer */}
      <CompareDrawer
        open={compareOpen} setOpen={setCompareOpen}
        compareList={compareList} toggleCompare={toggleCompare}
        lang={lang} t={t} themeStyles={themeStyles} currency={currency}
        goTo={goTo} isRTL={isRTL} isDark={isDark}
      />

      {/* Compare floating button */}
      {compareList.length > 0 && !compareOpen && (
        <button onClick={() => setCompareOpen(true)} style={{
          position: 'fixed', bottom: '96px', [isRTL ? 'left' : 'right']: '24px',
          background: 'linear-gradient(135deg, #C9A961, #D4B876)', color: '#0A1628',
          border: 'none', padding: '14px 20px', cursor: 'pointer',
          boxShadow: '0 8px 32px rgba(201, 169, 97, 0.4)', zIndex: 99,
          display: 'flex', alignItems: 'center', gap: '10px',
          fontWeight: 700, fontSize: '13px', letterSpacing: '0.05em',
          fontFamily: 'inherit',
        }}>
          <GitCompare size={16} />
          {lang === 'ar' ? 'قارن' : 'COMPARE'} ({compareList.length})
        </button>
      )}

      {/* WhatsApp floating */}
      <a href="https://wa.me/201000000000" target="_blank" rel="noopener noreferrer"
        style={{
          position: 'fixed', bottom: '24px', [isRTL ? 'left' : 'right']: chatOpen ? '380px' : '24px',
          width: '56px', height: '56px', borderRadius: '50%', background: '#25D366',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 32px rgba(37, 211, 102, 0.4)', zIndex: 100,
          transition: 'right 0.3s ease, left 0.3s ease, transform 0.2s ease',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        <MessageCircle size={26} color="white" strokeWidth={2.2} />
      </a>
    </div>
  );
}

// ============== Global Styles ==============
function GlobalStyles({ isDark, isRTL }) {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Noto+Kufi+Arabic:wght@300;400;500;600;700;800&display=swap');

      * { box-sizing: border-box; margin: 0; padding: 0; }
      html { scroll-behavior: smooth; }
      body { font-family: 'Plus Jakarta Sans', -apple-system, sans-serif; -webkit-font-smoothing: antialiased; }

      ${isRTL ? `body, .arabic-text { font-family: 'Noto Kufi Arabic', 'Plus Jakarta Sans', sans-serif !important; }` : ''}

      .serif { font-family: 'Cormorant Garamond', 'Noto Kufi Arabic', serif; font-weight: 500; letter-spacing: -0.01em; }
      ${isRTL ? `.serif { font-family: 'Noto Kufi Arabic', 'Cormorant Garamond', serif; }` : ''}

      ::-webkit-scrollbar { width: 10px; height: 10px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: rgba(201, 169, 97, 0.3); border-radius: 10px; }
      ::-webkit-scrollbar-thumb:hover { background: rgba(201, 169, 97, 0.5); }

      .gold-text { color: #C9A961; }
      .gold-bg { background: linear-gradient(135deg, #C9A961 0%, #D4B876 100%); }
      .gold-border { border-color: #C9A961; }

      .gold-line {
        height: 1px; background: linear-gradient(90deg, transparent, #C9A961, transparent);
      }

      .glass {
        background: ${isDark ? 'rgba(20, 40, 71, 0.5)' : 'rgba(255, 255, 255, 0.7)'};
        backdrop-filter: blur(20px) saturate(180%);
        -webkit-backdrop-filter: blur(20px) saturate(180%);
        border: 1px solid ${isDark ? 'rgba(247, 244, 237, 0.08)' : 'rgba(10, 22, 40, 0.08)'};
      }

      @keyframes fadeUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideRight {
        from { opacity: 0; transform: translateX(-30px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes shimmer {
        0% { background-position: -200% center; }
        100% { background-position: 200% center; }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
      @keyframes floatY {
        0%, 100% { opacity: 0.85; }
        50% { opacity: 1; }
      }
      @keyframes pulse-gold {
        0%, 100% { box-shadow: 0 0 0 0 rgba(201, 169, 97, 0.4); }
        50% { box-shadow: 0 0 0 12px rgba(201, 169, 97, 0); }
      }
      @keyframes drift {
        0% { transform: translate(0, 0) rotate(0deg); }
        25% { transform: translate(30px, -20px) rotate(90deg); }
        50% { transform: translate(-20px, -40px) rotate(180deg); }
        75% { transform: translate(-30px, 20px) rotate(270deg); }
        100% { transform: translate(0, 0) rotate(360deg); }
      }
      @keyframes loadingBar {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(350%); }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes marquee {
        0% { transform: translateX(0); }
        100% { transform: translateX(-25%); }
      }
      @keyframes gentleBob {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
      @keyframes glowPulse {
        0%, 100% { opacity: 0.4; transform: scale(1); }
        50% { opacity: 0.8; transform: scale(1.1); }
      }
      @keyframes scanline {
        0% { transform: translateY(-100%); }
        100% { transform: translateY(100%); }
      }
      @keyframes tickerScroll {
        0% { transform: translateY(0); }
        100% { transform: translateY(-50%); }
      }

      .reveal { animation: fadeUp 0.8s ease both; }
      .reveal-delay-1 { animation: fadeUp 0.8s ease 0.1s both; }
      .reveal-delay-2 { animation: fadeUp 0.8s ease 0.2s both; }
      .reveal-delay-3 { animation: fadeUp 0.8s ease 0.3s both; }
      .reveal-delay-4 { animation: fadeUp 0.8s ease 0.4s both; }

      .property-card {
        transition: all 0.5s cubic-bezier(0.19, 1, 0.22, 1);
        backdrop-filter: blur(20px) saturate(160%);
        -webkit-backdrop-filter: blur(20px) saturate(160%);
      }
      .luxe-card:hover .luxe-sweep { left: 140%; }
      .luxe-card:hover { box-shadow: 0 30px 60px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(201, 169, 97, 0.35); }
      .property-card:hover { transform: translateY(-8px); }
      .property-card:hover .card-image { transform: scale(1.08); }
      .card-image { transition: transform 0.8s cubic-bezier(0.19, 1, 0.22, 1); }

      .btn-primary {
        background: linear-gradient(135deg, #C9A961 0%, #D4B876 100%);
        color: #0A1628;
        border: none;
        padding: 14px 32px;
        font-weight: 600;
        letter-spacing: 0.02em;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.19, 1, 0.22, 1);
        position: relative;
        overflow: hidden;
        font-family: inherit;
        font-size: 14px;
      }
      .btn-primary::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, #D4B876 0%, #E5C988 100%);
        opacity: 0;
        transition: opacity 0.3s;
      }
      .btn-primary:hover::before { opacity: 1; }
      .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(201, 169, 97, 0.35); }
      .btn-primary > * { position: relative; z-index: 1; }

      .btn-ghost {
        background: transparent;
        color: ${isDark ? '#F7F4ED' : '#0A1628'};
        border: 1px solid ${isDark ? 'rgba(247, 244, 237, 0.2)' : 'rgba(10, 22, 40, 0.2)'};
        padding: 14px 32px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        font-family: inherit;
        font-size: 14px;
      }
      .btn-ghost:hover { border-color: #C9A961; color: #C9A961; }

      .input-field {
        background: ${isDark ? 'rgba(20, 40, 71, 0.4)' : 'rgba(255, 255, 255, 0.8)'};
        border: 1px solid ${isDark ? 'rgba(247, 244, 237, 0.12)' : 'rgba(10, 22, 40, 0.12)'};
        color: ${isDark ? '#F7F4ED' : '#0A1628'};
        padding: 14px 18px;
        font-family: inherit;
        font-size: 14px;
        outline: none;
        transition: border-color 0.3s ease;
        width: 100%;
      }
      .input-field:focus { border-color: #C9A961; }
      .input-field::placeholder { color: ${isDark ? '#6B7A91' : '#8896A8'}; }

      .badge-gold {
        background: linear-gradient(135deg, #C9A961, #D4B876);
        color: #0A1628;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.1em;
        padding: 5px 10px;
        text-transform: uppercase;
      }
      .badge-dark {
        background: rgba(10, 22, 40, 0.85);
        backdrop-filter: blur(10px);
        color: #F7F4ED;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.1em;
        padding: 5px 10px;
        text-transform: uppercase;
        border: 1px solid rgba(201, 169, 97, 0.4);
      }

      .section-eyebrow {
        font-size: 11px;
        letter-spacing: 0.3em;
        text-transform: uppercase;
        color: #C9A961;
        font-weight: 600;
        margin-bottom: 16px;
        display: inline-block;
      }

      .text-shimmer {
        background: linear-gradient(90deg, #C9A961, #E5C988, #C9A961);
        background-size: 200% auto;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        animation: shimmer 4s linear infinite;
      }

      @media (max-width: 768px) {
        .desktop-only { display: none !important; }
      }
      @media (min-width: 769px) {
        .mobile-only { display: none !important; }
      }
    `}</style>
  );
}

// ============== Header ==============
function Header({ page, goTo, t, lang, setLang, currency, setCurrency, theme, setTheme, mobileNav, setMobileNav, themeStyles, isRTL, favorites, compareCount, setCompareOpen }) {
  const [scrolled, setScrolled] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = [
    { key: 'home', label: t.nav.home },
    { key: 'listings', label: t.nav.listings },
    { key: 'developers', label: t.nav.developers },
    { key: 'areas', label: t.nav.areas },
    { key: 'about', label: t.nav.about },
    { key: 'contact', label: t.nav.contact },
  ];

  return (
    <>
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        background: scrolled ? themeStyles.glass : 'transparent',
        backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
        borderBottom: scrolled ? `1px solid ${themeStyles.borderLight}` : '1px solid transparent',
        transition: 'all 0.4s ease',
      }}>
        <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 32px', height: '76px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Logo */}
          <button onClick={() => goTo('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px', padding: 0 }}>
            <AtlantisLogo size={40} color={themeStyles.text} />
            <div style={{ textAlign: isRTL ? 'right' : 'left', lineHeight: 1 }}>
              <div style={{ fontSize: '17px', fontWeight: 400, letterSpacing: '0.2em', color: themeStyles.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>ATLANTIS</div>
              <div style={{ fontSize: '8px', letterSpacing: '0.32em', color: '#C9A961', marginTop: '5px', fontWeight: 500 }}>REAL ESTATE</div>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="desktop-only" style={{ display: 'flex', gap: '36px', alignItems: 'center' }}>
            {navItems.map(item => (
              <button key={item.key} onClick={() => goTo(item.key)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: page === item.key ? '#C9A961' : themeStyles.text,
                  fontSize: '13px', fontWeight: 500, letterSpacing: '0.05em',
                  fontFamily: 'inherit', position: 'relative', padding: '8px 0',
                  transition: 'color 0.3s',
                }}
                onMouseEnter={e => { if (page !== item.key) e.target.style.color = '#C9A961'; }}
                onMouseLeave={e => { if (page !== item.key) e.target.style.color = themeStyles.text; }}
              >
                {item.label}
                {page === item.key && (
                  <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: '#C9A961' }} />
                )}
              </button>
            ))}
          </nav>

          {/* Right utilities */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Currency */}
            <div className="desktop-only" style={{ position: 'relative' }}>
              <button onClick={() => setCurrencyOpen(!currencyOpen)}
                style={{ background: 'none', border: `1px solid ${themeStyles.borderLight}`, color: themeStyles.text, padding: '8px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit' }}
              >
                {currency} <ChevronDown size={12} />
              </button>
              {currencyOpen && (
                <div style={{ position: 'absolute', top: '100%', [isRTL ? 'left' : 'right']: 0, marginTop: '8px', background: themeStyles.bgSecondary, border: `1px solid ${themeStyles.border}`, minWidth: '100px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
                  {['EGP', 'USD', 'AED'].map(c => (
                    <button key={c} onClick={() => { setCurrency(c); setCurrencyOpen(false); }}
                      style={{ display: 'block', width: '100%', padding: '10px 14px', background: currency === c ? 'rgba(201, 169, 97, 0.1)' : 'transparent', border: 'none', color: currency === c ? '#C9A961' : themeStyles.text, fontSize: '12px', fontWeight: 600, textAlign: isRTL ? 'right' : 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Lang */}
            <button onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
              style={{ background: 'none', border: `1px solid ${themeStyles.borderLight}`, color: themeStyles.text, padding: '8px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit' }}>
              <Globe size={14} /> {lang === 'en' ? 'AR' : 'EN'}
            </button>

            {/* Theme */}
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="desktop-only"
              style={{ background: 'none', border: `1px solid ${themeStyles.borderLight}`, color: themeStyles.text, padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
            </button>

            {/* Favorites */}
            <button onClick={() => goTo('listings')} className="desktop-only"
              style={{ background: 'none', border: `1px solid ${themeStyles.borderLight}`, color: themeStyles.text, padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              <Heart size={14} fill={favorites.length ? '#C9A961' : 'none'} color={favorites.length ? '#C9A961' : themeStyles.text} />
              {favorites.length > 0 && (
                <span style={{ position: 'absolute', top: '-6px', [isRTL ? 'left' : 'right']: '-6px', background: '#C9A961', color: '#0A1628', fontSize: '9px', fontWeight: 700, width: '16px', height: '16px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{favorites.length}</span>
              )}
            </button>

            {/* Mobile menu */}
            <button onClick={() => setMobileNav(true)} className="mobile-only"
              style={{ background: 'none', border: 'none', color: themeStyles.text, cursor: 'pointer', padding: '4px' }}>
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileNav && (
        <div className="mobile-only" style={{ position: 'fixed', inset: 0, zIndex: 100, background: themeStyles.bgSolid, display: 'flex', flexDirection: 'column', animation: 'fadeIn 0.3s ease' }}>
          <div style={{ padding: '24px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${themeStyles.borderLight}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <AtlantisLogo size={36} color={themeStyles.text} />
              <div style={{ fontSize: '15px', fontWeight: 400, letterSpacing: '0.2em', color: themeStyles.text }}>ATLANTIS</div>
            </div>
            <button onClick={() => setMobileNav(false)} style={{ background: 'none', border: 'none', color: themeStyles.text, cursor: 'pointer' }}>
              <X size={26} />
            </button>
          </div>
          <nav style={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
            {navItems.map((item, i) => (
              <button key={item.key} onClick={() => goTo(item.key)} className="serif"
                style={{ background: 'none', border: 'none', color: page === item.key ? '#C9A961' : themeStyles.text, fontSize: '32px', textAlign: isRTL ? 'right' : 'left', padding: '16px 0', cursor: 'pointer', fontFamily: 'inherit', borderBottom: `1px solid ${themeStyles.borderLight}`, animation: `slideRight 0.4s ${i * 0.05}s both` }}>
                {item.label}
              </button>
            ))}
          </nav>
          <div style={{ padding: '24px 32px', display: 'flex', gap: '12px', borderTop: `1px solid ${themeStyles.borderLight}` }}>
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} style={{ flex: 1, padding: '14px', background: 'transparent', border: `1px solid ${themeStyles.borderLight}`, color: themeStyles.text, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: 'inherit' }}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />} {theme === 'dark' ? 'Light' : 'Dark'}
            </button>
            <select value={currency} onChange={e => setCurrency(e.target.value)} style={{ padding: '14px', background: 'transparent', border: `1px solid ${themeStyles.borderLight}`, color: themeStyles.text, fontFamily: 'inherit', cursor: 'pointer' }}>
              <option value="EGP" style={{ background: themeStyles.bgSolid }}>EGP</option>
              <option value="USD" style={{ background: themeStyles.bgSolid }}>USD</option>
              <option value="AED" style={{ background: themeStyles.bgSolid }}>AED</option>
            </select>
          </div>
        </div>
      )}
    </>
  );
}

// ============== Home Page ==============
function HomePage({ t, lang, goTo, themeStyles, currency, favorites, toggleFav, isDark, compareList, toggleCompare, recentlyViewed }) {
  const featured = PROPERTIES.filter(p => p.featured);
  const categories = [
    { id: 'apartments', icon: Building2, label: t.categories.apartments, count: PROPERTIES.filter(p => p.type === 'apartments').length },
    { id: 'villas', icon: HomeIcon, label: t.categories.villas, count: PROPERTIES.filter(p => p.type === 'villas').length },
    { id: 'chalets', icon: Sparkles, label: t.categories.chalets, count: PROPERTIES.filter(p => p.type === 'chalets').length },
    { id: 'commercial', icon: Briefcase, label: t.categories.commercial, count: PROPERTIES.filter(p => p.type === 'commercial').length },
  ];

  return (
    <>
      <Hero t={t} lang={lang} goTo={goTo} themeStyles={themeStyles} isDark={isDark} />

      {/* Luxury marquee strip */}
      <Marquee
        lang={lang}
        items={lang === 'ar'
          ? ['الفخامة', 'الاستثمار', 'الرؤية', 'التميز', 'الهندسة المعمارية', 'الإرث']
          : ['LUXURY', 'INVESTMENT', 'VISION', 'EXCELLENCE', 'ARCHITECTURE', 'LEGACY']}
      />

      {/* Parallax skyline divider */}
      <ParallaxSkylines isDark={isDark} />

      {/* Video Reel Section */}
      <VideoReelSection t={t} lang={lang} themeStyles={themeStyles} isDark={isDark} />

      {/* Cinematic storytelling section */}
      <CinematicSection
        image="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=2400&q=85"
        eyebrow={lang === 'ar' ? 'الرؤية' : 'OUR VISION'}
        title={lang === 'ar' ? 'حيث الهندسة تلتقي بالأسطورة' : 'Where architecture meets legend'}
        subtitle={lang === 'ar' ? 'نختار العقارات التي تحدد المشهد العمراني لمصر — مشاريع لا تُبنى فقط، بل تُروى عبر الأجيال.' : 'We curate properties that define Egypt\'s skyline — developments that aren\'t merely built, but legacies retold through generations.'}
        lang={lang}
        themeStyles={themeStyles}
      />

      {/* Categories */}
      <section style={{ padding: '120px 32px 80px', maxWidth: '1440px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '64px' }} className="reveal">
          <div className="section-eyebrow">— 01 —</div>
          <h2 className="serif" style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, lineHeight: 1.1 }}>{t.sections.categories}</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
          {categories.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <TiltCard key={cat.id} maxTilt={10} style={{ animation: `fadeUp 0.6s ${i * 0.1}s both` }}>
                <button onClick={() => goTo('listings')}
                  style={{
                    width: '100%',
                    background: themeStyles.bgSecondary,
                    backdropFilter: 'blur(20px) saturate(160%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                    border: `1px solid ${themeStyles.border}`,
                    padding: '40px 28px', cursor: 'pointer',
                    textAlign: lang === 'ar' ? 'right' : 'left', fontFamily: 'inherit', color: themeStyles.text,
                    position: 'relative', overflow: 'hidden',
                    transition: 'border-color 0.4s ease, box-shadow 0.4s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#C9A961'; e.currentTarget.style.boxShadow = '0 20px 50px rgba(201, 169, 97, 0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = themeStyles.border; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {/* Corner glow */}
                  <div style={{ position: 'absolute', top: '-50px', [lang === 'ar' ? 'left' : 'right']: '-50px', width: '150px', height: '150px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201, 169, 97, 0.2), transparent 70%)', filter: 'blur(20px)' }} />
                  <Icon size={28} color="#C9A961" strokeWidth={1.5} />
                  <div className="serif" style={{ fontSize: '24px', marginTop: '20px', fontWeight: 500, position: 'relative' }}>{cat.label}</div>
                  <div style={{ fontSize: '12px', color: themeStyles.textMuted, marginTop: '6px', letterSpacing: '0.1em', position: 'relative' }}>{cat.count} {lang === 'ar' ? 'عقار' : 'PROPERTIES'}</div>
                  <ArrowRight size={16} style={{ position: 'absolute', bottom: '24px', [lang === 'ar' ? 'left' : 'right']: '24px', color: '#C9A961', transform: lang === 'ar' ? 'scaleX(-1)' : 'none' }} />
                </button>
              </TiltCard>
            );
          })}
        </div>
      </section>

      {/* Featured Projects */}
      <section style={{ padding: '80px 32px', maxWidth: '1440px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '48px', flexWrap: 'wrap', gap: '20px' }}>
          <div className="reveal">
            <div className="section-eyebrow">— 02 —</div>
            <h2 className="serif" style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, lineHeight: 1.1, marginBottom: '12px' }}>{t.sections.featured}</h2>
            <p style={{ color: themeStyles.textSecondary, fontSize: '15px', maxWidth: '500px' }}>{t.sections.featuredSub}</p>
          </div>
          <button onClick={() => goTo('listings')} className="btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {t.common.viewAll} <ArrowRight size={14} style={{ transform: lang === 'ar' ? 'scaleX(-1)' : 'none' }} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '28px' }}>
          {featured.slice(0, 6).map((p, i) => (
            <PropertyCard key={p.id} property={p} lang={lang} t={t} goTo={goTo} currency={currency} isFavorite={favorites.includes(p.id)} toggleFav={toggleFav} themeStyles={themeStyles} delay={i * 0.1} compareList={compareList} toggleCompare={toggleCompare} />
          ))}
        </div>
      </section>

      {/* Recently Viewed */}
      {recentlyViewed && recentlyViewed.length > 0 && (
        <section style={{ padding: '60px 32px 40px', maxWidth: '1440px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
            <Clock size={20} color="#C9A961" />
            <div>
              <div className="serif" style={{ fontSize: '24px', fontWeight: 500 }}>{t.common.recentlyViewed}</div>
              <div style={{ fontSize: '12px', color: themeStyles.textMuted, letterSpacing: '0.1em', marginTop: '2px' }}>{t.common.recentlyViewedSub}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '12px', scrollSnapType: 'x mandatory' }}>
            {recentlyViewed.map(id => {
              const p = PROPERTIES.find(pp => pp.id === id);
              if (!p) return null;
              return (
                <button key={id} onClick={() => goTo('detail', id)}
                  style={{
                    flex: '0 0 280px', scrollSnapAlign: 'start',
                    background: themeStyles.bgSecondary, border: `1px solid ${themeStyles.border}`,
                    cursor: 'pointer', padding: 0, textAlign: lang === 'ar' ? 'right' : 'left',
                    fontFamily: 'inherit', color: themeStyles.text, overflow: 'hidden',
                  }}>
                  <div style={{ aspectRatio: '16/10', overflow: 'hidden' }}>
                    <img src={p.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <div style={{ padding: '16px' }}>
                    <div style={{ fontSize: '10px', letterSpacing: '0.15em', color: '#C9A961', fontWeight: 600, marginBottom: '4px' }}>{p.areaName[lang]}</div>
                    <div className="serif" style={{ fontSize: '16px', fontWeight: 500, marginBottom: '6px', lineHeight: 1.3 }}>{p.title[lang]}</div>
                    <div style={{ fontSize: '14px', color: '#C9A961', fontWeight: 600 }}>{formatPrice(p.price, currency, lang)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Areas */}
      <section style={{ padding: '120px 32px', background: themeStyles.bgSecondary, marginTop: '40px' }}>
        <div style={{ maxWidth: '1440px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }} className="reveal">
            <div className="section-eyebrow">— 03 —</div>
            <h2 className="serif" style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, lineHeight: 1.1, marginBottom: '16px' }}>{t.sections.areas}</h2>
            <p style={{ color: themeStyles.textSecondary, fontSize: '15px', maxWidth: '600px', margin: '0 auto' }}>{t.sections.areasSub}</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '16px', gridAutoRows: '280px' }}>
            {AREAS.map((area, i) => {
              const span = i === 0 ? { col: 'span 7', row: 'span 2' } : i === 1 ? { col: 'span 5', row: 'span 1' } : i === 2 ? { col: 'span 5', row: 'span 1' } : i === 3 ? { col: 'span 6', row: 'span 1' } : { col: 'span 6', row: 'span 1' };
              return (
                <button key={area.id} onClick={() => goTo('listings', null, area.id)}
                  style={{
                    gridColumn: span.col, gridRow: span.row,
                    position: 'relative', overflow: 'hidden', cursor: 'pointer', border: 'none', padding: 0,
                    minHeight: '280px',
                    animation: `fadeUp 0.7s ${i * 0.1}s both`,
                  }}
                  className="area-card"
                  onMouseEnter={e => { const img = e.currentTarget.querySelector('img'); if (img) img.style.transform = 'scale(1.1)'; }}
                  onMouseLeave={e => { const img = e.currentTarget.querySelector('img'); if (img) img.style.transform = 'scale(1)'; }}
                >
                  <img src={area.image} alt={area.name[lang]} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.8s cubic-bezier(0.19, 1, 0.22, 1)' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10, 22, 40, 0.1) 0%, rgba(10, 22, 40, 0.85) 100%)' }} />
                  <div style={{ position: 'absolute', inset: 0, padding: '32px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', textAlign: lang === 'ar' ? 'right' : 'left', color: '#F7F4ED' }}>
                    <div style={{ fontSize: '11px', letterSpacing: '0.3em', color: '#C9A961', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>{area.tagline[lang]}</div>
                    <h3 className="serif" style={{ fontSize: '32px', fontWeight: 500, marginBottom: '12px' }}>{area.name[lang]}</h3>
                    <div style={{ display: 'flex', gap: '24px', fontSize: '13px', flexWrap: 'wrap' }}>
                      <div><span style={{ color: '#C9A961', fontWeight: 600 }}>{area.projects}</span> {lang === 'ar' ? 'مشروع' : 'projects'}</div>
                      <div>ROI <span style={{ color: '#C9A961', fontWeight: 600 }}>{area.roi}</span></div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Atlantis */}
      <section style={{ padding: '120px 32px', maxWidth: '1440px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '80px', alignItems: 'center' }}>
          <div className="reveal">
            <div className="section-eyebrow">— 04 —</div>
            <h2 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', fontWeight: 500, lineHeight: 1.05, marginBottom: '24px' }}>{t.sections.whyUs}</h2>
            <p style={{ color: themeStyles.textSecondary, fontSize: '17px', lineHeight: 1.7, marginBottom: '40px' }}>{t.sections.whyUsSub}</p>
            <button onClick={() => goTo('about')} className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
              {t.common.learnMore} <ArrowRight size={14} style={{ transform: lang === 'ar' ? 'scaleX(-1)' : 'none' }} />
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {[t.why.one, t.why.two, t.why.three, t.why.four].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', paddingBottom: '32px', borderBottom: `1px solid ${themeStyles.borderLight}`, animation: `fadeUp 0.6s ${i * 0.15}s both` }}>
                <div className="serif gold-text" style={{ fontSize: '28px', minWidth: '50px', fontWeight: 400 }}>0{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <h3 className="serif" style={{ fontSize: '24px', fontWeight: 500, marginBottom: '10px' }}>{item.t}</h3>
                  <p style={{ color: themeStyles.textSecondary, fontSize: '15px', lineHeight: 1.7 }}>{item.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <StatsSection lang={lang} themeStyles={themeStyles} t={t} />

      {/* Testimonials */}
      <TestimonialsSection lang={lang} themeStyles={themeStyles} t={t} isDark={isDark} />

      {/* Before/After Transformation Showcase */}
      <section style={{ padding: '120px 32px', maxWidth: '1440px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '60px', alignItems: 'center' }}>
          <div className="reveal">
            <div className="section-eyebrow">— {lang === 'ar' ? 'التحول' : 'TRANSFORMATION'} —</div>
            <h2 className="serif" style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, lineHeight: 1.1, marginBottom: '24px' }}>
              {lang === 'ar' ? 'من الرؤية ' : 'From vision '}
              <em style={{ fontStyle: 'italic' }} className="text-shimmer">{lang === 'ar' ? 'إلى الواقع' : 'to reality'}</em>
            </h2>
            <p style={{ color: themeStyles.textSecondary, fontSize: '16px', lineHeight: 1.7, marginBottom: '32px' }}>
              {lang === 'ar' ? 'اسحب الشريط لمشاهدة تحول أحد مشاريعنا — من الخطط الأولى إلى التسليم النهائي. كل عقار يحمل قصة تطور تستحق أن تُروى.' : 'Drag the slider to witness one of our developments evolve — from blueprint to handover. Every property carries an evolution worth showing.'}
            </p>
            <div style={{ display: 'flex', gap: '40px', marginTop: '40px' }}>
              <div>
                <div className="serif text-shimmer" style={{ fontSize: '40px', fontWeight: 500, lineHeight: 1 }}>24</div>
                <div style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, marginTop: '6px', fontWeight: 600 }}>{lang === 'ar' ? 'شهر' : 'MONTHS'}</div>
              </div>
              <div>
                <div className="serif text-shimmer" style={{ fontSize: '40px', fontWeight: 500, lineHeight: 1 }}>+18%</div>
                <div style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, marginTop: '6px', fontWeight: 600 }}>{lang === 'ar' ? 'ارتفاع القيمة' : 'VALUE GAIN'}</div>
              </div>
              <div>
                <div className="serif text-shimmer" style={{ fontSize: '40px', fontWeight: 500, lineHeight: 1 }}>100%</div>
                <div style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, marginTop: '6px', fontWeight: 600 }}>{lang === 'ar' ? 'تسليم في الموعد' : 'ON SCHEDULE'}</div>
              </div>
            </div>
          </div>
          <BeforeAfterSlider
            before="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1600&q=85"
            after="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1600&q=85"
            beforeLabel={lang === 'ar' ? 'البداية' : 'BLUEPRINT'}
            afterLabel={lang === 'ar' ? 'النهاية' : 'DELIVERED'}
            lang={lang}
          />
        </div>
      </section>

      {/* Second cinematic story section */}
      <CinematicSection
        image="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=2400&q=85"
        eyebrow={lang === 'ar' ? 'القيمة' : 'THE VALUE'}
        title={lang === 'ar' ? 'استثمار يتحدث بلغة الإرث' : 'Investments that speak the language of legacy'}
        subtitle={lang === 'ar' ? 'ليست مجرد جدران وأسقف — بل قرارات استراتيجية تشكل مستقبل عائلتك ومحفظتك الاستثمارية.' : 'More than walls and ceilings — these are strategic decisions shaping your family\'s future and your investment portfolio.'}
        lang={lang}
        themeStyles={themeStyles}
        reverse
      />

      {/* Partners */}
      <section style={{ padding: '80px 32px', borderTop: `1px solid ${themeStyles.borderLight}`, borderBottom: `1px solid ${themeStyles.borderLight}` }}>
        <div style={{ maxWidth: '1440px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-eyebrow">— 06 —</div>
            <h3 className="serif" style={{ fontSize: '28px', fontWeight: 500 }}>{t.sections.partners}</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '40px', alignItems: 'center' }}>
            {DEVELOPERS.map((d, i) => (
              <div key={d.id} style={{ textAlign: 'center', opacity: 0.5, transition: 'opacity 0.3s', cursor: 'pointer', animation: `fadeIn 0.6s ${i * 0.05}s both` }}
                onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}
              >
                <div className="serif" style={{ fontSize: '20px', fontWeight: 500, color: themeStyles.text, letterSpacing: '0.05em' }}>{d.name}</div>
                <div style={{ fontSize: '10px', letterSpacing: '0.2em', color: themeStyles.textMuted, marginTop: '4px' }}>EST. {d.founded}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ padding: '120px 32px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 30% 50%, rgba(201, 169, 97, 0.08) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(201, 169, 97, 0.06) 0%, transparent 50%)' }} />
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center', position: 'relative' }}>
          <div className="gold-line" style={{ width: '80px', margin: '0 auto 32px' }} />
          <h2 className="serif" style={{ fontSize: 'clamp(40px, 6vw, 72px)', fontWeight: 400, lineHeight: 1.05, marginBottom: '24px' }}>
            {lang === 'ar' ? 'ابدأ مع ' : 'Begin with '}<em className="text-shimmer" style={{ fontStyle: 'italic' }}>{lang === 'ar' ? 'أتلانتس' : 'Atlantis'}</em>
          </h2>
          <p style={{ color: themeStyles.textSecondary, fontSize: '17px', lineHeight: 1.7, maxWidth: '600px', margin: '0 auto 40px' }}>
            {lang === 'ar' ? 'احجز استشارتك الخاصة مع أحد كبار مستشارينا اليوم.' : 'Schedule a private consultation with one of our senior advisors today.'}
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => goTo('contact')} className="btn-primary">{lang === 'ar' ? 'احجز استشارة' : 'Book Consultation'}</button>
            <button onClick={() => goTo('listings')} className="btn-ghost">{lang === 'ar' ? 'تصفح العقارات' : 'Browse Properties'}</button>
          </div>
        </div>
      </section>
    </>
  );
}

// ============== Hero ==============
function Hero({ t, lang, goTo, themeStyles, isDark }) {
  const [searchVal, setSearchVal] = useState('');
  const heroRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const offset = window.scrollY * 0.5;
        heroRef.current.style.transform = `translateY(${offset}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
      {/* Cinematic video background — luxury architecture drone footage */}
      <div ref={heroRef} style={{ position: 'absolute', inset: '-10% 0', zIndex: 0 }}>
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          poster="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=2400&q=90"
          style={{ width: '100%', height: '110%', objectFit: 'cover', filter: 'brightness(0.45) saturate(1.15) contrast(1.05)' }}
        >
          {/* Mixkit free luxury skyline drone footage */}
          <source src="https://assets.mixkit.co/videos/4067/4067-720.mp4" type="video/mp4" />
          {/* Fallback */}
          <source src="https://assets.mixkit.co/videos/4090/4090-720.mp4" type="video/mp4" />
        </video>

        {/* Cinematic letterbox bars (fade in) */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '8vh', background: 'linear-gradient(180deg, #050B17, transparent)', opacity: 0.9, zIndex: 2 }} />

        {/* Film grain overlay */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 2,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'200\' height=\'200\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' /%3E%3C/filter%3E%3Crect width=\'200\' height=\'200\' filter=\'url(%23n)\' opacity=\'0.4\'/%3E%3C/svg%3E")',
          opacity: 0.08, mixBlendMode: 'overlay', pointerEvents: 'none',
        }} />

        {/* Scanline effect (subtle, animated) */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 2, overflow: 'hidden', pointerEvents: 'none',
        }}>
          <div style={{
            position: 'absolute', left: 0, right: 0, height: '120px',
            background: 'linear-gradient(180deg, transparent, rgba(201, 169, 97, 0.06), transparent)',
            animation: 'scanline 8s linear infinite',
          }} />
        </div>
      </div>

      {/* Animated gradient mesh overlay */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        background: `
          radial-gradient(ellipse at 20% 30%, rgba(201, 169, 97, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 70%, rgba(10, 22, 40, 0.3) 0%, transparent 50%),
          linear-gradient(180deg, rgba(10, 22, 40, 0.5) 0%, rgba(10, 22, 40, 0.7) 60%, rgba(10, 22, 40, 0.95) 100%)
        `,
        pointerEvents: 'none',
      }} />

      {/* Animated city skyline silhouette */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '180px', zIndex: 1, opacity: 0.5, pointerEvents: 'none' }}>
        <SkylineSilhouette />
      </div>

      {/* Floating gold orbs */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '15%', right: '10%', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201, 169, 97, 0.15), transparent 70%)', filter: 'blur(40px)', animation: 'float 8s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '20%', left: '5%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201, 169, 97, 0.1), transparent 70%)', filter: 'blur(60px)', animation: 'float 10s ease-in-out infinite 2s' }} />
      </div>

      {/* Decorative gold lines */}
      <div style={{ position: 'absolute', top: '20%', [lang === 'ar' ? 'right' : 'left']: '5%', width: '1px', height: '60%', background: 'linear-gradient(180deg, transparent, rgba(201, 169, 97, 0.3), transparent)', zIndex: 1 }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 2, maxWidth: '1440px', margin: '0 auto', padding: '0 32px', width: '100%', color: '#F7F4ED' }}>
        <FloatingBadges lang={lang} />
        <div style={{ maxWidth: '780px' }}>
          <div className="reveal" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
            <div style={{ width: '40px', height: '1px', background: '#C9A961' }} />
            <span style={{ fontSize: '11px', letterSpacing: '0.4em', color: '#C9A961', fontWeight: 600 }}>{t.hero.tagline}</span>
          </div>

          <h1 className="serif reveal-delay-1" style={{ fontSize: 'clamp(48px, 8vw, 96px)', fontWeight: 400, lineHeight: 1.02, marginBottom: '24px', letterSpacing: '-0.02em' }}>
            {t.hero.title}
            <br />
            <em style={{ fontStyle: 'italic' }} className="text-shimmer">{t.hero.titleAccent}</em>
          </h1>

          <p className="reveal-delay-2" style={{ fontSize: '18px', lineHeight: 1.6, color: 'rgba(247, 244, 237, 0.8)', maxWidth: '560px', marginBottom: '48px', fontWeight: 300 }}>
            {t.hero.subtitle}
          </p>

          {/* Search bar */}
          <div className="glass reveal-delay-3" style={{
            padding: '8px', display: 'flex', alignItems: 'center', gap: '8px',
            maxWidth: '640px', borderRadius: '0',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1, padding: '0 16px', gap: '12px' }}>
              <Search size={18} color="#C9A961" />
              <input
                type="text"
                placeholder={t.hero.searchPlaceholder}
                value={searchVal}
                onChange={e => setSearchVal(e.target.value)}
                style={{ flex: 1, background: 'transparent', border: 'none', color: '#F7F4ED', fontSize: '15px', padding: '14px 0', outline: 'none', fontFamily: 'inherit' }}
              />
            </div>
            <button onClick={() => goTo('listings')} className="btn-primary" style={{ padding: '14px 28px' }}>
              {t.search.search}
            </button>
          </div>

          {/* Quick filters */}
          <div className="reveal-delay-4" style={{ display: 'flex', gap: '8px', marginTop: '20px', flexWrap: 'wrap' }}>
            {AREAS.map(a => (
              <button key={a.id} onClick={() => goTo('listings', null, a.id)}
                style={{ background: 'rgba(247, 244, 237, 0.05)', border: '1px solid rgba(247, 244, 237, 0.15)', color: 'rgba(247, 244, 237, 0.9)', padding: '8px 16px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.3s', fontFamily: 'inherit', backdropFilter: 'blur(10px)' }}
                onMouseEnter={e => { e.target.style.background = 'rgba(201, 169, 97, 0.15)'; e.target.style.borderColor = '#C9A961'; }}
                onMouseLeave={e => { e.target.style.background = 'rgba(247, 244, 237, 0.05)'; e.target.style.borderColor = 'rgba(247, 244, 237, 0.15)'; }}
              >
                {a.name[lang]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)', zIndex: 2, color: 'rgba(247, 244, 237, 0.6)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', animation: 'float 3s ease-in-out infinite' }}>
        <div style={{ fontSize: '10px', letterSpacing: '0.3em', fontWeight: 600 }}>SCROLL</div>
        <div style={{ width: '1px', height: '40px', background: 'linear-gradient(180deg, #C9A961, transparent)' }} />
      </div>
    </section>
  );
}

// ============== Stats ==============
function StatsSection({ lang, themeStyles, t }) {
  const [counts, setCounts] = useState([0, 0, 0, 0]);
  const [animated, setAnimated] = useState(false);
  const sectionRef = useRef(null);

  const stats = [
    { value: 1250, suffix: '+', label: lang === 'ar' ? 'عقار مباع' : 'Properties Sold' },
    { value: 11, suffix: '', label: lang === 'ar' ? 'سنوات خبرة' : 'Years Experience' },
    { value: 30, suffix: '+', label: lang === 'ar' ? 'مطور شريك' : 'Developer Partners' },
    { value: 98, suffix: '%', label: lang === 'ar' ? 'رضا العملاء' : 'Client Satisfaction' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !animated) {
        setAnimated(true);
        stats.forEach((stat, i) => {
          let current = 0;
          const target = stat.value;
          const increment = target / 60;
          const interval = setInterval(() => {
            current += increment;
            if (current >= target) {
              current = target;
              clearInterval(interval);
            }
            setCounts(prev => { const newC = [...prev]; newC[i] = Math.floor(current); return newC; });
          }, 25);
        });
      }
    }, { threshold: 0.3 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [animated]);

  return (
    <section ref={sectionRef} style={{ padding: '100px 32px', background: '#0A1628', color: '#F7F4ED', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 50%, rgba(201, 169, 97, 0.06), transparent 70%)' }} />
      <div style={{ maxWidth: '1440px', margin: '0 auto', position: 'relative' }}>
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div className="section-eyebrow">— 05 —</div>
          <h2 className="serif" style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, lineHeight: 1.1 }}>{t.sections.stats}</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '40px' }}>
          {stats.map((stat, i) => (
            <div key={i} style={{ textAlign: 'center', position: 'relative' }}>
              {i > 0 && <div className="desktop-only" style={{ position: 'absolute', [lang === 'ar' ? 'right' : 'left']: 0, top: '20%', height: '60%', width: '1px', background: 'rgba(201, 169, 97, 0.2)' }} />}
              <div className="serif text-shimmer" style={{ fontSize: 'clamp(64px, 8vw, 96px)', fontWeight: 400, lineHeight: 1, marginBottom: '12px' }}>
                {counts[i].toLocaleString()}{stat.suffix}
              </div>
              <div style={{ fontSize: '12px', letterSpacing: '0.25em', color: 'rgba(247, 244, 237, 0.7)', textTransform: 'uppercase', fontWeight: 500 }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== Testimonials ==============
function TestimonialsSection({ lang, themeStyles, t, isDark }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setActive(a => (a + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(interval);
  }, []);

  const tm = TESTIMONIALS[active];

  return (
    <section style={{ padding: '120px 32px', background: themeStyles.bgSecondary }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
        <div className="section-eyebrow">— TESTIMONIALS —</div>
        <h2 className="serif" style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 500, marginBottom: '64px' }}>{t.sections.testimonials}</h2>

        <div style={{ position: 'relative', minHeight: '320px' }}>
          {TESTIMONIALS.map((tm, i) => (
            <div key={tm.id} style={{
              position: i === active ? 'relative' : 'absolute', inset: 0,
              opacity: i === active ? 1 : 0,
              transition: 'opacity 0.8s ease',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <div className="serif gold-text" style={{ fontSize: '64px', lineHeight: 1, marginBottom: '24px' }}>&ldquo;</div>
              <p className="serif" style={{ fontSize: 'clamp(20px, 2.5vw, 28px)', fontWeight: 400, lineHeight: 1.5, marginBottom: '40px', fontStyle: 'italic', maxWidth: '800px', color: themeStyles.text }}>
                {tm.text[lang]}
              </p>
              <img src={tm.image} alt={tm.name} style={{ width: '64px', height: '64px', borderRadius: '50%', marginBottom: '16px', border: '2px solid #C9A961' }} />
              <div style={{ fontSize: '15px', fontWeight: 600, color: themeStyles.text, marginBottom: '4px' }}>{tm.name}</div>
              <div style={{ fontSize: '12px', letterSpacing: '0.15em', color: '#C9A961', textTransform: 'uppercase' }}>{tm.title}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '48px' }}>
          {TESTIMONIALS.map((_, i) => (
            <button key={i} onClick={() => setActive(i)}
              style={{ width: i === active ? '32px' : '8px', height: '4px', background: i === active ? '#C9A961' : themeStyles.borderLight, border: 'none', cursor: 'pointer', transition: 'all 0.4s', padding: 0 }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== Property Card ==============
function PropertyCard({ property, lang, t, goTo, currency, isFavorite, toggleFav, themeStyles, delay = 0, compareList = [], toggleCompare = null }) {
  const inCompare = compareList.includes(property.id);
  return (
    <div className="property-card luxe-card" style={{
      background: themeStyles.bgSecondary, border: `1px solid ${themeStyles.border}`,
      cursor: 'pointer', overflow: 'hidden', position: 'relative',
      animation: `fadeUp 0.6s ${delay}s both`,
    }}
      onClick={() => goTo('detail', property.id)}
    >
      {/* Gold sweep on hover */}
      <div className="luxe-sweep" style={{
        position: 'absolute', top: 0, left: '-100%', width: '60%', height: '100%',
        background: 'linear-gradient(110deg, transparent, rgba(201, 169, 97, 0.08), transparent)',
        transition: 'left 0.8s cubic-bezier(0.19, 1, 0.22, 1)',
        zIndex: 1, pointerEvents: 'none',
      }} />

      {/* Image */}
      <div style={{ position: 'relative', overflow: 'hidden', aspectRatio: '4/3' }}>
        <img src={property.image} alt={property.title[lang]} className="card-image" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

        {/* Image gradient overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 50%, rgba(10, 22, 40, 0.4) 100%)', pointerEvents: 'none' }} />

        {/* Badges */}
        <div style={{ position: 'absolute', top: '16px', [lang === 'ar' ? 'right' : 'left']: '16px', display: 'flex', gap: '6px', flexDirection: 'column', alignItems: 'flex-start', zIndex: 2 }}>
          {property.exclusive && <div className="badge-gold">{t.common.exclusive}</div>}
          {property.featured && !property.exclusive && <div className="badge-gold">{t.common.featured}</div>}
          {property.new && <div className="badge-dark">{t.common.new}</div>}
        </div>

        {/* Favorite */}
        <button onClick={e => { e.stopPropagation(); toggleFav(property.id); }}
          style={{
            position: 'absolute', top: '16px', [lang === 'ar' ? 'left' : 'right']: '16px',
            width: '40px', height: '40px', borderRadius: '50%',
            background: 'rgba(10, 22, 40, 0.6)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(247, 244, 237, 0.2)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s',
          }}>
          <Heart size={16} fill={isFavorite ? '#C9A961' : 'none'} color={isFavorite ? '#C9A961' : '#F7F4ED'} />
        </button>

        {/* Compare */}
        {toggleCompare && (
          <button onClick={e => { e.stopPropagation(); toggleCompare(property.id); }}
            style={{
              position: 'absolute', top: '64px', [lang === 'ar' ? 'left' : 'right']: '16px',
              width: '40px', height: '40px', borderRadius: '50%',
              background: inCompare ? '#C9A961' : 'rgba(10, 22, 40, 0.6)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${inCompare ? '#C9A961' : 'rgba(247, 244, 237, 0.2)'}`,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s',
            }}
            title={inCompare ? 'Remove from compare' : 'Add to compare'}
          >
            <GitCompare size={16} color={inCompare ? '#0A1628' : '#F7F4ED'} />
          </button>
        )}

        {/* Score */}
        {property.score >= 90 && (
          <div style={{ position: 'absolute', bottom: '16px', [lang === 'ar' ? 'right' : 'left']: '16px', background: 'rgba(10, 22, 40, 0.85)', backdropFilter: 'blur(10px)', border: '1px solid rgba(201, 169, 97, 0.4)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Sparkles size={12} color="#C9A961" />
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#F7F4ED', letterSpacing: '0.05em' }}>
              {property.score} {lang === 'ar' ? 'تقييم' : 'SCORE'}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#C9A961', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 600, marginBottom: '8px' }}>
          <MapPin size={12} /> {property.areaName[lang]}
        </div>
        <h3 className="serif" style={{ fontSize: '22px', fontWeight: 500, marginBottom: '6px', color: themeStyles.text, lineHeight: 1.2 }}>
          {property.title[lang]}
        </h3>
        <div style={{ fontSize: '12px', color: themeStyles.textMuted, marginBottom: '20px' }}>{property.developer} • {property.project}</div>

        <div style={{ display: 'flex', gap: '16px', paddingBottom: '20px', borderBottom: `1px solid ${themeStyles.borderLight}`, marginBottom: '20px', fontSize: '13px', color: themeStyles.textSecondary }}>
          {property.bedrooms > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Bed size={14} color="#C9A961" /> {property.bedrooms}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Bath size={14} color="#C9A961" /> {property.bathrooms}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Maximize size={14} color="#C9A961" /> {property.sqm}m²</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: '10px', letterSpacing: '0.2em', color: themeStyles.textMuted, marginBottom: '4px', fontWeight: 600 }}>{t.listings.from.toUpperCase()}</div>
            <div className="serif" style={{ fontSize: '22px', fontWeight: 600, color: '#C9A961' }}>
              {formatPrice(property.price, currency, lang)}
            </div>
          </div>
          <ArrowRight size={18} color="#C9A961" style={{ transform: lang === 'ar' ? 'scaleX(-1)' : 'none' }} />
        </div>
      </div>
    </div>
  );
}

// ============== Listings Page ==============
function ListingsPage({ t, lang, goTo, themeStyles, currency, favorites, toggleFav, isDark, initialArea, setFilterArea, compareList, toggleCompare }) {
  const [view, setView] = useState('grid');
  const [filters, setFilters] = useState({
    area: initialArea || 'all',
    type: 'all',
    bedrooms: 'all',
    minPrice: 0,
    maxPrice: 100000000,
    developer: 'all',
    finishing: 'all',
  });
  const [sort, setSort] = useState('recommended');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (initialArea) setFilters(f => ({ ...f, area: initialArea }));
  }, [initialArea]);

  const filtered = useMemo(() => {
    let result = PROPERTIES.filter(p => {
      if (filters.area !== 'all' && p.area !== filters.area) return false;
      if (filters.type !== 'all' && p.type !== filters.type) return false;
      if (filters.bedrooms !== 'all' && p.bedrooms !== parseInt(filters.bedrooms)) return false;
      if (p.price < filters.minPrice || p.price > filters.maxPrice) return false;
      if (filters.developer !== 'all' && p.developer !== filters.developer) return false;
      if (filters.finishing !== 'all' && p.finishing !== filters.finishing) return false;
      return true;
    });

    if (sort === 'priceLow') result.sort((a, b) => a.price - b.price);
    else if (sort === 'priceHigh') result.sort((a, b) => b.price - a.price);
    else if (sort === 'newest') result.sort((a, b) => (b.new ? 1 : 0) - (a.new ? 1 : 0));
    else result.sort((a, b) => b.score - a.score);

    return result;
  }, [filters, sort]);

  const updateFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  const clearFilters = () => {
    setFilters({ area: 'all', type: 'all', bedrooms: 'all', minPrice: 0, maxPrice: 100000000, developer: 'all', finishing: 'all' });
    setFilterArea('all');
  };

  return (
    <div style={{ paddingTop: '76px', position: 'relative' }}>
      <PageBackdrop
        image="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=2400&q=85"
        height="55vh"
        isDark={isDark}
      />
      {/* Header */}
      <section style={{ padding: '60px 32px 40px', maxWidth: '1440px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div className="section-eyebrow">— {t.listings.title.toUpperCase()} —</div>
        <h1 className="serif" style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, lineHeight: 1.1, marginBottom: '12px' }}>{t.listings.title}</h1>
        <p style={{ color: themeStyles.textSecondary, fontSize: '15px' }}>
          <span style={{ color: '#C9A961', fontWeight: 600 }}>{filtered.length}</span> {t.listings.results}
        </p>
      </section>

      {/* Filters bar */}
      <section style={{ padding: '0 32px 32px', maxWidth: '1440px', margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={() => setShowFilters(!showFilters)} className="btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }}>
            <SlidersHorizontal size={14} /> {t.listings.filters}
          </button>

          <select value={filters.area} onChange={e => updateFilter('area', e.target.value)} className="input-field" style={{ width: 'auto', minWidth: '150px', padding: '10px 16px' }}>
            <option value="all" style={{ background: themeStyles.bgSolid }}>{t.listings.any} - {lang === 'ar' ? 'الموقع' : 'Location'}</option>
            {AREAS.map(a => <option key={a.id} value={a.id} style={{ background: themeStyles.bgSolid }}>{a.name[lang]}</option>)}
          </select>

          <select value={filters.type} onChange={e => updateFilter('type', e.target.value)} className="input-field" style={{ width: 'auto', minWidth: '150px', padding: '10px 16px' }}>
            <option value="all" style={{ background: themeStyles.bgSolid }}>{t.listings.any} - {lang === 'ar' ? 'النوع' : 'Type'}</option>
            <option value="apartments" style={{ background: themeStyles.bgSolid }}>{t.categories.apartments}</option>
            <option value="villas" style={{ background: themeStyles.bgSolid }}>{t.categories.villas}</option>
            <option value="chalets" style={{ background: themeStyles.bgSolid }}>{t.categories.chalets}</option>
            <option value="commercial" style={{ background: themeStyles.bgSolid }}>{t.categories.commercial}</option>
          </select>

          <select value={sort} onChange={e => setSort(e.target.value)} className="input-field" style={{ width: 'auto', minWidth: '170px', padding: '10px 16px', [lang === 'ar' ? 'marginRight' : 'marginLeft']: 'auto' }}>
            <option value="recommended" style={{ background: themeStyles.bgSolid }}>{t.listings.recommended}</option>
            <option value="priceLow" style={{ background: themeStyles.bgSolid }}>{t.listings.priceLow}</option>
            <option value="priceHigh" style={{ background: themeStyles.bgSolid }}>{t.listings.priceHigh}</option>
            <option value="newest" style={{ background: themeStyles.bgSolid }}>{t.listings.newest}</option>
          </select>

          {/* View toggle */}
          <div style={{ display: 'flex', border: `1px solid ${themeStyles.borderLight}` }}>
            <button onClick={() => setView('grid')} style={{ background: view === 'grid' ? '#C9A961' : 'transparent', color: view === 'grid' ? '#0A1628' : themeStyles.text, border: 'none', padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit', fontSize: '13px' }}>
              <Grid3x3 size={14} /> {t.listings.grid}
            </button>
            <button onClick={() => setView('map')} style={{ background: view === 'map' ? '#C9A961' : 'transparent', color: view === 'map' ? '#0A1628' : themeStyles.text, border: 'none', padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit', fontSize: '13px' }}>
              <MapIcon size={14} /> {t.listings.map}
            </button>
          </div>
        </div>

        {/* Advanced filters drawer */}
        {showFilters && (
          <div style={{ marginTop: '16px', padding: '24px', background: themeStyles.bgSecondary, border: `1px solid ${themeStyles.border}`, animation: 'fadeUp 0.3s ease' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
              <div>
                <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, textTransform: 'uppercase', fontWeight: 600, marginBottom: '8px', display: 'block' }}>{t.listings.bedrooms}</label>
                <select value={filters.bedrooms} onChange={e => updateFilter('bedrooms', e.target.value)} className="input-field">
                  <option value="all" style={{ background: themeStyles.bgSolid }}>{t.listings.any}</option>
                  {[1, 2, 3, 4, 5].map(n => <option key={n} value={n} style={{ background: themeStyles.bgSolid }}>{n}+</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, textTransform: 'uppercase', fontWeight: 600, marginBottom: '8px', display: 'block' }}>{t.listings.developer}</label>
                <select value={filters.developer} onChange={e => updateFilter('developer', e.target.value)} className="input-field">
                  <option value="all" style={{ background: themeStyles.bgSolid }}>{t.listings.any}</option>
                  {DEVELOPERS.map(d => <option key={d.id} value={d.name} style={{ background: themeStyles.bgSolid }}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, textTransform: 'uppercase', fontWeight: 600, marginBottom: '8px', display: 'block' }}>{t.listings.finishing}</label>
                <select value={filters.finishing} onChange={e => updateFilter('finishing', e.target.value)} className="input-field">
                  <option value="all" style={{ background: themeStyles.bgSolid }}>{t.listings.any}</option>
                  <option value="Fully Finished" style={{ background: themeStyles.bgSolid }}>Fully Finished</option>
                  <option value="Semi-Finished" style={{ background: themeStyles.bgSolid }}>Semi-Finished</option>
                  <option value="Core & Shell" style={{ background: themeStyles.bgSolid }}>Core & Shell</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, textTransform: 'uppercase', fontWeight: 600, marginBottom: '8px', display: 'block' }}>{t.listings.price} (EGP)</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="number" placeholder="Min" value={filters.minPrice || ''} onChange={e => updateFilter('minPrice', parseInt(e.target.value) || 0)} className="input-field" style={{ padding: '12px' }} />
                  <input type="number" placeholder="Max" value={filters.maxPrice === 100000000 ? '' : filters.maxPrice} onChange={e => updateFilter('maxPrice', parseInt(e.target.value) || 100000000)} className="input-field" style={{ padding: '12px' }} />
                </div>
              </div>
            </div>
            <button onClick={clearFilters} style={{ marginTop: '20px', background: 'none', border: 'none', color: '#C9A961', cursor: 'pointer', fontSize: '12px', letterSpacing: '0.1em', fontWeight: 600, fontFamily: 'inherit', textTransform: 'uppercase' }}>
              {t.listings.clear}
            </button>
          </div>
        )}
      </section>

      {/* Results */}
      <section style={{ padding: '0 32px 80px', maxWidth: '1440px', margin: '0 auto' }}>
        {view === 'grid' ? (
          filtered.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '28px' }}>
              {filtered.map((p, i) => (
                <PropertyCard key={p.id} property={p} lang={lang} t={t} goTo={goTo} currency={currency} isFavorite={favorites.includes(p.id)} toggleFav={toggleFav} themeStyles={themeStyles} delay={i * 0.05} compareList={compareList} toggleCompare={toggleCompare} />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '80px 32px' }}>
              <div className="serif" style={{ fontSize: '32px', marginBottom: '12px' }}>{lang === 'ar' ? 'لا توجد نتائج' : 'No results found'}</div>
              <p style={{ color: themeStyles.textSecondary, marginBottom: '24px' }}>{lang === 'ar' ? 'جرب تعديل الفلاتر' : 'Try adjusting your filters'}</p>
              <button onClick={clearFilters} className="btn-primary">{t.listings.clear}</button>
            </div>
          )
        ) : (
          <LeafletMap
            properties={filtered}
            goTo={goTo}
            currency={currency}
            lang={lang}
            themeStyles={themeStyles}
            isDark={isDark}
            isRTL={lang === 'ar'}
          />
        )}
      </section>
    </div>
  );
}

// ============== Property Detail Page ==============
function DetailPage({ propertyId, t, lang, goTo, themeStyles, currency, favorites, toggleFav, isDark, compareList, toggleCompare }) {
  const property = PROPERTIES.find(p => p.id === propertyId);
  const [activeImage, setActiveImage] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const inCompare = compareList?.includes(propertyId);

  // Mortgage calculator state
  const [downPayment, setDownPayment] = useState(20);
  const [interestRate, setInterestRate] = useState(8);
  const [loanYears, setLoanYears] = useState(20);

  if (!property) return <div style={{ padding: '120px 32px', textAlign: 'center' }}>Property not found</div>;

  const loanAmount = property.price * (1 - downPayment / 100);
  const monthlyRate = interestRate / 100 / 12;
  const numPayments = loanYears * 12;
  const monthlyPayment = monthlyRate > 0 ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1) : loanAmount / numPayments;

  const similar = PROPERTIES.filter(p => p.id !== property.id && (p.area === property.area || p.type === property.type)).slice(0, 3);

  const specs = [
    { label: t.listings.bedrooms, value: property.bedrooms || '—' },
    { label: t.listings.bathrooms, value: property.bathrooms },
    { label: t.listings.area, value: `${property.sqm} m²` },
    { label: t.listings.finishing, value: property.finishing },
    { label: t.listings.delivery, value: property.delivery },
    { label: t.listings.developer, value: property.developer },
  ];

  const amenities = lang === 'ar' ? ['حمام سباحة', 'صالة رياضية', 'حديقة', 'موقف خاص', 'أمن ٢٤/٧', 'كلوب هاوس', 'منطقة شواء', 'منطقة أطفال'] : ['Swimming Pool', 'Fitness Center', 'Landscaped Gardens', 'Private Parking', '24/7 Security', 'Clubhouse', 'BBQ Area', 'Kids Play Area'];

  return (
    <div style={{ paddingTop: '76px' }}>
      {/* Breadcrumb */}
      <div style={{ padding: '24px 32px 0', maxWidth: '1440px', margin: '0 auto', fontSize: '12px', color: themeStyles.textMuted, letterSpacing: '0.1em' }}>
        <button onClick={() => goTo('home')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontFamily: 'inherit' }}>{t.nav.home}</button>
        <span style={{ margin: '0 12px' }}>/</span>
        <button onClick={() => goTo('listings')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontFamily: 'inherit' }}>{t.nav.listings}</button>
        <span style={{ margin: '0 12px' }}>/</span>
        <span style={{ color: themeStyles.text }}>{property.title[lang]}</span>
      </div>

      {/* Gallery */}
      <section style={{ padding: '32px', maxWidth: '1440px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', aspectRatio: '16/9' }}>
          <div style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer' }}>
            <img src={property.gallery[activeImage]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', top: '24px', [lang === 'ar' ? 'right' : 'left']: '24px', display: 'flex', gap: '8px' }}>
              {property.exclusive && <div className="badge-gold">{t.common.exclusive}</div>}
              {property.featured && !property.exclusive && <div className="badge-gold">{t.common.featured}</div>}
            </div>
            {/* Video play button */}
            {property.video && (
              <button onClick={() => setVideoOpen(true)}
                style={{
                  position: 'absolute', bottom: '24px', [lang === 'ar' ? 'right' : 'left']: '24px',
                  background: 'rgba(10, 22, 40, 0.8)', backdropFilter: 'blur(12px)',
                  border: '1px solid #C9A961', color: '#F7F4ED', padding: '12px 20px',
                  display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, letterSpacing: '0.05em',
                  transition: 'all 0.3s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#C9A961'; e.currentTarget.style.color = '#0A1628'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(10, 22, 40, 0.8)'; e.currentTarget.style.color = '#F7F4ED'; }}
              >
                <Play size={14} fill="currentColor" /> {t.common.watchVideo}
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {property.gallery.slice(0, 4).map((img, i) => (
              <button key={i} onClick={() => setActiveImage(i)} style={{ position: 'relative', overflow: 'hidden', border: 'none', padding: 0, cursor: 'pointer' }}>
                <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: i === activeImage ? 1 : 0.7, transition: 'opacity 0.3s' }} />
                {i === 3 && property.gallery.length > 4 && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(10, 22, 40, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C9A961', fontWeight: 600 }}>+{property.gallery.length - 4}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Title + actions */}
      <section style={{ padding: '24px 32px', maxWidth: '1440px', margin: '0 auto', display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '40px', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#C9A961', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600, marginBottom: '12px' }}>
            <MapPin size={14} /> {property.areaName[lang]} • {property.project}
          </div>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, lineHeight: 1.05, marginBottom: '20px' }}>{property.title[lang]}</h1>

          <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', paddingBottom: '24px', borderBottom: `1px solid ${themeStyles.borderLight}`, color: themeStyles.textSecondary, fontSize: '15px' }}>
            {property.bedrooms > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Bed size={18} color="#C9A961" /> {property.bedrooms} {lang === 'ar' ? 'غرف' : 'beds'}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Bath size={18} color="#C9A961" /> {property.bathrooms} {lang === 'ar' ? 'حمام' : 'baths'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Maximize size={18} color="#C9A961" /> {property.sqm} m²</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Calendar size={18} color="#C9A961" /> {t.detail.deliveryIn} {property.delivery}</div>
          </div>

          <p style={{ fontSize: '16px', lineHeight: 1.8, color: themeStyles.textSecondary, marginTop: '32px', maxWidth: '720px' }}>{property.description[lang]}</p>
        </div>

        <div style={{ position: 'sticky', top: '100px' }}>
          <div className="glass" style={{ padding: '32px' }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.25em', color: themeStyles.textMuted, fontWeight: 600, marginBottom: '8px' }}>{t.listings.from.toUpperCase()}</div>
            <div className="serif" style={{ fontSize: '40px', fontWeight: 600, color: '#C9A961', lineHeight: 1, marginBottom: '8px' }}>
              {formatPrice(property.price, currency, lang)}
            </div>
            <div style={{ fontSize: '13px', color: themeStyles.textSecondary, marginBottom: '32px' }}>
              {formatPrice(monthlyPayment, currency, lang)} {t.listings.perMonth}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <a href="tel:+20100000000" className="btn-primary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', textDecoration: 'none' }}>
                <Phone size={16} /> {t.detail.callNow}
              </a>
              <a href={`https://wa.me/201000000000?text=I'm interested in ${property.title.en}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '14px 28px', background: '#25D366', color: 'white', textDecoration: 'none', fontWeight: 600, fontSize: '14px' }}>
                <MessageCircle size={16} /> {t.detail.whatsapp}
              </a>
              <button className="btn-ghost" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <Calendar size={16} /> {t.detail.schedule}
              </button>
            </div>

            <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: `1px solid ${themeStyles.borderLight}`, display: 'flex', gap: '8px' }}>
              <button onClick={() => toggleFav(property.id)} style={{ flex: 1, background: 'none', border: `1px solid ${themeStyles.borderLight}`, padding: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', color: themeStyles.text, fontFamily: 'inherit', fontSize: '12px' }}>
                <Heart size={14} fill={favorites.includes(property.id) ? '#C9A961' : 'none'} color={favorites.includes(property.id) ? '#C9A961' : themeStyles.text} />
                {favorites.includes(property.id) ? t.listings.saved : t.listings.save}
              </button>
              <button style={{ flex: 1, background: 'none', border: `1px solid ${themeStyles.borderLight}`, padding: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', color: themeStyles.text, fontFamily: 'inherit', fontSize: '12px' }}>
                <Share2 size={14} /> {t.detail.share}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Specs grid */}
      <section style={{ padding: '60px 32px', maxWidth: '1440px', margin: '0 auto' }}>
        <h2 className="serif" style={{ fontSize: '36px', fontWeight: 500, marginBottom: '32px' }}>{t.detail.specsTitle}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '24px', borderTop: `1px solid ${themeStyles.borderLight}`, paddingTop: '32px' }}>
          {specs.map((s, i) => (
            <div key={i} style={{ paddingRight: '20px' }}>
              <div style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>{s.label}</div>
              <div className="serif" style={{ fontSize: '20px', fontWeight: 500, color: themeStyles.text }}>{s.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Amenities */}
      <section style={{ padding: '60px 32px', maxWidth: '1440px', margin: '0 auto', background: themeStyles.bgSecondary, marginLeft: '0', marginRight: '0', maxWidth: '100%' }}>
        <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 0' }}>
          <h2 className="serif" style={{ fontSize: '36px', fontWeight: 500, marginBottom: '32px' }}>{t.detail.amenities}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {amenities.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 0', borderBottom: `1px solid ${themeStyles.borderLight}` }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(201, 169, 97, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Check size={16} color="#C9A961" />
                </div>
                <span style={{ fontSize: '14px' }}>{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mortgage Calculator */}
      <section style={{ padding: '60px 32px', maxWidth: '1440px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '60px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <Calculator size={24} color="#C9A961" />
              <div className="section-eyebrow" style={{ marginBottom: 0 }}>{lang === 'ar' ? 'حاسبة' : 'Calculator'}</div>
            </div>
            <h2 className="serif" style={{ fontSize: '36px', fontWeight: 500, marginBottom: '32px' }}>{t.detail.mortgage}</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label style={{ fontSize: '12px', letterSpacing: '0.15em', color: themeStyles.textMuted, fontWeight: 600 }}>{t.detail.downpayment}</label>
                  <span className="serif gold-text" style={{ fontSize: '20px', fontWeight: 600 }}>{downPayment}%</span>
                </div>
                <input type="range" min="10" max="60" step="5" value={downPayment} onChange={e => setDownPayment(parseInt(e.target.value))} style={{ width: '100%', accentColor: '#C9A961' }} />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label style={{ fontSize: '12px', letterSpacing: '0.15em', color: themeStyles.textMuted, fontWeight: 600 }}>{t.detail.interestRate}</label>
                  <span className="serif gold-text" style={{ fontSize: '20px', fontWeight: 600 }}>{interestRate}%</span>
                </div>
                <input type="range" min="3" max="20" step="0.5" value={interestRate} onChange={e => setInterestRate(parseFloat(e.target.value))} style={{ width: '100%', accentColor: '#C9A961' }} />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label style={{ fontSize: '12px', letterSpacing: '0.15em', color: themeStyles.textMuted, fontWeight: 600 }}>{t.detail.loanTerm}</label>
                  <span className="serif gold-text" style={{ fontSize: '20px', fontWeight: 600 }}>{loanYears}</span>
                </div>
                <input type="range" min="5" max="30" step="1" value={loanYears} onChange={e => setLoanYears(parseInt(e.target.value))} style={{ width: '100%', accentColor: '#C9A961' }} />
              </div>
            </div>
          </div>

          <div className="glass" style={{ padding: '40px' }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.25em', color: themeStyles.textMuted, fontWeight: 600, marginBottom: '12px' }}>{t.detail.monthlyPayment.toUpperCase()}</div>
            <div className="serif text-shimmer" style={{ fontSize: '56px', fontWeight: 600, lineHeight: 1, marginBottom: '32px' }}>
              {formatPrice(monthlyPayment, currency, lang)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '24px', borderTop: `1px solid ${themeStyles.borderLight}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span style={{ color: themeStyles.textSecondary }}>{lang === 'ar' ? 'سعر العقار' : 'Property Price'}</span>
                <span style={{ fontWeight: 600 }}>{formatPrice(property.price, currency, lang)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span style={{ color: themeStyles.textSecondary }}>{t.detail.downpayment}</span>
                <span style={{ fontWeight: 600 }}>{formatPrice(property.price * downPayment / 100, currency, lang)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span style={{ color: themeStyles.textSecondary }}>{t.detail.loanAmount}</span>
                <span style={{ fontWeight: 600 }}>{formatPrice(loanAmount, currency, lang)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span style={{ color: themeStyles.textSecondary }}>{lang === 'ar' ? 'إجمالي الفائدة' : 'Total Interest'}</span>
                <span style={{ fontWeight: 600, color: '#C9A961' }}>{formatPrice(monthlyPayment * numPayments - loanAmount, currency, lang)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agent */}
      <section style={{ padding: '60px 32px', background: themeStyles.bgSecondary }}>
        <div style={{ maxWidth: '1440px', margin: '0 auto' }}>
          <h2 className="serif" style={{ fontSize: '36px', fontWeight: 500, marginBottom: '32px' }}>{t.detail.agent}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '32px', alignItems: 'center', padding: '32px', background: themeStyles.bg, border: `1px solid ${themeStyles.border}` }}>
            <img src="https://i.pravatar.cc/200?img=33" alt="" style={{ width: '120px', height: '120px', borderRadius: '50%', border: '2px solid #C9A961', objectFit: 'cover' }} />
            <div>
              <div className="serif" style={{ fontSize: '28px', fontWeight: 500, marginBottom: '8px' }}>Khaled Mansour</div>
              <div style={{ fontSize: '12px', letterSpacing: '0.2em', color: '#C9A961', textTransform: 'uppercase', fontWeight: 600, marginBottom: '12px' }}>{lang === 'ar' ? 'مستشار عقاري أول' : 'Senior Property Advisor'}</div>
              <div style={{ fontSize: '14px', color: themeStyles.textSecondary, display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <span><Star size={14} color="#C9A961" fill="#C9A961" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} /> 4.9 (127 {lang === 'ar' ? 'تقييم' : 'reviews'})</span>
                <span>{lang === 'ar' ? '٨ سنوات خبرة' : '8 years experience'}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }} className="desktop-only">
              <a href="tel:+20100000000" style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#C9A961', color: '#0A1628', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                <Phone size={18} />
              </a>
              <a href="https://wa.me/201000000000" style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#25D366', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                <MessageCircle size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Similar */}
      <section style={{ padding: '80px 32px', maxWidth: '1440px', margin: '0 auto' }}>
        <h2 className="serif" style={{ fontSize: '36px', fontWeight: 500, marginBottom: '40px' }}>{t.detail.similar}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '28px' }}>
          {similar.map((p, i) => (
            <PropertyCard key={p.id} property={p} lang={lang} t={t} goTo={goTo} currency={currency} isFavorite={favorites.includes(p.id)} toggleFav={toggleFav} themeStyles={themeStyles} delay={i * 0.1} compareList={compareList} toggleCompare={toggleCompare} />
          ))}
        </div>
      </section>

      {/* Video Modal */}
      {videoOpen && property.video && (
        <VideoModal videoUrl={property.video} title={property.title[lang]} onClose={() => setVideoOpen(false)} lang={lang} />
      )}
    </div>
  );
}

// ============== About Page ==============
function AboutPage({ t, lang, themeStyles, isDark }) {
  const team = [
    { name: lang === 'ar' ? 'كريم حسن' : 'Karim Hassan', title: lang === 'ar' ? 'الرئيس التنفيذي والمؤسس' : 'CEO & Founder', img: 'https://i.pravatar.cc/300?img=53' },
    { name: lang === 'ar' ? 'نور الشريف' : 'Nour El Sherif', title: lang === 'ar' ? 'مديرة العمليات' : 'COO', img: 'https://i.pravatar.cc/300?img=49' },
    { name: lang === 'ar' ? 'يوسف عبد الرحمن' : 'Youssef Abdelrahman', title: lang === 'ar' ? 'مدير المبيعات' : 'Head of Sales', img: 'https://i.pravatar.cc/300?img=60' },
    { name: lang === 'ar' ? 'لارا فهمي' : 'Lara Fahmy', title: lang === 'ar' ? 'مديرة التسويق' : 'Head of Marketing', img: 'https://i.pravatar.cc/300?img=44' },
  ];

  return (
    <div style={{ paddingTop: '76px' }}>
      {/* Hero */}
      <section style={{ position: 'relative', minHeight: '60vh', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <img src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=2400&q=85" alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.4)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10,22,40,0.6), rgba(10,22,40,0.9))' }} />
        <div style={{ position: 'relative', maxWidth: '1440px', margin: '0 auto', padding: '0 32px', color: '#F7F4ED', width: '100%' }}>
          <div className="section-eyebrow reveal">— {lang === 'ar' ? 'من نحن' : 'ABOUT US'} —</div>
          <h1 className="serif reveal-delay-1" style={{ fontSize: 'clamp(48px, 7vw, 88px)', fontWeight: 400, lineHeight: 1.05, maxWidth: '900px' }}>
            {t.about.title.split(' ').map((w, i) => i === t.about.title.split(' ').length - 1 ? <em key={i} style={{ fontStyle: 'italic' }} className="text-shimmer">{w}</em> : <span key={i}>{w} </span>)}
          </h1>
          <p className="reveal-delay-2" style={{ fontSize: '18px', color: 'rgba(247, 244, 237, 0.8)', marginTop: '24px', maxWidth: '600px' }}>{t.about.subtitle}</p>
        </div>
      </section>

      {/* Story */}
      <section style={{ padding: '120px 32px', maxWidth: '1000px', margin: '0 auto' }}>
        <div className="gold-line" style={{ width: '60px', marginBottom: '32px' }} />
        <p style={{ fontSize: 'clamp(18px, 2vw, 22px)', lineHeight: 1.8, color: themeStyles.textSecondary, fontWeight: 300 }}>{t.about.story}</p>
      </section>

      {/* Vision/Mission */}
      <section style={{ padding: '60px 32px', background: themeStyles.bgSecondary }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '40px' }}>
          {[{ t: t.about.vision, d: t.about.visionText, icon: Eye, n: '01' }, { t: t.about.mission, d: t.about.missionText, icon: TrendingUp, n: '02' }].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} style={{ padding: '40px', background: themeStyles.bg, border: `1px solid ${themeStyles.border}`, position: 'relative' }}>
                <div className="serif gold-text" style={{ position: 'absolute', top: '20px', [lang === 'ar' ? 'left' : 'right']: '24px', fontSize: '60px', fontWeight: 300, opacity: 0.3 }}>{item.n}</div>
                <Icon size={32} color="#C9A961" strokeWidth={1.5} style={{ marginBottom: '24px' }} />
                <h3 className="serif" style={{ fontSize: '32px', fontWeight: 500, marginBottom: '16px' }}>{item.t}</h3>
                <p style={{ color: themeStyles.textSecondary, fontSize: '15px', lineHeight: 1.7 }}>{item.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Team */}
      <section style={{ padding: '120px 32px', maxWidth: '1440px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div className="section-eyebrow">— {lang === 'ar' ? 'القيادة' : 'LEADERSHIP'} —</div>
          <h2 className="serif" style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500 }}>{t.about.team}</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '32px' }}>
          {team.map((m, i) => (
            <div key={i} style={{ animation: `fadeUp 0.6s ${i * 0.1}s both`, textAlign: 'center' }}>
              <div style={{ aspectRatio: '3/4', overflow: 'hidden', marginBottom: '20px', background: themeStyles.bgSecondary }}>
                <img src={m.img} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(20%)', transition: 'filter 0.4s' }}
                  onMouseEnter={e => e.target.style.filter = 'grayscale(0%)'}
                  onMouseLeave={e => e.target.style.filter = 'grayscale(20%)'}
                />
              </div>
              <h3 className="serif" style={{ fontSize: '22px', fontWeight: 500, marginBottom: '6px' }}>{m.name}</h3>
              <div style={{ fontSize: '11px', letterSpacing: '0.2em', color: '#C9A961', textTransform: 'uppercase', fontWeight: 600 }}>{m.title}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ============== Developers Page ==============
function DevelopersPage({ t, lang, goTo, themeStyles, isDark }) {
  return (
    <div style={{ paddingTop: '120px', paddingBottom: '80px', position: 'relative' }}>
      <PageBackdrop
        image="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=2400&q=85"
        height="80vh"
        isDark={isDark}
      />
      <section style={{ padding: '0 32px', maxWidth: '1440px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div className="section-eyebrow">— {lang === 'ar' ? 'شركاؤنا' : 'OUR PARTNERS'} —</div>
        <h1 className="serif" style={{ fontSize: 'clamp(40px, 6vw, 72px)', fontWeight: 500, lineHeight: 1.05, marginBottom: '20px' }}>{t.nav.developers}</h1>
        <p style={{ fontSize: '17px', color: themeStyles.textSecondary, maxWidth: '700px', marginBottom: '60px' }}>
          {lang === 'ar' ? 'شراكات مباشرة مع نخبة المطورين العقاريين في مصر تضمن لك الأولوية في الوصول وأفضل الأسعار.' : 'Direct partnerships with Egypt\'s most respected developers ensure priority access and competitive pricing.'}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
          {DEVELOPERS.map((d, i) => {
            // Rotate through architectural showcase images
            const showcaseImages = [
              'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&q=80',
              'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1200&q=80',
              'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1200&q=80',
              'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
              'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80',
              'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1200&q=80',
              'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80',
              'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=80',
            ];
            return (
              <div key={d.id} className="luxe-card" style={{
                background: themeStyles.bgSecondary,
                backdropFilter: 'blur(20px) saturate(160%)',
                WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                border: `1px solid ${themeStyles.border}`,
                cursor: 'pointer', transition: 'all 0.4s cubic-bezier(0.19, 1, 0.22, 1)',
                animation: `fadeUp 0.6s ${i * 0.05}s both`,
                overflow: 'hidden', position: 'relative',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#C9A961'; e.currentTarget.style.transform = 'translateY(-6px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = themeStyles.border; e.currentTarget.style.transform = 'translateY(0)'; }}
                onClick={() => goTo('listings')}
              >
                {/* Gold sweep on hover */}
                <div className="luxe-sweep" style={{
                  position: 'absolute', top: 0, left: '-100%', width: '60%', height: '100%',
                  background: 'linear-gradient(110deg, transparent, rgba(201, 169, 97, 0.08), transparent)',
                  transition: 'left 0.8s cubic-bezier(0.19, 1, 0.22, 1)',
                  zIndex: 1, pointerEvents: 'none',
                }} />

                {/* Project showcase header image */}
                <div style={{ position: 'relative', aspectRatio: '16/9', overflow: 'hidden' }}>
                  <img src={showcaseImages[i % showcaseImages.length]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.7) saturate(1.05)' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 0%, rgba(10, 22, 40, 0.6) 100%)' }} />
                  <div style={{ position: 'absolute', bottom: '16px', [lang === 'ar' ? 'right' : 'left']: '16px', width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(10, 22, 40, 0.7)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(201, 169, 97, 0.4)' }}>
                    <Building2 size={20} color="#C9A961" />
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: '24px 28px 28px', position: 'relative' }}>
                  <h3 className="serif" style={{ fontSize: '24px', fontWeight: 500, marginBottom: '6px' }}>{d.name}</h3>
                  <div style={{ fontSize: '11px', letterSpacing: '0.2em', color: '#C9A961', textTransform: 'uppercase', fontWeight: 600, marginBottom: '20px' }}>EST. {d.founded}</div>
                  <div style={{ display: 'flex', gap: '24px', fontSize: '13px', color: themeStyles.textSecondary, paddingTop: '16px', borderTop: `1px solid ${themeStyles.borderLight}` }}>
                    <div><span style={{ color: themeStyles.text, fontWeight: 600 }}>{d.projects}</span> {lang === 'ar' ? 'مشروع' : 'projects'}</div>
                    <div><span style={{ color: themeStyles.text, fontWeight: 600 }}>{new Date().getFullYear() - d.founded}</span> {lang === 'ar' ? 'سنة' : 'years'}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ============== Areas Page ==============
function AreasPage({ t, lang, goTo, themeStyles, isDark }) {
  const areaInsights = {
    'north-coast': { en: 'Egypt\'s premier summer destination, where Mediterranean shores meet world-class developments. Average ROI of 12-18% drives strong investor demand.', ar: 'وجهة مصر الصيفية الرائدة، حيث تلتقي شواطئ البحر المتوسط بمشاريع عالمية المستوى. عائد استثماري ١٢-١٨٪ يدفع طلب المستثمرين.' },
    'new-cairo': { en: 'The capital\'s premier residential hub. Established infrastructure, top schools, and proximity to major business districts.', ar: 'المركز السكني الأرقى في القاهرة. بنية تحتية متطورة وأفضل المدارس وقرب من المناطق التجارية الرئيسية.' },
    'new-capital': { en: 'Egypt\'s ambitious greenfield capital, offering ground-floor investment opportunities with exceptional appreciation potential.', ar: 'العاصمة الإدارية الجديدة الطموحة، تقدم فرص استثمار مبكرة بإمكانات نمو استثنائية.' },
    'sheikh-zayed': { en: 'West Cairo\'s most established premium community. Family-oriented compounds, mature greenery, and proven appreciation.', ar: 'المجتمع المتميز الأكثر استقراراً في غرب القاهرة. مجمعات عائلية وخضرة ناضجة وقيمة متنامية.' },
    'sokhna': { en: 'Red Sea retreat just two hours from Cairo. Year-round destination growing rapidly with new developments and infrastructure.', ar: 'ملاذ البحر الأحمر على بعد ساعتين من القاهرة. وجهة على مدار العام تنمو بسرعة مع مشاريع وبنية تحتية جديدة.' },
  };

  return (
    <div style={{ paddingTop: '120px', paddingBottom: '80px', position: 'relative' }}>
      <PageBackdrop
        image="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=2400&q=85"
        height="80vh"
        isDark={isDark}
      />
      <section style={{ padding: '0 32px 60px', maxWidth: '1440px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div className="section-eyebrow">— {lang === 'ar' ? 'المواقع' : 'LOCATIONS'} —</div>
        <h1 className="serif" style={{ fontSize: 'clamp(40px, 6vw, 72px)', fontWeight: 500, lineHeight: 1.05, marginBottom: '20px' }}>
          {lang === 'ar' ? 'أرقى مواقع' : 'Egypt\'s '}<em style={{ fontStyle: 'italic' }} className="text-shimmer">{lang === 'ar' ? 'مصر' : 'Prime'}</em>{lang !== 'ar' && ' Locations'}
        </h1>
        <p style={{ fontSize: '17px', color: themeStyles.textSecondary, maxWidth: '700px' }}>{t.sections.areasSub}</p>
      </section>

      <section style={{ padding: '0 32px', maxWidth: '1440px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {AREAS.map((area, i) => {
          const projects = PROPERTIES.filter(p => p.area === area.id);
          return (
            <div key={area.id} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '0', background: themeStyles.bgSecondary, border: `1px solid ${themeStyles.border}`, overflow: 'hidden', animation: `fadeUp 0.6s ${i * 0.1}s both` }}>
              <div style={{ aspectRatio: '4/3', overflow: 'hidden', position: 'relative' }}>
                <img src={area.image} alt={area.name[lang]} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div className="badge-gold" style={{ position: 'absolute', top: '20px', [lang === 'ar' ? 'right' : 'left']: '20px' }}>ROI {area.roi}</div>
              </div>
              <div style={{ padding: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontSize: '11px', letterSpacing: '0.3em', color: '#C9A961', textTransform: 'uppercase', fontWeight: 600, marginBottom: '12px' }}>{area.tagline[lang]}</div>
                <h2 className="serif" style={{ fontSize: 'clamp(28px, 3.5vw, 42px)', fontWeight: 500, marginBottom: '20px', lineHeight: 1.1 }}>{area.name[lang]}</h2>
                <p style={{ color: themeStyles.textSecondary, fontSize: '15px', lineHeight: 1.7, marginBottom: '24px' }}>{areaInsights[area.id][lang]}</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', paddingTop: '24px', borderTop: `1px solid ${themeStyles.borderLight}`, marginBottom: '24px' }}>
                  <div>
                    <div className="serif gold-text" style={{ fontSize: '28px', fontWeight: 500 }}>{area.projects}</div>
                    <div style={{ fontSize: '10px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600 }}>{lang === 'ar' ? 'مشروع' : 'PROJECTS'}</div>
                  </div>
                  <div>
                    <div className="serif gold-text" style={{ fontSize: '28px', fontWeight: 500 }}>{projects.length}</div>
                    <div style={{ fontSize: '10px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600 }}>{lang === 'ar' ? 'متاح' : 'AVAILABLE'}</div>
                  </div>
                  <div>
                    <div className="serif gold-text" style={{ fontSize: '28px', fontWeight: 500 }}>{area.roi.split('-')[1]}</div>
                    <div style={{ fontSize: '10px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600 }}>{lang === 'ar' ? 'أعلى عائد' : 'MAX ROI'}</div>
                  </div>
                </div>
                <button onClick={() => goTo('listings', null, area.id)} className="btn-primary" style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                  {lang === 'ar' ? 'تصفح العقارات' : 'Browse Properties'} <ArrowRight size={14} style={{ transform: lang === 'ar' ? 'scaleX(-1)' : 'none' }} />
                </button>
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}

// ============== Contact Page ==============
function ContactPage({ t, lang, themeStyles, isDark }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.createLead({ ...formData, property_id: null });
      setSent(true);
      setTimeout(() => { setSent(false); setFormData({ name: '', email: '', phone: '', message: '' }); }, 3000);
    } catch (err) {
      console.error('Failed to submit lead:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ paddingTop: '120px', paddingBottom: '80px', position: 'relative' }}>
      <PageBackdrop
        image="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=2400&q=85"
        height="70vh"
        isDark={isDark}
      />
      <section style={{ padding: '0 32px', maxWidth: '1440px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '80px' }}>
          <div>
            <div className="section-eyebrow">— {lang === 'ar' ? 'تواصل' : 'CONTACT'} —</div>
            <h1 className="serif" style={{ fontSize: 'clamp(40px, 6vw, 72px)', fontWeight: 500, lineHeight: 1.05, marginBottom: '20px' }}>
              {t.contact.title.split(' ').map((w, i, arr) => i === arr.length - 1 ? <em key={i} style={{ fontStyle: 'italic' }} className="text-shimmer">{w}</em> : <span key={i}>{w} </span>)}
            </h1>
            <p style={{ fontSize: '17px', color: themeStyles.textSecondary, marginBottom: '48px', lineHeight: 1.7 }}>{t.contact.subtitle}</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(201, 169, 97, 0.1)', border: '1px solid rgba(201, 169, 97, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <MapPin size={20} color="#C9A961" />
                </div>
                <div>
                  <div style={{ fontSize: '11px', letterSpacing: '0.2em', color: '#C9A961', fontWeight: 600, marginBottom: '6px', textTransform: 'uppercase' }}>{t.contact.office}</div>
                  <div className="serif" style={{ fontSize: '20px', fontWeight: 500, marginBottom: '4px' }}>{lang === 'ar' ? 'التجمع الخامس، القاهرة الجديدة' : 'Fifth Settlement, New Cairo'}</div>
                  <div style={{ color: themeStyles.textSecondary, fontSize: '14px' }}>{lang === 'ar' ? 'برج النخيل، الدور ١٥' : 'Palm Tower, 15th Floor'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(201, 169, 97, 0.1)', border: '1px solid rgba(201, 169, 97, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Phone size={20} color="#C9A961" />
                </div>
                <div>
                  <div style={{ fontSize: '11px', letterSpacing: '0.2em', color: '#C9A961', fontWeight: 600, marginBottom: '6px', textTransform: 'uppercase' }}>{lang === 'ar' ? 'الهاتف' : 'Phone'}</div>
                  <div className="serif" style={{ fontSize: '20px', fontWeight: 500, marginBottom: '4px' }}>+20 100 000 0000</div>
                  <div style={{ color: themeStyles.textSecondary, fontSize: '14px' }}>{t.contact.hours}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(201, 169, 97, 0.1)', border: '1px solid rgba(201, 169, 97, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Mail size={20} color="#C9A961" />
                </div>
                <div>
                  <div style={{ fontSize: '11px', letterSpacing: '0.2em', color: '#C9A961', fontWeight: 600, marginBottom: '6px', textTransform: 'uppercase' }}>{lang === 'ar' ? 'البريد' : 'Email'}</div>
                  <div className="serif" style={{ fontSize: '20px', fontWeight: 500 }}>info@atlantis-realestate.com</div>
                </div>
              </div>
            </div>

            {/* Map preview */}
            <div style={{ marginTop: '48px', aspectRatio: '16/9', overflow: 'hidden', border: `1px solid ${themeStyles.border}`, position: 'relative' }}>
              <LeafletMap
                properties={[]}
                singleMarker={{ lat: 30.0286, lng: 31.4954, label: lang === 'ar' ? 'مكتب أتلانتس' : 'Atlantis Office' }}
                lang={lang}
                themeStyles={themeStyles}
                isDark={isDark}
                hideUI
              />
            </div>
          </div>

          {/* Form */}
          <div className="glass" style={{ padding: '48px' }}>
            {sent ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(201, 169, 97, 0.15)', border: '2px solid #C9A961', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                  <Check size={32} color="#C9A961" />
                </div>
                <h3 className="serif" style={{ fontSize: '28px', fontWeight: 500, marginBottom: '12px' }}>{lang === 'ar' ? 'تم الإرسال!' : 'Message Sent!'}</h3>
                <p style={{ color: themeStyles.textSecondary }}>{lang === 'ar' ? 'سنتواصل معك خلال ٣٠ دقيقة.' : 'We\'ll respond within 30 minutes.'}</p>
              </div>
            ) : (
              <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600, marginBottom: '8px', display: 'block', textTransform: 'uppercase' }}>{t.contact.name}</label>
                  <input required type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="input-field" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600, marginBottom: '8px', display: 'block', textTransform: 'uppercase' }}>{t.contact.email}</label>
                    <input required type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="input-field" />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600, marginBottom: '8px', display: 'block', textTransform: 'uppercase' }}>{t.contact.phone}</label>
                    <input required type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="input-field" />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600, marginBottom: '8px', display: 'block', textTransform: 'uppercase' }}>{t.contact.message}</label>
                  <textarea required rows={5} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} className="input-field" style={{ resize: 'vertical' }} />
                </div>
                <button type="submit" className="btn-primary" style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <Send size={16} /> {t.contact.send}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// ============== Footer ==============
function Footer({ t, lang, goTo, themeStyles, isDark }) {
  return (
    <footer style={{ background: '#0A1628', color: '#F7F4ED', padding: '80px 32px 32px', borderTop: '1px solid rgba(201, 169, 97, 0.2)' }}>
      <div style={{ maxWidth: '1440px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '60px', marginBottom: '60px' }}>
          {/* Brand */}
          <div style={{ maxWidth: '320px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
              <AtlantisLogo size={48} color="#F7F4ED" />
              <div style={{ lineHeight: 1 }}>
                <div style={{ fontSize: '18px', fontWeight: 400, letterSpacing: '0.2em', color: '#F7F4ED', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>ATLANTIS</div>
                <div style={{ fontSize: '9px', letterSpacing: '0.32em', color: '#C9A961', marginTop: '6px', fontWeight: 500 }}>REAL ESTATE</div>
              </div>
            </div>
            <p style={{ fontSize: '14px', color: 'rgba(247, 244, 237, 0.6)', lineHeight: 1.7, marginBottom: '24px' }}>{t.footer.tagline}</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              {[Mail, Phone, Globe, Send].map((Icon, i) => (
                <a key={i} href="#" style={{ width: '40px', height: '40px', borderRadius: '50%', border: '1px solid rgba(247, 244, 237, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F7F4ED', textDecoration: 'none', transition: 'all 0.3s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#C9A961'; e.currentTarget.style.color = '#C9A961'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(247, 244, 237, 0.15)'; e.currentTarget.style.color = '#F7F4ED'; }}
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Navigate */}
          <div>
            <div style={{ fontSize: '11px', letterSpacing: '0.25em', color: '#C9A961', fontWeight: 600, marginBottom: '24px', textTransform: 'uppercase' }}>{t.footer.quick}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[{ k: 'home', l: t.nav.home }, { k: 'listings', l: t.nav.listings }, { k: 'developers', l: t.nav.developers }, { k: 'areas', l: t.nav.areas }, { k: 'about', l: t.nav.about }, { k: 'contact', l: t.nav.contact }].map(item => (
                <button key={item.k} onClick={() => goTo(item.k)} style={{ background: 'none', border: 'none', color: 'rgba(247, 244, 237, 0.7)', textAlign: lang === 'ar' ? 'right' : 'left', cursor: 'pointer', fontSize: '14px', padding: 0, fontFamily: 'inherit', transition: 'color 0.3s' }}
                  onMouseEnter={e => e.target.style.color = '#C9A961'}
                  onMouseLeave={e => e.target.style.color = 'rgba(247, 244, 237, 0.7)'}
                >{item.l}</button>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <div style={{ fontSize: '11px', letterSpacing: '0.25em', color: '#C9A961', fontWeight: 600, marginBottom: '24px', textTransform: 'uppercase' }}>{t.footer.contact}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '14px', color: 'rgba(247, 244, 237, 0.7)' }}>
              <div>{lang === 'ar' ? 'التجمع الخامس، القاهرة الجديدة' : 'Fifth Settlement, New Cairo'}</div>
              <div>+20 100 000 0000</div>
              <div>info@atlantis-realestate.com</div>
              <div>{t.contact.hours}</div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <div style={{ fontSize: '11px', letterSpacing: '0.25em', color: '#C9A961', fontWeight: 600, marginBottom: '24px', textTransform: 'uppercase' }}>{t.sections.newsletter}</div>
            <p style={{ fontSize: '13px', color: 'rgba(247, 244, 237, 0.6)', marginBottom: '16px', lineHeight: 1.6 }}>
              {lang === 'ar' ? 'أحدث المشاريع والفرص الاستثمارية الحصرية في بريدك.' : 'Latest projects and exclusive investment opportunities in your inbox.'}
            </p>
            <div style={{ display: 'flex' }}>
              <input type="email" placeholder={t.footer.emailPlace} style={{ flex: 1, background: 'rgba(247, 244, 237, 0.05)', border: '1px solid rgba(247, 244, 237, 0.15)', color: '#F7F4ED', padding: '12px 14px', fontSize: '13px', outline: 'none', fontFamily: 'inherit' }} />
              <button style={{ background: '#C9A961', color: '#0A1628', border: 'none', padding: '12px 16px', cursor: 'pointer' }}>
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>

        <div style={{ paddingTop: '32px', borderTop: '1px solid rgba(247, 244, 237, 0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: 'rgba(247, 244, 237, 0.4)' }}>
          <div>© {new Date().getFullYear()} Atlantis Real Estate. {t.footer.rights}</div>
          <div style={{ display: 'flex', gap: '24px' }}>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>{lang === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}</a>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>{lang === 'ar' ? 'الشروط' : 'Terms'}</a>
            <button onClick={() => goTo('admin')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit', padding: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Lock size={11} /> {t.common.admin}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============== Chat Widget ==============
function ChatWidget({ open, setOpen, lang, isDark }) {
  const [messages, setMessages] = useState([
    { from: 'bot', text: lang === 'ar' ? 'مرحباً! كيف يمكنني مساعدتك في رحلة العقار اليوم؟' : 'Hello! How can I assist you in your property journey today?' },
  ]);
  const [input, setInput] = useState('');

  useEffect(() => {
    setMessages([{ from: 'bot', text: lang === 'ar' ? 'مرحباً! كيف يمكنني مساعدتك في رحلة العقار اليوم؟' : 'Hello! How can I assist you in your property journey today?' }]);
  }, [lang]);

  const send = () => {
    if (!input.trim()) return;
    setMessages([...messages, { from: 'user', text: input }]);
    const userInput = input;
    setInput('');
    setTimeout(() => {
      const reply = lang === 'ar'
        ? 'شكراً لتواصلك! سيقوم أحد مستشارينا بالرد عليك خلال دقائق. هل ترغب في حجز معاينة أو الحصول على مزيد من المعلومات؟'
        : 'Thanks for reaching out! One of our advisors will respond within minutes. Would you like to schedule a viewing or get more information?';
      setMessages(m => [...m, { from: 'bot', text: reply }]);
    }, 1200);
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} style={{
        position: 'fixed', bottom: '24px', [lang === 'ar' ? 'right' : 'left']: '24px',
        width: '56px', height: '56px', borderRadius: '50%',
        background: 'linear-gradient(135deg, #C9A961, #D4B876)', color: '#0A1628',
        border: 'none', cursor: 'pointer', boxShadow: '0 8px 32px rgba(201, 169, 97, 0.4)',
        zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'transform 0.3s',
      }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        {open ? <X size={24} /> : <Sparkles size={24} />}
      </button>

      {open && (
        <div style={{
          position: 'fixed', bottom: '96px', [lang === 'ar' ? 'right' : 'left']: '24px',
          width: '340px', height: '480px',
          background: isDark ? '#0E1D33' : '#FFFFFF',
          border: `1px solid ${isDark ? 'rgba(201, 169, 97, 0.2)' : 'rgba(10, 22, 40, 0.1)'}`,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
          zIndex: 99, display: 'flex', flexDirection: 'column',
          animation: 'fadeUp 0.3s ease',
        }}>
          <div style={{ padding: '20px', borderBottom: `1px solid ${isDark ? 'rgba(247, 244, 237, 0.08)' : 'rgba(10, 22, 40, 0.06)'}`, background: 'linear-gradient(135deg, #C9A961, #D4B876)', color: '#0A1628' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Sparkles size={20} />
              <div>
                <div className="serif" style={{ fontSize: '16px', fontWeight: 600 }}>{lang === 'ar' ? 'مساعد أتلانتس' : 'Atlantis Assistant'}</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>{lang === 'ar' ? 'متاح الآن' : 'Online now'}</div>
              </div>
            </div>
          </div>
          <div style={{ flex: 1, padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', color: isDark ? '#F7F4ED' : '#0A1628' }}>
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.from === 'user' ? (lang === 'ar' ? 'flex-start' : 'flex-end') : (lang === 'ar' ? 'flex-end' : 'flex-start'), maxWidth: '80%', padding: '10px 14px', background: m.from === 'user' ? '#C9A961' : (isDark ? 'rgba(247, 244, 237, 0.08)' : 'rgba(10, 22, 40, 0.05)'), color: m.from === 'user' ? '#0A1628' : (isDark ? '#F7F4ED' : '#0A1628'), fontSize: '14px', lineHeight: 1.5, animation: 'fadeUp 0.3s ease' }}>
                {m.text}
              </div>
            ))}
          </div>
          <div style={{ padding: '16px', borderTop: `1px solid ${isDark ? 'rgba(247, 244, 237, 0.08)' : 'rgba(10, 22, 40, 0.06)'}`, display: 'flex', gap: '8px' }}>
            <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && send()} placeholder={lang === 'ar' ? 'اكتب رسالتك...' : 'Type your message...'} style={{ flex: 1, background: isDark ? 'rgba(247, 244, 237, 0.05)' : 'rgba(10, 22, 40, 0.04)', border: 'none', padding: '10px 14px', color: isDark ? '#F7F4ED' : '#0A1628', fontSize: '14px', outline: 'none', fontFamily: 'inherit' }} />
            <button onClick={send} style={{ background: '#C9A961', color: '#0A1628', border: 'none', padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================
// VIDEO REEL SECTION — Hero-style luxury showcase reel
// ============================================================
function VideoReelSection({ t, lang, themeStyles, isDark }) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  // Curated luxury showcase video (open license sample)
  const reelUrl = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
  const posterUrl = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=2000&q=85';

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.pause();
    else videoRef.current.play();
    setPlaying(!playing);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  return (
    <section style={{ padding: '120px 32px 80px', maxWidth: '1440px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '48px' }} className="reveal">
        <div className="section-eyebrow">— {lang === 'ar' ? 'العرض' : 'SHOWCASE'} —</div>
        <h2 className="serif" style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, lineHeight: 1.1 }}>
          {t.common.videoReel}
        </h2>
      </div>

      <div style={{ position: 'relative', aspectRatio: '21/9', overflow: 'hidden', borderRadius: '0', boxShadow: '0 30px 80px rgba(0,0,0,0.4)' }}>
        <video
          ref={videoRef}
          src={reelUrl}
          poster={posterUrl}
          muted={muted}
          loop
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />

        {!playing && (
          <>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10,22,40,0.3) 0%, rgba(10,22,40,0.7) 100%)', pointerEvents: 'none' }} />
            <button onClick={togglePlay}
              style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: '100px', height: '100px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #C9A961, #D4B876)', color: '#0A1628',
                border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 12px 40px rgba(201, 169, 97, 0.5)',
                animation: 'pulse-gold 2s infinite',
              }}>
              <Play size={36} fill="#0A1628" style={{ marginLeft: '4px' }} />
            </button>
            <div style={{ position: 'absolute', bottom: '40px', left: '40px', color: '#F7F4ED', maxWidth: '500px' }}>
              <div style={{ fontSize: '11px', letterSpacing: '0.3em', color: '#C9A961', fontWeight: 600, marginBottom: '12px' }}>
                {lang === 'ar' ? 'فيديو ترويجي' : 'CINEMATIC REEL'}
              </div>
              <div className="serif" style={{ fontSize: '32px', fontWeight: 500, lineHeight: 1.1 }}>
                {lang === 'ar' ? 'رحلة عبر أفخم الوجهات في مصر' : 'A journey through Egypt\'s most prestigious destinations'}
              </div>
            </div>
          </>
        )}

        {playing && (
          <div style={{ position: 'absolute', bottom: '20px', right: '20px', display: 'flex', gap: '8px' }}>
            <button onClick={toggleMute} style={{
              width: '44px', height: '44px', borderRadius: '50%',
              background: 'rgba(10, 22, 40, 0.7)', backdropFilter: 'blur(10px)',
              border: '1px solid rgba(247, 244, 237, 0.2)', color: '#F7F4ED',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <button onClick={togglePlay} style={{
              width: '44px', height: '44px', borderRadius: '50%',
              background: 'rgba(10, 22, 40, 0.7)', backdropFilter: 'blur(10px)',
              border: '1px solid rgba(247, 244, 237, 0.2)', color: '#F7F4ED',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Pause size={18} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// ============================================================
// VIDEO MODAL — Full-screen video player for property tours
// ============================================================
function VideoModal({ videoUrl, title, onClose, lang }) {
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(10, 22, 40, 0.95)', backdropFilter: 'blur(20px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '32px', animation: 'fadeIn 0.3s ease',
    }} onClick={onClose}>
      <button onClick={onClose} style={{
        position: 'absolute', top: '24px', right: '24px',
        width: '48px', height: '48px', borderRadius: '50%',
        background: 'rgba(247, 244, 237, 0.1)', border: '1px solid rgba(247, 244, 237, 0.2)',
        color: '#F7F4ED', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <X size={20} />
      </button>
      <div style={{ width: '100%', maxWidth: '1200px' }} onClick={e => e.stopPropagation()}>
        <div style={{ marginBottom: '20px', color: '#F7F4ED' }}>
          <div style={{ fontSize: '11px', letterSpacing: '0.3em', color: '#C9A961', fontWeight: 600, marginBottom: '8px' }}>
            {lang === 'ar' ? 'جولة فيديو' : 'VIDEO TOUR'}
          </div>
          <div className="serif" style={{ fontSize: '28px', fontWeight: 500 }}>{title}</div>
        </div>
        <video
          src={videoUrl}
          controls
          autoPlay
          style={{ width: '100%', aspectRatio: '16/9', display: 'block', background: '#000' }}
        />
      </div>
    </div>
  );
}

// ============================================================
// LEAFLET MAP — Real interactive map via OpenStreetMap (no API key)
// ============================================================
function LeafletMap({ properties = [], singleMarker = null, goTo, currency, lang, themeStyles, isDark, isRTL, hideUI = false }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const [leafletReady, setLeafletReady] = useState(false);

  // Inject Leaflet CSS + JS once
  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }

    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    css.crossOrigin = '';
    document.head.appendChild(css);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.crossOrigin = '';
    script.onload = () => setLeafletReady(true);
    document.head.appendChild(script);
  }, []);

  // Initialize map
  useEffect(() => {
    if (!leafletReady || !mapRef.current || mapInstance.current) return;
    const L = window.L;

    // Center on Egypt's New Cairo by default, or on single marker
    const center = singleMarker
      ? [singleMarker.lat, singleMarker.lng]
      : properties.length > 0
        ? [properties[0].lat || 30.0286, properties[0].lng || 31.4954]
        : [30.0286, 31.4954];

    const zoom = singleMarker ? 15 : 7;

    const map = L.map(mapRef.current, {
      center, zoom,
      zoomControl: !hideUI,
      attributionControl: !hideUI,
    });
    mapInstance.current = map;

    // Use dark or light tile layer based on theme
    const tileUrl = isDark
      ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
      : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

    L.tileLayer(tileUrl, {
      attribution: '© OpenStreetMap, © CARTO',
      maxZoom: 19,
    }).addTo(map);

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [leafletReady, isDark, hideUI]);

  // Add markers when properties change
  useEffect(() => {
    if (!leafletReady || !mapInstance.current) return;
    const L = window.L;
    const map = mapInstance.current;

    // Clear existing markers
    map.eachLayer(layer => {
      if (layer instanceof L.Marker || layer instanceof L.Popup) map.removeLayer(layer);
    });

    // Custom gold pin icon
    const createPriceIcon = (priceText) => L.divIcon({
      className: 'atlantis-price-marker',
      html: `<div style="
        background: linear-gradient(135deg, #C9A961, #D4B876);
        color: #0A1628;
        padding: 6px 12px;
        font-weight: 700;
        font-size: 12px;
        font-family: 'Plus Jakarta Sans', sans-serif;
        white-space: nowrap;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        border: 2px solid #0A1628;
        transform: translateY(-50%);
      ">${priceText}</div>`,
      iconSize: [80, 30],
      iconAnchor: [40, 15],
    });

    const singleIcon = L.divIcon({
      className: 'atlantis-single-marker',
      html: `<div style="
        width: 36px; height: 36px;
        background: linear-gradient(135deg, #C9A961, #D4B876);
        border: 3px solid #0A1628;
        border-radius: 50%;
        box-shadow: 0 0 0 6px rgba(201, 169, 97, 0.3), 0 4px 12px rgba(0,0,0,0.3);
        display: flex; align-items: center; justify-content: center;
      "><svg width="14" height="14" viewBox="0 0 24 24" fill="#0A1628"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg></div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });

    if (singleMarker) {
      L.marker([singleMarker.lat, singleMarker.lng], { icon: singleIcon })
        .addTo(map)
        .bindPopup(`<strong>${singleMarker.label || 'Office'}</strong>`);
    }

    properties.forEach(p => {
      if (!p.lat || !p.lng) return;
      const priceShort = formatPrice(p.price, currency, lang).replace(/,/g, '').replace(/\s+/g, ' ');
      const compact = (p.price / 1000000).toFixed(1) + 'M';
      const sym = currency === 'EGP' ? (lang === 'ar' ? 'ج.م' : 'EGP') : currency === 'USD' ? '$' : 'AED';
      const display = `${sym} ${compact}`;

      const marker = L.marker([p.lat, p.lng], { icon: createPriceIcon(display) }).addTo(map);
      marker.bindPopup(`
        <div style="font-family: 'Plus Jakarta Sans', sans-serif; min-width: 220px;">
          <img src="${p.image}" style="width: 100%; height: 120px; object-fit: cover; display: block;" />
          <div style="padding: 10px 4px 4px;">
            <div style="font-size: 10px; letter-spacing: 0.15em; color: #C9A961; font-weight: 700; margin-bottom: 4px;">${p.areaName[lang].toUpperCase()}</div>
            <div style="font-size: 15px; font-weight: 600; color: #0A1628; margin-bottom: 4px;">${p.title[lang]}</div>
            <div style="font-size: 14px; color: #C9A961; font-weight: 700;">${formatPrice(p.price, currency, lang)}</div>
          </div>
        </div>
      `);
      marker.on('click', () => {
        // popup will show; user can click for details via global handler if needed
      });
    });

    // Fit bounds if multiple properties
    if (properties.length > 1 && !singleMarker) {
      const valid = properties.filter(p => p.lat && p.lng);
      if (valid.length > 0) {
        const bounds = L.latLngBounds(valid.map(p => [p.lat, p.lng]));
        map.fitBounds(bounds, { padding: [40, 40] });
      }
    }
  }, [properties, leafletReady, currency, lang, singleMarker]);

  return (
    <div style={{ position: 'relative', aspectRatio: hideUI ? '16/9' : '16/10', border: `1px solid ${themeStyles.border}`, overflow: 'hidden', background: themeStyles.bgSecondary }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%', zIndex: 1 }} />
      {!leafletReady && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: themeStyles.textMuted, fontSize: '13px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Loading map...
        </div>
      )}
      {!hideUI && leafletReady && goTo && (
        <div className="glass" style={{ position: 'absolute', bottom: '24px', [isRTL ? 'right' : 'left']: '24px', padding: '14px 18px', maxWidth: '280px', zIndex: 500 }}>
          <div style={{ fontSize: '11px', letterSpacing: '0.2em', color: '#C9A961', fontWeight: 600, marginBottom: '4px' }}>
            {lang === 'ar' ? 'الخريطة التفاعلية' : 'INTERACTIVE MAP'}
          </div>
          <div style={{ fontSize: '12px', color: themeStyles.text }}>
            {lang === 'ar' ? 'اضغط أي علامة لعرض التفاصيل' : 'Click any pin for property details'}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// COMPARE DRAWER — Side-by-side comparison
// ============================================================
function CompareDrawer({ open, setOpen, compareList, toggleCompare, lang, t, themeStyles, currency, goTo, isRTL, isDark }) {
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const items = compareList.map(id => PROPERTIES.find(p => p.id === id)).filter(Boolean);

  const rows = [
    { key: 'price', label: lang === 'ar' ? 'السعر' : 'Price', value: p => formatPrice(p.price, currency, lang) },
    { key: 'area', label: lang === 'ar' ? 'الموقع' : 'Location', value: p => p.areaName[lang] },
    { key: 'project', label: lang === 'ar' ? 'المشروع' : 'Project', value: p => p.project },
    { key: 'developer', label: lang === 'ar' ? 'المطور' : 'Developer', value: p => p.developer },
    { key: 'type', label: lang === 'ar' ? 'النوع' : 'Type', value: p => p.type },
    { key: 'bedrooms', label: lang === 'ar' ? 'غرف النوم' : 'Bedrooms', value: p => p.bedrooms },
    { key: 'bathrooms', label: lang === 'ar' ? 'الحمامات' : 'Bathrooms', value: p => p.bathrooms },
    { key: 'sqm', label: lang === 'ar' ? 'المساحة' : 'Area', value: p => `${p.sqm} m²` },
    { key: 'pricePerSqm', label: lang === 'ar' ? 'سعر المتر' : 'Price / m²', value: p => formatPrice(Math.round(p.price / p.sqm), currency, lang), highlight: true },
    { key: 'finishing', label: lang === 'ar' ? 'التشطيب' : 'Finishing', value: p => p.finishing },
    { key: 'delivery', label: lang === 'ar' ? 'التسليم' : 'Delivery', value: p => p.delivery },
    { key: 'installments', label: lang === 'ar' ? 'الأقساط' : 'Installments', value: p => `${p.installments} ${lang === 'ar' ? 'سنوات' : 'years'}` },
    { key: 'score', label: lang === 'ar' ? 'تقييم أتلانتس' : 'Atlantis Score', value: p => p.score, highlight: true },
  ];

  // Find best value per row (lowest price, highest score etc)
  const bestPerRow = {};
  rows.forEach(row => {
    if (row.key === 'price' || row.key === 'pricePerSqm') {
      bestPerRow[row.key] = items.reduce((best, p) => {
        const val = row.key === 'price' ? p.price : (p.price / p.sqm);
        return val < best.val ? { id: p.id, val } : best;
      }, { id: null, val: Infinity }).id;
    } else if (row.key === 'score' || row.key === 'sqm' || row.key === 'installments') {
      bestPerRow[row.key] = items.reduce((best, p) => {
        const val = p[row.key];
        return val > best.val ? { id: p.id, val } : best;
      }, { id: null, val: -Infinity }).id;
    }
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(10, 22, 40, 0.9)', backdropFilter: 'blur(10px)',
      animation: 'fadeIn 0.3s ease',
    }} onClick={() => setOpen(false)}>
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0,
        width: '100%', maxWidth: '1200px',
        background: themeStyles.bg, color: themeStyles.text,
        overflowY: 'auto', boxShadow: '-20px 0 60px rgba(0,0,0,0.4)',
        animation: 'slideRight 0.4s cubic-bezier(0.19, 1, 0.22, 1)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 10,
          padding: '24px 32px', background: themeStyles.bgSecondary,
          borderBottom: `1px solid ${themeStyles.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <GitCompare size={22} color="#C9A961" />
            <div>
              <div className="serif" style={{ fontSize: '24px', fontWeight: 500, lineHeight: 1 }}>
                {lang === 'ar' ? 'مقارنة العقارات' : 'Compare Properties'}
              </div>
              <div style={{ fontSize: '12px', color: themeStyles.textMuted, marginTop: '4px', letterSpacing: '0.1em' }}>
                {items.length} {lang === 'ar' ? 'عقار' : 'PROPERTIES'} • {lang === 'ar' ? 'الحد الأقصى ٤' : 'MAX 4'}
              </div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} style={{
            background: 'none', border: 'none', color: themeStyles.text, cursor: 'pointer',
            padding: '8px',
          }}>
            <X size={24} />
          </button>
        </div>

        {items.length === 0 ? (
          <div style={{ padding: '80px 32px', textAlign: 'center' }}>
            <GitCompare size={48} color={themeStyles.textMuted} style={{ marginBottom: '16px' }} />
            <div className="serif" style={{ fontSize: '24px', marginBottom: '8px' }}>
              {lang === 'ar' ? 'لا توجد عقارات للمقارنة' : 'No properties to compare yet'}
            </div>
            <p style={{ color: themeStyles.textSecondary, fontSize: '14px', maxWidth: '400px', margin: '0 auto 24px' }}>
              {lang === 'ar' ? 'أضف ما يصل إلى ٤ عقارات من خلال أيقونة المقارنة على كل بطاقة' : 'Add up to 4 properties using the compare icon on any property card'}
            </p>
            <button onClick={() => { setOpen(false); goTo('listings'); }} className="btn-primary">
              {lang === 'ar' ? 'تصفح العقارات' : 'Browse Properties'}
            </button>
          </div>
        ) : (
          <div style={{ padding: '32px' }}>
            {/* Property images header */}
            <div style={{ display: 'grid', gridTemplateColumns: `200px repeat(${items.length}, 1fr)`, gap: '12px', marginBottom: '24px' }}>
              <div></div>
              {items.map(p => (
                <div key={p.id} style={{ position: 'relative' }}>
                  <div style={{ aspectRatio: '4/3', overflow: 'hidden', marginBottom: '12px', cursor: 'pointer' }}
                    onClick={() => { setOpen(false); goTo('detail', p.id); }}>
                    <img src={p.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <button onClick={() => toggleCompare(p.id)} style={{
                    position: 'absolute', top: '8px', right: '8px',
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: 'rgba(10, 22, 40, 0.8)', border: '1px solid rgba(247, 244, 237, 0.3)',
                    color: '#F7F4ED', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <X size={14} />
                  </button>
                  <div style={{ fontSize: '10px', letterSpacing: '0.15em', color: '#C9A961', fontWeight: 700, marginBottom: '4px' }}>{p.areaName[lang].toUpperCase()}</div>
                  <div className="serif" style={{ fontSize: '16px', fontWeight: 500, lineHeight: 1.3 }}>{p.title[lang]}</div>
                </div>
              ))}
            </div>

            {/* Comparison rows */}
            <div style={{ border: `1px solid ${themeStyles.borderLight}` }}>
              {rows.map((row, i) => (
                <div key={row.key} style={{
                  display: 'grid', gridTemplateColumns: `200px repeat(${items.length}, 1fr)`, gap: '12px',
                  padding: '16px 20px', alignItems: 'center',
                  background: i % 2 === 0 ? themeStyles.bgSecondary : 'transparent',
                  borderBottom: i < rows.length - 1 ? `1px solid ${themeStyles.borderLight}` : 'none',
                }}>
                  <div style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>
                    {row.label}
                  </div>
                  {items.map(p => {
                    const isBest = bestPerRow[row.key] === p.id;
                    return (
                      <div key={p.id} style={{
                        fontSize: row.highlight ? '17px' : '14px',
                        fontWeight: row.highlight ? 600 : 500,
                        color: isBest ? '#C9A961' : themeStyles.text,
                        display: 'flex', alignItems: 'center', gap: '6px',
                      }}>
                        {row.value(p)}
                        {isBest && <Star size={12} color="#C9A961" fill="#C9A961" />}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: `200px repeat(${items.length}, 1fr)`, gap: '12px', marginTop: '24px' }}>
              <div></div>
              {items.map(p => (
                <button key={p.id} onClick={() => { setOpen(false); goTo('detail', p.id); }} className="btn-primary" style={{ fontSize: '12px', padding: '12px' }}>
                  {lang === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ADMIN PAGE — Dashboard with login, leads, properties, stats
// ============================================================
function AdminPage({ t, lang, themeStyles, isDark, authed, setAuthed, goTo }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [editingProperty, setEditingProperty] = useState(null);

  useEffect(() => {
    if (authed) {
      api.listLeads().then(setLeads);
      api.listProperties().then(setProperties);
    }
  }, [authed]);

  const handleLogin = (e) => {
    e.preventDefault();
    // Demo credentials — replace with Supabase auth in production
    if (username === 'admin' && password === 'atlantis2025') {
      setAuthed(true);
      setLoginError('');
    } else {
      setLoginError(lang === 'ar' ? 'بيانات غير صحيحة' : 'Invalid credentials');
    }
  };

  if (!authed) {
    return (
      <div style={{ paddingTop: '76px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '120px 32px 60px' }}>
        <div className="glass" style={{ padding: '48px', maxWidth: '440px', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(201, 169, 97, 0.1)', border: '1px solid rgba(201, 169, 97, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <Lock size={24} color="#C9A961" />
            </div>
            <div className="section-eyebrow">— {lang === 'ar' ? 'الإدارة' : 'ADMIN'} —</div>
            <h1 className="serif" style={{ fontSize: '36px', fontWeight: 500, marginBottom: '8px' }}>
              {lang === 'ar' ? 'تسجيل الدخول' : 'Sign In'}
            </h1>
            <p style={{ color: themeStyles.textSecondary, fontSize: '14px' }}>
              {lang === 'ar' ? 'لوحة تحكم أتلانتس' : 'Atlantis Admin Dashboard'}
            </p>
          </div>

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600, marginBottom: '8px', display: 'block', textTransform: 'uppercase' }}>
                {lang === 'ar' ? 'اسم المستخدم' : 'Username'}
              </label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} className="input-field" autoComplete="username" />
            </div>
            <div>
              <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600, marginBottom: '8px', display: 'block', textTransform: 'uppercase' }}>
                {lang === 'ar' ? 'كلمة المرور' : 'Password'}
              </label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="input-field" autoComplete="current-password" />
            </div>
            {loginError && <div style={{ color: '#E57373', fontSize: '13px' }}>{loginError}</div>}
            <button type="submit" className="btn-primary" style={{ marginTop: '8px' }}>
              {lang === 'ar' ? 'دخول' : 'Sign In'}
            </button>
            <div style={{ marginTop: '12px', padding: '14px', background: 'rgba(201, 169, 97, 0.08)', border: '1px solid rgba(201, 169, 97, 0.2)', fontSize: '12px', color: themeStyles.textSecondary, lineHeight: 1.6 }}>
              <strong style={{ color: '#C9A961' }}>{lang === 'ar' ? 'بيانات تجريبية:' : 'Demo credentials:'}</strong><br />
              Username: <code style={{ color: '#C9A961' }}>admin</code><br />
              Password: <code style={{ color: '#C9A961' }}>atlantis2025</code>
            </div>
          </form>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: lang === 'ar' ? 'نظرة عامة' : 'Overview', icon: BarChart3 },
    { id: 'leads', label: lang === 'ar' ? 'العملاء المحتملين' : 'Leads', icon: UserCheck },
    { id: 'properties', label: lang === 'ar' ? 'العقارات' : 'Properties', icon: Building2 },
    { id: 'settings', label: lang === 'ar' ? 'الإعدادات' : 'Settings', icon: Settings },
  ];

  return (
    <div style={{ paddingTop: '76px', minHeight: '100vh' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', minHeight: 'calc(100vh - 76px)' }}>
        {/* Sidebar */}
        <aside style={{ background: themeStyles.bgSecondary, borderRight: `1px solid ${themeStyles.border}`, padding: '32px 0' }}>
          <div style={{ padding: '0 24px 24px', borderBottom: `1px solid ${themeStyles.borderLight}` }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.3em', color: '#C9A961', fontWeight: 600, marginBottom: '4px' }}>
              {lang === 'ar' ? 'الإدارة' : 'ADMIN'}
            </div>
            <div className="serif" style={{ fontSize: '22px', fontWeight: 500 }}>
              {lang === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
            </div>
          </div>
          <nav style={{ padding: '24px 0' }}>
            {tabs.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                  width: '100%', padding: '14px 24px',
                  background: active ? 'rgba(201, 169, 97, 0.1)' : 'transparent',
                  border: 'none',
                  borderLeft: active ? '3px solid #C9A961' : '3px solid transparent',
                  color: active ? '#C9A961' : themeStyles.text,
                  display: 'flex', alignItems: 'center', gap: '12px',
                  cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: '14px', fontWeight: active ? 600 : 500,
                  textAlign: lang === 'ar' ? 'right' : 'left',
                  transition: 'all 0.2s',
                }}>
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
            <button onClick={() => { setAuthed(false); goTo('home'); }} style={{
              width: '100%', padding: '14px 24px', marginTop: '16px',
              background: 'transparent', border: 'none',
              color: themeStyles.textMuted,
              display: 'flex', alignItems: 'center', gap: '12px',
              cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px',
              textAlign: lang === 'ar' ? 'right' : 'left',
            }}>
              <LogOut size={18} />
              {lang === 'ar' ? 'تسجيل الخروج' : 'Sign Out'}
            </button>
          </nav>

          <div style={{ padding: '24px', borderTop: `1px solid ${themeStyles.borderLight}`, fontSize: '11px', color: themeStyles.textMuted, lineHeight: 1.6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Database size={12} />
              <span style={{ letterSpacing: '0.15em', fontWeight: 600 }}>{USE_SUPABASE ? 'SUPABASE' : 'MOCK DATA'}</span>
            </div>
            <div>{lang === 'ar' ? 'وضع التطوير' : 'Development mode'}</div>
          </div>
        </aside>

        {/* Main content */}
        <main style={{ padding: '40px 48px' }}>
          {activeTab === 'overview' && <AdminOverview leads={leads} properties={properties} lang={lang} themeStyles={themeStyles} />}
          {activeTab === 'leads' && <AdminLeads leads={leads} setLeads={setLeads} lang={lang} themeStyles={themeStyles} />}
          {activeTab === 'properties' && <AdminProperties properties={properties} setProperties={setProperties} lang={lang} themeStyles={themeStyles} editingProperty={editingProperty} setEditingProperty={setEditingProperty} />}
          {activeTab === 'settings' && <AdminSettings lang={lang} themeStyles={themeStyles} />}
        </main>
      </div>
    </div>
  );
}

function AdminOverview({ leads, properties, lang, themeStyles }) {
  const stats = [
    { label: lang === 'ar' ? 'إجمالي العقارات' : 'Total Properties', value: properties.length, icon: Building2, color: '#C9A961' },
    { label: lang === 'ar' ? 'عملاء محتملين جدد' : 'New Leads', value: leads.filter(l => l.status === 'new').length, icon: UserCheck, color: '#4CAF50' },
    { label: lang === 'ar' ? 'مؤهلين' : 'Qualified', value: leads.filter(l => l.status === 'qualified').length, icon: TrendingUp, color: '#2196F3' },
    { label: lang === 'ar' ? 'مجموع العملاء' : 'Total Leads', value: leads.length, icon: Users, color: '#FF9800' },
  ];

  const totalValue = properties.reduce((sum, p) => sum + p.price, 0);

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h1 className="serif" style={{ fontSize: '36px', fontWeight: 500, marginBottom: '8px' }}>
          {lang === 'ar' ? 'مرحباً بعودتك' : 'Welcome back'}
        </h1>
        <p style={{ color: themeStyles.textSecondary, fontSize: '14px' }}>
          {lang === 'ar' ? 'ملخص نشاط أتلانتس' : 'Atlantis activity summary'}
        </p>
      </div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '40px' }}>
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} style={{ background: themeStyles.bgSecondary, border: `1px solid ${themeStyles.border}`, padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <Icon size={20} color={s.color} />
                <span style={{ fontSize: '10px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600 }}>{lang === 'ar' ? 'إجمالي' : 'TOTAL'}</span>
              </div>
              <div className="serif" style={{ fontSize: '40px', fontWeight: 500, lineHeight: 1, marginBottom: '8px' }}>{s.value}</div>
              <div style={{ fontSize: '12px', color: themeStyles.textSecondary, letterSpacing: '0.05em' }}>{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Portfolio value card */}
      <div style={{ background: 'linear-gradient(135deg, rgba(201, 169, 97, 0.15), rgba(201, 169, 97, 0.05))', border: '1px solid rgba(201, 169, 97, 0.3)', padding: '32px', marginBottom: '32px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '0.3em', color: '#C9A961', fontWeight: 600, marginBottom: '8px' }}>
          {lang === 'ar' ? 'قيمة المحفظة' : 'PORTFOLIO VALUE'}
        </div>
        <div className="serif text-shimmer" style={{ fontSize: '52px', fontWeight: 500, lineHeight: 1 }}>
          {formatPrice(totalValue, 'EGP', lang)}
        </div>
        <div style={{ fontSize: '13px', color: themeStyles.textSecondary, marginTop: '12px' }}>
          {lang === 'ar' ? 'إجمالي قيمة العقارات المتاحة' : 'Combined value of available inventory'}
        </div>
      </div>

      {/* Recent leads */}
      <div style={{ background: themeStyles.bgSecondary, border: `1px solid ${themeStyles.border}`, padding: '32px' }}>
        <h2 className="serif" style={{ fontSize: '24px', fontWeight: 500, marginBottom: '24px' }}>
          {lang === 'ar' ? 'أحدث العملاء' : 'Recent Leads'}
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {leads.slice(0, 5).map(lead => (
            <div key={lead.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr auto auto', gap: '16px', alignItems: 'center', padding: '14px 0', borderBottom: `1px solid ${themeStyles.borderLight}` }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>{lead.name}</div>
                <div style={{ fontSize: '12px', color: themeStyles.textMuted, marginTop: '2px' }}>{lead.email}</div>
              </div>
              <div style={{ fontSize: '13px', color: themeStyles.textSecondary }}>{lead.message}</div>
              <StatusBadge status={lead.status} lang={lang} />
              <div style={{ fontSize: '11px', color: themeStyles.textMuted }}>
                {new Date(lead.created_at).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status, lang }) {
  const config = {
    new: { color: '#4CAF50', bg: 'rgba(76, 175, 80, 0.15)', label: lang === 'ar' ? 'جديد' : 'New' },
    contacted: { color: '#FF9800', bg: 'rgba(255, 152, 0, 0.15)', label: lang === 'ar' ? 'تم التواصل' : 'Contacted' },
    qualified: { color: '#2196F3', bg: 'rgba(33, 150, 243, 0.15)', label: lang === 'ar' ? 'مؤهل' : 'Qualified' },
    closed: { color: '#9E9E9E', bg: 'rgba(158, 158, 158, 0.15)', label: lang === 'ar' ? 'مغلق' : 'Closed' },
  };
  const c = config[status] || config.new;
  return (
    <span style={{ padding: '4px 10px', background: c.bg, color: c.color, fontSize: '11px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: '2px' }}>
      {c.label}
    </span>
  );
}

function AdminLeads({ leads, setLeads, lang, themeStyles }) {
  const [filter, setFilter] = useState('all');

  const updateStatus = async (id, status) => {
    await api.updateLeadStatus(id, status);
    setLeads(await api.listLeads());
  };

  const filtered = filter === 'all' ? leads : leads.filter(l => l.status === filter);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 className="serif" style={{ fontSize: '36px', fontWeight: 500, marginBottom: '8px' }}>
            {lang === 'ar' ? 'العملاء المحتملين' : 'Leads'}
          </h1>
          <p style={{ color: themeStyles.textSecondary, fontSize: '14px' }}>
            {filtered.length} {lang === 'ar' ? 'عميل' : 'leads'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {['all', 'new', 'contacted', 'qualified', 'closed'].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding: '8px 16px',
              background: filter === s ? '#C9A961' : 'transparent',
              color: filter === s ? '#0A1628' : themeStyles.text,
              border: `1px solid ${filter === s ? '#C9A961' : themeStyles.borderLight}`,
              cursor: 'pointer', fontFamily: 'inherit',
              fontSize: '12px', fontWeight: 600, letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}>
              {s === 'all' ? (lang === 'ar' ? 'الكل' : 'All') : s}
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: themeStyles.bgSecondary, border: `1px solid ${themeStyles.border}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr 1fr auto', gap: '16px', padding: '16px 24px', borderBottom: `1px solid ${themeStyles.border}`, fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>
          <div>{lang === 'ar' ? 'العميل' : 'Contact'}</div>
          <div>{lang === 'ar' ? 'الرسالة' : 'Message'}</div>
          <div>{lang === 'ar' ? 'التاريخ' : 'Date'}</div>
          <div>{lang === 'ar' ? 'الحالة' : 'Status'}</div>
          <div></div>
        </div>
        {filtered.map(lead => (
          <div key={lead.id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr 1fr auto', gap: '16px', padding: '20px 24px', borderBottom: `1px solid ${themeStyles.borderLight}`, alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>{lead.name}</div>
              <div style={{ fontSize: '12px', color: themeStyles.textMuted }}>{lead.email}</div>
              <div style={{ fontSize: '12px', color: themeStyles.textMuted }}>{lead.phone}</div>
            </div>
            <div style={{ fontSize: '13px', color: themeStyles.textSecondary, lineHeight: 1.5 }}>{lead.message}</div>
            <div style={{ fontSize: '12px', color: themeStyles.textMuted }}>
              {new Date(lead.created_at).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US')}
            </div>
            <select value={lead.status} onChange={e => updateStatus(lead.id, e.target.value)} style={{
              padding: '6px 10px', background: 'transparent', border: `1px solid ${themeStyles.borderLight}`,
              color: themeStyles.text, fontFamily: 'inherit', fontSize: '12px', cursor: 'pointer',
            }}>
              <option value="new" style={{ background: themeStyles.bgSolid }}>{lang === 'ar' ? 'جديد' : 'New'}</option>
              <option value="contacted" style={{ background: themeStyles.bgSolid }}>{lang === 'ar' ? 'تم التواصل' : 'Contacted'}</option>
              <option value="qualified" style={{ background: themeStyles.bgSolid }}>{lang === 'ar' ? 'مؤهل' : 'Qualified'}</option>
              <option value="closed" style={{ background: themeStyles.bgSolid }}>{lang === 'ar' ? 'مغلق' : 'Closed'}</option>
            </select>
            <a href={`mailto:${lead.email}`} style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(201, 169, 97, 0.1)', border: '1px solid rgba(201, 169, 97, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C9A961', textDecoration: 'none' }}>
              <Mail size={14} />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminProperties({ properties, setProperties, lang, themeStyles, editingProperty, setEditingProperty }) {
  const handleDelete = async (id) => {
if (!window.confirm(lang === 'ar' ? 'هل أنت متأكد من الحذف؟' : 'Delete this property?')) return;
    await api.deleteProperty(id);
    setProperties(await api.listProperties());
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 className="serif" style={{ fontSize: '36px', fontWeight: 500, marginBottom: '8px' }}>
            {lang === 'ar' ? 'العقارات' : 'Properties'}
          </h1>
          <p style={{ color: themeStyles.textSecondary, fontSize: '14px' }}>
            {properties.length} {lang === 'ar' ? 'عقار' : 'properties'}
          </p>
        </div>
        <button className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Plus size={16} /> {lang === 'ar' ? 'إضافة عقار' : 'Add Property'}
        </button>
      </div>

      <div style={{ background: themeStyles.bgSecondary, border: `1px solid ${themeStyles.border}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: '80px 2fr 1fr 1fr 1fr auto', gap: '16px', padding: '16px 24px', borderBottom: `1px solid ${themeStyles.border}`, fontSize: '11px', letterSpacing: '0.2em', color: themeStyles.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>
          <div></div>
          <div>{lang === 'ar' ? 'العقار' : 'Property'}</div>
          <div>{lang === 'ar' ? 'النوع' : 'Type'}</div>
          <div>{lang === 'ar' ? 'الموقع' : 'Location'}</div>
          <div>{lang === 'ar' ? 'السعر' : 'Price'}</div>
          <div></div>
        </div>
        {properties.map(p => (
          <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '80px 2fr 1fr 1fr 1fr auto', gap: '16px', padding: '16px 24px', borderBottom: `1px solid ${themeStyles.borderLight}`, alignItems: 'center' }}>
            <div style={{ width: '64px', height: '48px', overflow: 'hidden' }}>
              <img src={p.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>{p.title[lang]}</div>
              <div style={{ fontSize: '12px', color: themeStyles.textMuted }}>{p.developer} • {p.project}</div>
            </div>
            <div style={{ fontSize: '13px', textTransform: 'capitalize' }}>{p.type}</div>
            <div style={{ fontSize: '13px' }}>{p.areaName[lang]}</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#C9A961' }}>{formatPrice(p.price, 'EGP', lang)}</div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button style={{ width: '32px', height: '32px', background: 'rgba(33, 150, 243, 0.15)', border: '1px solid rgba(33, 150, 243, 0.3)', color: '#2196F3', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Edit3 size={14} />
              </button>
              <button onClick={() => handleDelete(p.id)} style={{ width: '32px', height: '32px', background: 'rgba(244, 67, 54, 0.15)', border: '1px solid rgba(244, 67, 54, 0.3)', color: '#F44336', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminSettings({ lang, themeStyles }) {
  return (
    <div>
      <h1 className="serif" style={{ fontSize: '36px', fontWeight: 500, marginBottom: '32px' }}>
        {lang === 'ar' ? 'الإعدادات' : 'Settings'}
      </h1>
      <div style={{ background: themeStyles.bgSecondary, border: `1px solid ${themeStyles.border}`, padding: '32px' }}>
        <h2 className="serif" style={{ fontSize: '22px', fontWeight: 500, marginBottom: '8px' }}>
          {lang === 'ar' ? 'إعدادات الخلفية' : 'Backend Configuration'}
        </h2>
        <p style={{ fontSize: '14px', color: themeStyles.textSecondary, marginBottom: '24px', lineHeight: 1.7 }}>
          {lang === 'ar' ? 'البيانات حالياً تستخدم وضع المحاكاة. لتفعيل Supabase، عدّل المتغير USE_SUPABASE في الكود وأضف مفاتيح المشروع.' : 'Currently running in mock data mode. To enable Supabase, set USE_SUPABASE = true in the source code and add your project URL and anon key in the data layer scaffold.'}
        </p>
        <div style={{ padding: '20px', background: 'rgba(201, 169, 97, 0.08)', border: '1px solid rgba(201, 169, 97, 0.2)', fontFamily: 'monospace', fontSize: '12px', color: themeStyles.text, lineHeight: 1.8, whiteSpace: 'pre' }}>
{`const USE_SUPABASE = false;  // change to: true
const BACKEND_URL   = 'https://YOUR-PROJECT.your-backend.co';
const BACKEND_KEY   = 'YOUR_ANON_KEY';`}
        </div>
        <div style={{ marginTop: '24px', fontSize: '13px', color: themeStyles.textSecondary }}>
          {lang === 'ar' ? 'الجداول المطلوبة:' : 'Required tables:'} <code style={{ color: '#C9A961' }}>properties</code>, <code style={{ color: '#C9A961' }}>developers</code>, <code style={{ color: '#C9A961' }}>areas</code>, <code style={{ color: '#C9A961' }}>leads</code>
        </div>
      </div>
    </div>
  );
}